/**
 * SirCAD application shell.
 *
 * Wires the part browser, canvas and layer panel together. Editing tools live
 * in ./tools; this file owns the document, the viewport and the event plumbing.
 */

// Imported here rather than linked from index.html so the production build
// hashes and bundles it; a bare <link> to src/ only works in dev.
import './style.css';

import {
  type CadDocument, type ComponentEntity, type DocumentKind, type Entity,
  type ImageRef, type LineEntity, type TextAnchor, type Vertex,
  emptyDocument, layerNumber, newId,
  LayerType,
} from '@sircad/core/model.ts';
import {
  buildRatsnest, extractNetlist, routingProgress, connectivityReport,
  placeInComponent, refDesOf, type Net, type RatLine,
} from '@sircad/core/net/netlist.ts';
import { extractDesignNetlist } from '@sircad/core/net/design.ts';
import {
  buildPort, nextPortName, portSignal, schLayers,
  PORT_LABELS, type PortKind,
} from '@sircad/core/net/ports.ts';
import { clearanceViolations } from '@sircad/core/net/clearance.ts';
import {
  addTeardrops, deleteHighlighted, deleteIsolationIslands, deleteTeardrops,
} from '@sircad/core/net/copper.ts';
import {
  editValue, formatDisplay, fromDisplay, readEdit, unitName, type DisplayUnit,
} from '@sircad/core/units.ts';
import { KEY_SEP } from '@sircad/core/keys.ts';
import { padThrough } from '@sircad/core/geom/pad.ts';
import type { IsolationOptions } from '@sircad/core/cam/isolation.ts';
import type { MillJob } from '@sircad/core/cam/mill.ts';
import { camwork, type OutputChoice } from './camwork.ts';
import { showAboutDialog } from './ui/about.ts';
import { installUi, isInstalled, registerWorker, type InstallUi } from './ui/install.ts';
import { showHelp } from './ui/help.ts';
import { showDfmReport } from './ui/dfmreport.ts';
import { showDensityReport } from './ui/density.ts';
import { showPlaceDialog } from './ui/place.ts';
import type { AutoRouteResult } from '@sircad/core/route/pathfinder.ts';
import { showAutoRouteDialog } from './ui/autoroute.ts';
import { showPlanesDialog } from './ui/planes.ts';
import type { RouteMessage } from './route/worker.ts';
import { boardwork, type BoardWork } from './boardwork.ts';
import type { CongestionReport } from '@sircad/core/route/congestion.ts';
import type { SequenceOptions } from '@sircad/core/cam/sequence.ts';
import { showDfmRules } from './ui/dfmrules.ts';
import { dfmCheck } from '@sircad/core/dfm/check.ts';
import {
  DEFAULT_DFM, allProfiles, sameRules, type DfmRules,
} from '@sircad/core/dfm/rules.ts';
import {
  drawable as photoDrawable, forget as photoForget, load as photoLoad,
  missing as photoMissing, pick as photoPick, pixelsFor as photoPixels,
  place as photoPlace, resolutionFrom as photoResolutionFrom,
} from './photo.ts';
import {
  Renderer, layerTypeLabel, type DensityFault, type DensityOverlay,
} from './render/renderer.ts';
import { ORTHO_NAMES, Viewport, snap, type OrthoMode } from './render/viewport.ts';
import {
  DevParts, HttpParts, type LibraryInfo, type PartInfo, type PartSource,
} from './parts.ts';
import { History } from './history.ts';
import {
  MacroRecorder, playMacro, summarise,
  type MacroSink, type MacroStep,
} from './macro.ts';
import { ToolBox, type ToolContext } from './tools/index.ts';
import { SCHEMATIC_WIRE_WIDTH } from './tools/track.ts';
import {
  componentRect, entityBounds, hitTest, selectable, setPickScope,
  type PickScope, type Rect,
} from './tools/hit.ts';
import { entitiesInBand } from './tools/select.ts';
import {
  ensureUniqueIds, instantiate, rotateEntity, translateEntity,
} from '@sircad/core/geom/transform.ts';
import { FutureVersionError, migrateDocument } from '@sircad/core/native/migrate.ts';
import { showBuildPartDialog } from './ui/buildpart.ts';
import {
  createTextEditor, type TextEditor, type ViewState,
} from './ui/textedit.ts';
import { button, buttons, openDialog } from './ui/dialog.ts';
import { snapCandidates, snapPoint, type SnapResult } from '@sircad/core/geom/snap.ts';
import {
  Projects, Session,
  type LoadedProject, type RevisionSummary, type TextDocumentData, type User,
} from './session.ts';
import { ApiError } from './api.ts';
import { showAccountDialog } from './ui/account.ts';
import type { OpenDrawing } from './ui/feedback.ts';
import { showProjectDialog } from './ui/projects.ts';
import { showLibraryManager } from './ui/libraries.ts';
import { showCompareDialog } from './ui/compare.ts';
import type { NativeProject } from '@sircad/core/native/project.ts';
import { showLayerDialog } from './ui/layers.ts';
import {
  applyDisplayPrefs, applyTheme, savePrefs, loadMacros, loadPrefs, showDrawModesDialog, showMacrosDialog,
  showOptionsDialog, showPreferencesDialog, showViaParamsDialog,
  type MacroMap, type Prefs,
} from './ui/setup.ts';
import { showNetReport } from './ui/netreport.ts';
import { showFootprintDialog, showFootprintPicker } from './ui/footprints.ts';
import {
  checkFootprints, footprintNeeds, refitComponent, resolvePattern, samePattern,
} from '@sircad/core/net/footprints.ts';
import { CURATED_ENTRIES } from '@sircad/core/lib/catalogue.ts';
import { suggestFootprints, type FootprintCandidate } from '@sircad/core/net/suggest.ts';
import { FootprintRegistry, harvestParts } from './footprints.ts';
import { convertEasyEda } from '@sircad/core/lib/easyeda.ts';
import type { SavedPart } from './parts.ts';
import { MenuSystem } from './menu/runtime.ts';
import {
  showSizePicker, showGridPicker, showLayerPicker, showPicker, closePicker,
  usedWidths,
  type PickerItem,
  type PickerContext,
} from './ui/pickers.ts';
import { showEditDialog } from './ui/edit.ts';
import { showPlaceParams } from './ui/place-params.ts';
import { adoptLayers, insertLayer, pruneUnusedLayers } from '@sircad/core/geom/layers.ts';
import { defaultPcbLayers, defaultSchLayers } from '@sircad/core/model.ts';
import { boardExtent } from '@sircad/core/geom/board.ts';
// Static, not dynamic: the parser is already in the main chunk by way of the
// part library and the import dialog, so importing it lazily only warns about
// a split that cannot happen.
import { parseKeywordDocument } from '@sircad/core/circad/parse.ts';
import { packageOf } from '@sircad/core/net/annotate.ts';

// --- State ------------------------------------------------------------------

interface AppState {
  mode: DocumentKind;
  docs: Record<DocumentKind, CadDocument>;
  activeLayer: number;
  units: DisplayUnit;
  selected: Set<number>;
  /** Component following the cursor, awaiting a click to place. */
  placing: ComponentEntity | null;
  placingLabel: string;
  /**
   * Set when what is in hand is a port rather than a part.
   *
   * Two things follow from it: the click lands on the pin or wire end under the
   * cursor instead of on the nearest grid line — a port half a grid step from
   * the pin it was aimed at is a port on no net at all — and the command re-arms
   * afterwards, numbering up, so a bus is clicked out.
   */
  placingPort: PortKind | null;
  showRatsnest: boolean;
  /** Placement density overlay (N P). */
  showDensity: boolean;
  /** Ratnest Signals Only (N T): leave power and ground out of it. */
  ratsnestSignalsOnly: boolean;
  /** Label every pad with its pin number. */
  showPins: boolean;
  /** Ortho snap, in the original's four states: 0 off, 1 H/V, 2 45°, 3 both. */
  ortho: OrthoMode;
  /** Pull a point that lands on nothing to the nearest grid line. */
  snap: boolean;
  /**
   * Read coordinates as a count of grid steps rather than as a length.
   *
   * A fourth stop on the units cycle. Laying out on a tenth-inch grid you think
   * in holes, not in thousandths - "three across and two up" is the sentence in
   * your head, and the readout may as well say it.
   */
  gridUnits: boolean;
}

const state: AppState = {
  mode: 'sch',
  docs: {
    // Plain, not blankDocument(): this runs before the preferences have been
    // read, so there is nothing to apply yet. They are applied to these two the
    // moment `prefs` is loaded, further down.
    sch: emptyDocument('sch', 'Sheet 1'),
    pcb: emptyDocument('pcb', 'Board'),
  },
  activeLayer: 2,
  units: 'in',
  selected: new Set(),
  placing: null,
  placingLabel: '',
  placingPort: null,
  // The ratsnest starts off: on a board of any size it is a thicket of lines
  // over the copper, so it is something you turn on to route by (N R), not the
  // view you are given.
  showRatsnest: false,
  showDensity: false,
  ratsnestSignalsOnly: false,
  // Off to begin with, for the same reason as the ratsnest: the numbers sit on
  // top of the copper they name, which is what you want while you are checking
  // a part and in the way while you are routing one.
  showPins: false,
  // Off, because in the original ortho is not a setting you leave switched on.
  // It is a property of the command in hand: nearly every menu handler clears
  // it on entry and the drawing commands then ask for the mode they want —
  // Place Line none, Place Signal Wire vertical and horizontal, Place Ortho-Line
  // and Place Bus Wire the diagonals as well (OGmenu02.cpp:106, 157, 1097,
  // 1138). `T` overrides it mid-command, and the next command sets it afresh.
  ortho: 0,
  // On. Off is the exception — a mounting hole at no round number.
  snap: true,
  gridUnits: false,
};

const doc = (): CadDocument => state.docs[state.mode];

/**
 * The layer to open a drawing on.
 *
 * The file says: every CIRCAD drawing carries `Display:: Layer:` and both of
 * the shipped libraries set it — `0` in PCB.LIB, `"Master"` in SCH.LIB. It was
 * parsed and stored faithfully and then never read, so a drawing that named its
 * own working layer was overruled by a constant, and an imported board opened
 * on whatever index 0 happened to be.
 *
 * The constants stay as the fallback, for a document that says nothing or names
 * a layer it has not got: Signals on a sheet, because that is where wire goes,
 * and the Pad Master on a board, where a pad reaches every copper layer at
 * once. A plane is never opened on — its flood is drawn last and would hide the
 * board under a blank slab.
 */
function workingLayer(d: CadDocument): number {
  const stated = layerNumber(d, d.display?.layer);
  if (stated !== undefined && d.layers[stated]?.type !== LayerType.Plane) return stated;
  return d.kind === 'sch' ? 2 : 0;
}

/** Undo history, one stack per document kind. */
const histories: Record<DocumentKind, History> = {
  sch: new History(),
  pcb: new History(),
};
const history = (): History => histories[state.mode];

// --- Element lookup ---------------------------------------------------------

function el<T extends HTMLElement>(id: string): T {
  const e = document.getElementById(id);
  if (!e) throw new Error(`missing element #${id}`);
  return e as T;
}

const canvas = el<HTMLCanvasElement>('canvas');
const viewport = new Viewport();
const renderer = new Renderer(canvas, viewport);

// The canvas's page position, cached so mousemove does not force a layout read
// (getBoundingClientRect) every event. Refreshed on resize/scroll, which are
// the only things that move it — the layout is a fixed full-height grid.
let canvasRect = canvas.getBoundingClientRect();
function refreshCanvasRect(): void { canvasRect = canvas.getBoundingClientRect(); }

// In development there is no database, so parse the original libraries here.
const parts: PartSource = import.meta.env.DEV ? new DevParts() : new HttpParts();

/** Custom footprints harvested from imported boards, a fallback for netlist-in. */
const footprints = new FootprintRegistry();

// --- Rendering --------------------------------------------------------------

/** Transient geometry owned by the active tool. */
const preview: Entity[] = [];

/** Cursor position in document space, and whether it is over the canvas. */
let cursorWorld: { x: number; y: number } | null = null;

/**
 * The crosshair to draw, or undefined.
 *
 * The original shows its large X/Y cursor while placing a line or a component
 * — the operations where you are positioning a point against distant geometry.
 * The plain arrow tools (select, grab) keep the normal pointer.
 */
/**
 * Grid mode: the crosshair is the cursor, and the arrow keys drive it.
 *
 * The original's space bar switches between the mouse pointer and the grid
 * crosshair, and the two modes decide what the arrow keys do — in pointer mode
 * they scroll the board, in grid mode they walk the crosshair a snap at a
 * time. Off by default, because a mouse is what most people reach for first.
 */
let gridMode = false;

/** Tools that are positioning a point, and so want the crosshair. */
/**
 * Tools that lay something down, as against the ones that pick things up.
 *
 * Everything the keyboard needs hangs off this: the arrow keys walk the
 * crosshair instead of scrolling the board, and Space drops a point instead of
 * swapping between pointer and crosshair. A drawing tool missing from this list
 * simply cannot be driven from the keyboard — which is what happened to `line`
 * when it was split from `track`, and had always been true of `fill` and
 * `arc-points`. `check-app-shell` now holds every drawing tool to it.
 */
function isPlacementTool(id: string): boolean {
  return id === 'track' || id === 'line' || id === 'arc' || id === 'pad'
    || id === 'via' || id === 'junction' || id === 'text' || id === 'vertex'
    || id === 'fill' || id === 'arc-points';
}

/** Whether the program is in the middle of laying something down. */
function isDrawing(): boolean {
  return state.placing !== null
    || clickCapture !== null || clickMode !== null
    // Carrying a grabbed item counts: the arrow keys should walk it, and Space
    // should put it down, rather than scrolling the view and toggling modes.
    || toolbox.holding
    || isPlacementTool(toolbox.active.id);
}

function crosshairAt(): { x: number; y: number; diagonal?: boolean } | undefined {
  if (!cursorWorld) return undefined;
  // Grid mode shows it always; otherwise only while a point is being
  // positioned. Zoom Centre and Zoom Window ask for a point and then sit
  // silently until you click, which reads exactly like the command having done
  // nothing — the crosshair is how the original says "I want a point from you".
  if (!gridMode && !isDrawing()) return undefined;
  // In 45°-only ortho the original turns the cursor itself through 45°
  // (OGxyz04.cpp:1008), so the two lines you are sighting along are the two
  // directions a segment can actually go. The renderer has always been able to
  // draw it; until there were four ortho modes there was nothing to ask it.
  return { x: cursorWorld.x, y: cursorWorld.y, diagonal: state.ortho === 2 };
}

/** Arrow key → (dx, dy) in grid steps; document y is up, so Up is +y. */
const ARROWS: Record<string, [number, number]> = {
  ArrowUp: [0, 1], ArrowDown: [0, -1], ArrowLeft: [-1, 0], ArrowRight: [1, 0],
};

/** Digits typed before an arrow key repeat it: `4↑` moves four grid snaps. */
let cursorRepeat = '';

/** The keyboard crosshair, seeded to the view centre the first time it is used. */
function ensureCursor(): { x: number; y: number } {
  if (cursorWorld) return cursorWorld;
  const b = viewport.bounds();
  const g = doc().grid.current || 0.1;
  cursorWorld = { x: snap((b.x1 + b.x2) / 2, g), y: snap((b.y1 + b.y2) / 2, g) };
  return cursorWorld;
}

/**
 * Move the crosshair by whole grid snaps, the way the original's arrow keys do
 * — with a leading number as a repeat count. Feeds the move to the active tool
 * (or the component in hand) so its ghost follows, exactly as the mouse would.
 */
function moveCursor(dx: number, dy: number): void {
  const g = doc().grid.current || 0.1;
  const n = Math.max(1, parseInt(cursorRepeat, 10) || 1);
  cursorRepeat = '';
  const base = ensureCursor();
  cursorWorld = { x: snap(base.x + dx * g * n, g), y: snap(base.y + dy * g * n, g) };
  if (state.placing) {
    state.placing.x = cursorWorld.x;
    state.placing.y = cursorWorld.y;
  } else {
    toolbox.pointerMove(cursorWorld);
  }
  updateStatus(cursorWorld.x, cursorWorld.y);
  reportUnderCursor(cursorWorld.x, cursorWorld.y);
  keepCursorInView();
  draw();
}

/**
 * Slide the view along when the crosshair nears the edge.
 *
 * Walking the crosshair with the arrow keys will otherwise take it off the
 * screen, and then you are drawing somewhere you cannot see. It scrolls rather
 * than recentres: a jump to the middle throws away everything you had your eye
 * on, while a scroll keeps the work you were following on screen and simply
 * makes room ahead. The view moves by exactly enough to put the crosshair back
 * on the margin, so a run of arrow presses pushes the board along steadily.
 *
 * The remembered view is deliberately not disturbed: Zoom Previous should go
 * back to where *you* last put the view, not to wherever it scrolled itself
 * while you were typing.
 */
function keepCursorInView(): void {
  if (!cursorWorld) return;
  const sx = viewport.toScreenX(cursorWorld.x);
  const sy = viewport.toScreenY(cursorWorld.y);
  // A margin of a tenth of the smaller side: far enough in that a corner does
  // not trigger it constantly, close enough that nothing is drawn off-screen.
  const margin = Math.min(viewport.width, viewport.height) * 0.1;

  let dx = 0;
  let dy = 0;
  if (sx < margin) dx = sx - margin;
  else if (sx > viewport.width - margin) dx = sx - (viewport.width - margin);
  if (sy < margin) dy = sy - margin;
  else if (sy > viewport.height - margin) dy = sy - (viewport.height - margin);
  if (dx === 0 && dy === 0) return;

  // Screen y runs the other way from the drawing's, so its shift is subtracted.
  viewport.cx += dx / viewport.scale;
  viewport.cy -= dy / viewport.scale;
  updateStatus(cursorWorld.x, cursorWorld.y);
}

/**
 * Say what the crosshair is standing on.
 *
 * The original names whatever the cursor is over in its status line, which is
 * how you identify a track or a pad without disturbing it — and the reason
 * moving the crosshair over a board is a way of reading it, not just of
 * pointing at it. Silent when there is nothing there, so a command's own
 * message is not wiped by wandering over empty board.
 */
function reportUnderCursor(x: number, y: number): void {
  const hit = hitTest(doc(), x, y, doc().grid.current || 0.01);
  if (!hit) return;
  const layer = doc().layers[hit.layer]?.name;
  setMessage(`${describeSelection(hit)}${layer ? ` · ${layer}` : ''}`);
}

/**
 * Switch between the mouse pointer and the grid crosshair.
 *
 * Turning the crosshair on cancels whatever tool was in hand — Grab in
 * particular, which is the original's own reason for putting the two on one
 * key: the space bar is how you let go of what you picked up.
 */
function setGridMode(on: boolean): void {
  gridMode = on;
  toolbox.cancel();
  cancelPlace();
  if (on) {
    ensureCursor();
    updateStatus(cursorWorld?.x, cursorWorld?.y);
    setMessage('Grid cursor — arrow keys move the crosshair, Enter places, Space returns to the pointer');
  } else {
    setMessage('Pointer — arrow keys scroll the board, Space returns to the crosshair');
  }
  draw();
}

/**
 * Scroll the board by an arrow key.
 *
 * A fixed fraction of the window rather than a fixed distance, so one press
 * moves the same visible amount however far in you are zoomed. The leading
 * repeat count works here too: `5→` scrolls five steps.
 */
function scrollView(dx: number, dy: number): void {
  const n = Math.max(1, parseInt(cursorRepeat, 10) || 1);
  cursorRepeat = '';
  const step = 0.1;
  // Screen y runs the other way from the drawing's, so a press of Up scrolls
  // the view toward the top of the board.
  viewport.panByPixels(-dx * viewport.width * step * n, dy * viewport.height * step * n);
  updateStatus();
  draw();
}

/** Snap radius in screen pixels, so it feels identical at any zoom. */
const SNAP_PIXELS = 9;

/**
 * Snap targets, rebuilt only when the document changes.
 *
 * They are recomputed from the whole document, which is far too much work to
 * repeat on every mouse move, so the list is cached and invalidated on edit.
 */
let snapCache: { doc: CadDocument; version: number; points: SnapResult[] } | null = null;
let docVersion = 0;

function invalidateSnap(): void {
  docVersion++;
}

function snapCandidateCache(): SnapResult[] {
  const d = doc();
  if (snapCache && snapCache.doc === d && snapCache.version === docVersion) {
    return snapCache.points;
  }
  const points = snapCandidates(d);
  snapCache = { doc: d, version: docVersion, points };
  return points;
}

/**
 * The ratsnest, memoised on the document version. It is drawn every frame while
 * the ratsnest is on, but the underlying netlist only changes on an edit — so
 * without this it re-ran the whole union-find connectivity walk on every pan,
 * zoom and mouse-move. It only ever moves *with* the geometry, so a cached copy
 * is exact between edits.
 */
let ratsnestCache:
  { doc: CadDocument; version: number; signalsOnly: boolean; lines: RatLine[] } | null = null;
function cachedRatsnest(d: CadDocument): RatLine[] {
  const signalsOnly = state.ratsnestSignalsOnly;
  if (ratsnestCache && ratsnestCache.doc === d && ratsnestCache.version === docVersion
      && ratsnestCache.signalsOnly === signalsOnly) {
    return ratsnestCache.lines;
  }
  const all = buildRatsnest(d);
  const lines = signalsOnly ? all.filter((l) => !isPowerNet(l.net)) : all;
  ratsnestCache = { doc: d, version: docVersion, signalsOnly, lines };
  return lines;
}

/**
 * Power and ground, by the original's own list.
 *
 * Ratnest Signals Only (N T) leaves these out: they go to almost every part, so
 * on a board of any size they are most of the thicket and none of the
 * information. The names are the manual's, verbatim — GND, PWR, VCC, VSS, VDD,
 * and +nV / -nV for any number.
 */
function isPowerNet(name: string): boolean {
  const n = name.trim().toUpperCase();
  if (['GND', 'PWR', 'VCC', 'VSS', 'VDD'].includes(n)) return true;
  return /^[+-]\d+(\.\d+)?V$/.test(n);
}

/**
 * The ratsnest is hidden while the view is being moved, and comes back shortly
 * after it settles.
 *
 * On a dense board it is easily the heaviest thing on screen — hundreds of
 * ratlines redrawn every frame — and if an edit has just invalidated it, the
 * first frame of a pan also pays for rebuilding it. Dropping it for the
 * duration keeps panning and zooming smooth however big the design is, and a
 * ratsnest you cannot read mid-gesture costs nothing to lose.
 *
 * This is deliberately *not* `state.showRatsnest`: that is the user's setting,
 * and the checkbox and status line must keep reflecting what they chose.
 */
let ratsnestResting = false;
let ratsnestTimer = 0;
const RATSNEST_SETTLE_MS = 900;

function pauseRatsnest(): void {
  if (!state.showRatsnest) return;
  ratsnestResting = true;
  if (ratsnestTimer) window.clearTimeout(ratsnestTimer);
  ratsnestTimer = window.setTimeout(() => {
    ratsnestTimer = 0;
    ratsnestResting = false;
    draw();
  }, RATSNEST_SETTLE_MS);
}

/** Show the ratsnest now — asking for it explicitly should not wait out a rest. */
function resumeRatsnest(): void {
  if (ratsnestTimer) { window.clearTimeout(ratsnestTimer); ratsnestTimer = 0; }
  ratsnestResting = false;
}

// --- Shorts: geometry of two different nets touching. extractNetlist already
// flags these as signal-mismatch conflicts; we surface the offending copper in
// the attention colour and flash it so it catches the eye as you route.
let shortsVersion = -1;
let shortsMode: DocumentKind | '' = '';
let shortIds = new Set<number>();
let shortsKey = '';
let flashOn = true;
let flashTimer = 0;
let flashPulses = 0;

function refreshShorts(d: CadDocument): void {
  if (docVersion === shortsVersion && shortsMode === state.mode) return;
  shortsVersion = docVersion;
  shortsMode = state.mode;

  // Only the board flashes shorts — on a schematic a wrong-signal is a netlist
  // report, not a copper short, and the original does not flag it on the sheet.
  const ids = new Set<number>();
  if (d.kind === 'pcb') {
    for (const c of extractNetlist(d).conflicts) {
      if (c.kind === 'signal-mismatch') for (const id of c.entityIds) ids.add(id);
    }
  }
  const key = [...ids].sort((a, b) => a - b).join(',');
  const changed = key !== shortsKey;
  shortsKey = key;
  shortIds = ids;

  if (ids.size === 0) {
    if (flashTimer) { window.clearInterval(flashTimer); flashTimer = 0; }
    flashOn = true;
    return;
  }
  // A newly-created (or changed) short flashes a few times to catch the eye,
  // then settles to a steady highlight so it stays marked without pulsing on.
  if (changed) {
    flashPulses = 6; // three on/off cycles
    flashOn = true;
    if (flashTimer) window.clearInterval(flashTimer);
    flashTimer = window.setInterval(() => {
      flashOn = !flashOn;
      if (--flashPulses <= 0) {
        window.clearInterval(flashTimer);
        flashTimer = 0;
        flashOn = true; // leave the short highlighted, just not flashing
      }
      draw();
    }, 220);
    setMessage(`⚠ short — ${ids.size} object${ids.size === 1 ? '' : 's'} join different nets`);
  }
}

let frame = 0;
// True only while every draw coalesced into the pending frame is a pure
// viewport move (pan/zoom); a single geometry-changing draw() clears it, so the
// renderer rebuilds its flattened tree. See RenderOptions.reuseFlatten.
let framePanZoomOnly = false;
/**
 * Request a repaint. Pass `viewportOnly` when nothing but the pan/zoom changed
 * so the renderer can reuse its cached entity flatten instead of re-walking the
 * whole document — the difference between smooth and janky panning on a big board.
 */
function draw(viewportOnly = false): void {
  if (!viewportOnly) framePanZoomOnly = false;
  else if (!frame) framePanZoomOnly = true;
  if (frame) return;
  frame = requestAnimationFrame(() => {
    frame = 0;
    const reuseFlatten = framePanZoomOnly;
    framePanZoomOnly = false;
    const d = doc();
    refreshShorts(d);

    // The component awaiting placement is drawn as preview, not appended to
    // the document — appending it made it briefly part of the model, which
    // would have leaked into a save landing mid-placement.
    const pending = state.placing ? [...preview, state.placing] : preview;
    // The block being marked is drawn above the tools' own preview.
    const marks = [...blockPreview, ...regionPreview];
    const withBlock = marks.length ? [...pending, ...marks] : pending;
    setBlockMarch(marks.length > 0 && prefs.animatedBlock);

    renderer.render(d, {
      selected: state.selected,
      flagged: shortIds.size && flashOn ? shortIds : undefined,
      highlighted: highlightNet?.ids,
      activeLayer: state.activeLayer,
      preview: withBlock,
      // Not board-only. The original rat-nests whatever file is open — its
      // ratnest walks component pads and joins the ones sharing a signal, and a
      // schematic has those too. On a sheet the lines show which pins a net says
      // belong together but no wire yet reaches, which is exactly what you want
      // to see before you call the drawing finished.
      ratsnest: state.showRatsnest && !ratsnestResting ? cachedRatsnest(d) : undefined,
      density: state.showDensity && !ratsnestResting ? densityOverlay(d) : undefined,
      showPins: state.showPins,
      images: photoDrawable(d),
      imageOpacity: photoOpacity,
      // What is being carried is drawn hollow until it is put down.
      outlined: toolbox.holding ? state.selected : undefined,
      // The marking rectangle is an instrument, not geometry: it keeps the
      // highlight colour while a tool's own preview takes its layer's.
      previewAccent: marks.length
        ? new Set(marks.map((e) => e.id)) : undefined,
      previewDash: blockMarchTimer ? blockMarch + 1 : 0,
      crosshair: crosshairAt(),
      thickCrosshair: prefs.thickCrosshair,
      reuseFlatten,
      // Reusing the geometry and reducing the detail are separate things. The
      // settling frame does the first and not the second: nothing has moved,
      // so the flatten still stands, but this is the frame that must draw
      // everything or the board never comes into focus.
      interacting: reuseFlatten && !frameFullDetail,
    });
    frameFullDetail = false;

    // Hide the OS pointer while the big crosshair is up, so there is one
    // cursor, not two — as the original does.
    canvas.style.cursor = crosshairAt() ? 'none' : 'default';

    // A frame drawn while the view was moving left out the detail that costs
    // most and says least. Once the view stops, draw it again in full — the
    // board settles into focus a moment after you let go, which is a far
    // better trade than a view that cannot keep up with the mouse.
    if (reuseFlatten) scheduleSettle();
  });
}

/**
 * The marching block outline.
 *
 * The original animates its block perimeter, and it is not decoration: a thin
 * static rectangle over a busy sheet is invisible, and the block is the thing
 * every Block command is about to act on. Marching dashes say "this is an
 * instrument laid over your drawing", which is exactly what it is.
 *
 * Started and stopped from `draw` itself, by whether there is an outline to
 * march, so no command has to remember to turn it off. A viewport-only redraw
 * at full detail: nothing has moved, so the flattened tree still stands and
 * rebuilding it eight times a second to shift a dash would be absurd.
 */
let blockMarch = 0;
let blockMarchTimer = 0;

function setBlockMarch(on: boolean): void {
  if (on === (blockMarchTimer !== 0)) return;
  if (!on) {
    window.clearInterval(blockMarchTimer);
    blockMarchTimer = 0;
    blockMarch = 0;
    return;
  }
  blockMarchTimer = window.setInterval(() => {
    blockMarch = (blockMarch + 2) % 10;
    frameFullDetail = true;
    draw(true);
  }, 120);
}

/**
 * The board-work modules, once something has needed them.
 *
 * Held because two callers cannot await: the density overlay is built inside a
 * frame, and a frame cannot wait on a network request. It does not need to —
 * the overlay is only ever on because a command turned it on, and that command
 * loaded these first. Until then this is null and the overlay is simply absent,
 * which is what it was anyway.
 */
let bw: BoardWork | null = null;

/** Load them if they are not here yet, and remember them for the frame path. */
async function needBoardwork(): Promise<BoardWork> {
  bw = await boardwork();
  return bw;
}

/** Pending full-quality redraw after a pan or zoom stops. */
let settleTimer = 0;
/** The next frame draws everything, even though the view has not changed. */
let frameFullDetail = false;

/**
 * Redraw at full detail once the view has been still for a moment.
 *
 * Long enough that a continuous drag never triggers it, short enough that it
 * feels like part of the same gesture rather than an afterthought.
 */
function scheduleSettle(): void {
  if (settleTimer) clearTimeout(settleTimer);
  settleTimer = window.setTimeout(() => {
    settleTimer = 0;
    frameFullDetail = true;
    // A viewport frame, so the flattened tree is kept — rebuilding forty
    // thousand entities to redraw the same geometry would cost more than the
    // detail it restores.
    draw(true);
  }, 120);
}

function resize(): void {
  renderer.resize();
  refreshCanvasRect();
  draw();
}

// --- Status bar -------------------------------------------------------------

function setMessage(msg: string): void {
  el('st-msg').textContent = msg;
}

/**
 * Keep the picking rule in step with the preference and the working layer.
 *
 * Called from updateStatus, which runs after anything that could have changed
 * either. Six separate places set the active layer; hooking the one function
 * they all end up calling is a good deal safer than remembering six.
 */
function applyPickScope(): void {
  setPickScope(prefs.pickActiveLayerOnly ? 'active' : 'visible', state.activeLayer);
}

/**
 * Drop anything held that the current rule would not let you pick.
 *
 * Selecting and manipulating are the same permission asked at two different
 * times, and only the first was ever checked. Grab a track, hide its layer,
 * press an arrow key: the track moves, on a layer you cannot see, and nothing
 * on screen changes to tell you. So the selection is pruned whenever the rule
 * or the layers change, and what is gone is said rather than left to be found.
 */
function pruneSelection(): void {
  if (state.selected.size === 0) return;
  const d = doc();
  const before = state.selected.size;
  for (const id of [...state.selected]) {
    const e = d.entities.find((x) => x.id === id);
    if (!e || !selectable(d, e)) state.selected.delete(id);
  }
  const lost = before - state.selected.size;
  if (lost > 0) {
    setMessage(`${lost} item${lost === 1 ? '' : 's'} released — no longer pickable`);
  }
}

/** Status field with its Alt letter marked, wherever it falls in the word. */
function altLabel(into: HTMLElement, label: string, alt: string): void {
  const at = label.toLowerCase().indexOf(alt.toLowerCase());
  into.replaceChildren();
  if (at < 0) { into.append(label); return; }
  const key = document.createElement('span');
  key.className = 'st-key';
  key.textContent = label[at]!;
  into.append(label.slice(0, at), key, label.slice(at + 1));
}

/** On/off field: name, Alt letter, tick or cross. Shapes differ, not just colours. */
function toggleField(
  id: string, label: string, alt: string, on: boolean, title: string,
): void {
  const box = el(id);
  altLabel(box, label, alt);
  const mark = document.createElement('span');
  mark.className = on ? 'st-mark yes' : 'st-mark no';
  mark.textContent = on ? ' ✓' : ' ✗';
  box.append(mark);
  box.classList.toggle('on', on);
  box.title = title;
}

function updateStatus(wx?: number, wy?: number): void {
  const d = doc();
  applyPickScope();
  if (wx !== undefined && wy !== undefined) {
    lastPos = { x: wx, y: wy };
  }
  if (lastPos) {
    // Relative addressing measures from the zero point, and the manual marks it
    // with an asterisk after the coordinates so the mode is never a surprise.
    const rx = relativeZero ? lastPos.x - d.zeroPoint.x : lastPos.x;
    const ry = relativeZero ? lastPos.y - d.zeroPoint.y : lastPos.y;
    el('st-pos').textContent =
      `${formatPosition(rx, d)}, ${formatPosition(ry, d)}`
      + (relativeZero ? ' *' : '');
  }
  // The working command, in the original's "Place: Line" form.
  el('st-cmd').textContent = workingCommand();

  // Values, not switches — but they cycle from the keyboard, so mark the letter.
  altLabel(el('st-size'), `width ${formatDisplay(sizeForActiveTool(), state.units)}`, 'w');
  el('st-size').title = 'Track width — click to pick, Alt+W to cycle';

  // No asterisk. Ortho has its own field; on the grid it read as a modified grid.
  altLabel(el('st-grid'), `grid ${formatDisplay(d.grid.current, state.units)}`, 'g');
  el('st-grid').title = 'Grid snap spacing — click to pick, Alt+G to cycle';

  toggleField('st-snap', 'snap', 's', state.snap, state.snap
    ? 'Points are held to the grid — click to let them go anywhere (Alt+S)'
    : 'Points go where you put them — click to hold them to the grid (Alt+S)');

  // Layer field carries a colour box matching the layer, as the manual shows.
  const layer = d.layers[state.activeLayer];
  const box = el('st-layer');
  box.replaceChildren();
  if (layer) {
    const sw = document.createElement('span');
    sw.className = 'st-swatch';
    sw.style.background = `#${(layer.screen >>> 0).toString(16).padStart(6, '0').slice(-6)}`;
    box.append(sw, document.createTextNode(layer.name));
  }

  // What a click may reach. Named, not ticked — two answers, not on/off.
  // Both readings carry an l for Alt+L.
  const scope = el('st-scope');
  const only = prefs.pickActiveLayerOnly;
  altLabel(scope, only ? 'pick: this layer' : 'pick: every layer', 'l');
  scope.classList.toggle('on', only);
  scope.title = only
    ? `Only ${layer?.name ?? 'the active layer'} can be picked — click for every visible layer (Alt+L)`
    : 'Every visible layer can be picked — click to restrict to the active layer (Alt+L)';

  // Named, not ticked — four answers, and which one is in force decides what a
  // line can do. The o carries Alt+O.
  const ortho = el('st-ortho');
  altLabel(ortho, `ortho: ${ORTHO_NAMES[state.ortho]}`, 'o');
  ortho.classList.toggle('on', state.ortho !== 0);
  ortho.title = state.ortho === 0
    ? 'Ortho off — lines go where you point; click to step through the modes (Alt+O or T)'
    : `Lines are held to ${ORTHO_NAMES[state.ortho]}; click to step through the modes (Alt+O or T).`
      + ' A command sets its own mode when you give it';

  toggleField('st-pins', 'pins', 'p', state.showPins, state.showPins
    ? 'Pin numbers are shown on every pad — click to hide them (Alt+P)'
    : 'Pin numbers are hidden — click to show them on every pad (Alt+P)');

  // Named, not ticked — four answers. The prefix gives Alt+U a letter.
  altLabel(el('st-units'), `units: ${unitsWord()}`, 'u');
  el('st-units').title = state.gridUnits
    ? `Coordinates in grid steps of ${formatDisplay(d.grid.current, state.units)}`
      + ` — click for ${nextUnitsLabel()} (Alt+U)`
    : `Showing ${unitName(state.units)} — click for ${nextUnitsLabel()} (Alt+U)`;
  el('status-right').textContent =
    `${d.entities.length} objects · ${Math.round(viewport.scale)} px/in`;

  updateRatsnestStatus();
}

/**
 * Show or hide the pin number on every pad.
 *
 * One route for the button, the menu command and anything else that wants it,
 * so the button can never disagree with what is on screen.
 */
/**
 * Swap what a click is allowed to reach: every visible layer, or only the one
 * being worked on.
 *
 * One route for the status-bar button and Alt+L. The choice is a preference
 * rather than document state — it is about how you like to work, not about the
 * board — so it is saved.
 */
function togglePickScope(): void {
  prefs = { ...prefs, pickActiveLayerOnly: !prefs.pickActiveLayerOnly };
  savePrefs(prefs);
  applyPickScope();
  // Anything already picked that the new rule would not have picked has to go,
  // or the restriction only applies to what you select next — and the thing
  // already held is the one about to be moved.
  pruneSelection();
  updateInfo();
  updateStatus();
  draw();
  setMessage(prefs.pickActiveLayerOnly
    ? `Picking only on ${doc().layers[state.activeLayer]?.name ?? 'the active layer'}`
    : 'Picking on every visible layer');
}

/** Next unit round: inches, millimetres, thou. */
function nextUnit(unit: DisplayUnit): DisplayUnit {
  return unit === 'in' ? 'mm' : unit === 'mm' ? 'mil' : 'in';
}

/**
 * A coordinate for the status line, in grid steps or in a length.
 *
 * Grid steps only apply to a position. A track width of ".1 grid" would be a
 * silly thing to read, so the width field and the dialogs keep the length —
 * which is why this is not a fourth `DisplayUnit` but a mode over the top of
 * one.
 *
 * Falls back to the length when the grid is zero, since dividing by it would
 * give infinity, and to two decimals otherwise: a coordinate lands on whole
 * steps while snap is on, and the fraction is what tells you it has not.
 */
function formatPosition(inches: number, d: CadDocument): string {
  const g = d.grid.current;
  if (!state.gridUnits || !g) return formatDisplay(inches, state.units);
  const steps = inches / g;
  // Whole steps read as whole numbers; anything off-grid keeps its fraction.
  const shown = Math.abs(steps - Math.round(steps)) < 1e-9
    ? String(Math.round(steps))
    : steps.toFixed(2);
  return `${shown}g`;
}

/**
 * What the units field says, and what it means.
 *
 * Four stops on one cycle rather than a switch of its own, because all four
 * answer the same question — what the numbers on screen are counted in. Grid
 * is last, and stepping past it comes back to inches with whatever length unit
 * was in use before left untouched underneath.
 */
function unitsWord(): string {
  if (state.gridUnits) return 'grid';
  return state.units === 'in' ? 'inches' : state.units === 'mm' ? 'mm' : 'thou';
}

/** What the next press of Alt+U will give. */
function nextUnitsLabel(): string {
  if (state.gridUnits) return 'inches';
  if (state.units === 'mil') return 'grid steps';
  return unitName(nextUnit(state.units));
}

/**
 * Step the display unit. View setting only — the board stays in inches, and
 * what a fab house gets follows the export dialog, not this.
 */
function cycleUnits(): void {
  // …, thou, grid, and round to inches. Grid is a mode over the length rather
  // than a length of its own, so the unit underneath is left where it was and
  // the widths go on reading in it.
  if (state.gridUnits) { state.gridUnits = false; state.units = 'in'; }
  else if (state.units === 'mil') state.gridUnits = true;
  else state.units = nextUnit(state.units);
  updateStatus();
  updateInfo();
  draw();
  setMessage(state.gridUnits
    ? `Coordinates in grid steps of ${formatDisplay(doc().grid.current, state.units)}`
    : `Showing ${unitName(state.units)}`);
}

/**
 * Hold points to the grid, or free them.
 *
 * Grid only — pads and vertices still snap, or wires stop near pins instead of
 * joining them. The keyboard crosshair steps by grid either way; it has to
 * step by something.
 *
 * This has no counterpart in the original, which is always on a grid: give
 * `getgridsnap` a spacing of zero and it hands back .001" (OGxyz00.cpp:767),
 * and the finest entry in a board's own table is that same thousandth. So off
 * is ours, and it can put a point at a coordinate the original would round on
 * load. On by default for that reason, and worth turning back on before saving
 * anything that has to go back through CIRCAD.
 */
function setSnap(on: boolean): void {
  state.snap = on;
  updateStatus();
  setMessage(on ? 'Snapping to the grid' : 'Grid snap off — points go where you put them');
}

/**
 * Grid to snap to, or 0. Every placed point goes through this, so the setting
 * cannot be honoured in one place and forgotten in another. Pick tolerance
 * still reads `grid.current` — nearness to click is not affected by rounding.
 */
function activeGrid(): number {
  return state.snap ? doc().grid.current : 0;
}

/**
 * Set the ortho mode.
 *
 * One route for the status-bar field, Alt+O, `T` and Setup ▸ Ortho Mode, so the
 * field can never disagree with what the line tools are actually doing. The
 * hidden checkbox is the older way in and is kept in step with it.
 *
 * `quiet` is for a command announcing itself: Place Signal Wire sets H/V on its
 * way in, and "Ortho H/V" written over "Place: Signal Wire" would tell you the
 * less useful of the two things.
 */
function setOrtho(mode: OrthoMode, quiet = false): void {
  state.ortho = mode;
  el<HTMLInputElement>('opt-ortho').checked = mode !== 0;
  updateStatus();
  if (!quiet) setMessage(`Ortho ${ORTHO_NAMES[mode]}`);
}

/**
 * Step to the next ortho mode, the way the original's own key does.
 *
 * `wp->fOrtho = (wp->fOrtho + 1) & 3` — off, vertical/horizontal, the two
 * diagonals, then all four directions, and round again (OGmenu08.cpp:192). Both
 * `T` and Setup ▸ Ortho Mode run it, which is why the Setup entry is called
 * Ortho *Mode* and not Ortho.
 */
function cycleOrtho(): void {
  setOrtho(((state.ortho + 1) & 3) as OrthoMode);
}

function setShowPins(on: boolean): void {
  state.showPins = on;
  updateStatus();
  draw();
  setMessage(`Pin numbers ${on ? 'on' : 'off'}`);
}

/**
 * The current working command, in the manual's "Place: Line" form.
 *
 * The original keeps the last action verb and object on the status line so you
 * always know what a click will do; the tool the toolbox holds is that state.
 */
function workingCommand(): string {
  if (state.placing) {
    return state.placingPort ? `Place: ${PORT_LABELS[state.placingPort]}` : 'Place: Component';
  }
  // The command that armed the tool, where it has a name of its own. Two
  // commands can share a tool and a mode — Bus Wire and Ortho-Line are both the
  // line tool at 45° — and the field is supposed to say which click you are
  // about to make, not which tool is holding the pen.
  if (armedAs && toolbox.active.id === armedAs.id) return armedAs.label;
  switch (toolbox.active.id) {
    // Two tools since the wire and the line were separated, and the bar has to
    // name whichever is armed — `line` was missing here and fell through to the
    // default, so Place Line left the working-command field blank.
    case 'track': return 'Place: Signal Wire';
    case 'line': return state.ortho === 3 ? 'Place: Ortho-Line' : 'Place: Line';
    case 'arc': return 'Place: Arc';
    case 'pad': return 'Place: Pad';
    case 'via': return 'Place: Via';
    case 'junction': return 'Place: Dot';
    case 'text': return 'Place: Text';
    case 'vertex': return 'Place: Vertex';
    case 'fill': return 'Place: Filled Solid';
    case 'arc-points': return 'Place: Arc Points';
    case 'select': return grabFilter ? 'Grab: filtered' : 'Grab: Item';
    default: return '';
  }
}

/**
 * Which primitive the active tool draws.
 *
 * The size picker is context-sensitive in the original — the Place Line size
 * list is line widths, the Place Pad list is pad sizes — so the status field
 * has to know what is being placed.
 */
function pickerTarget(): 'line' | 'arc' | 'pad' | 'via' {
  switch (toolbox.active.id) {
    case 'arc': return 'arc';
    case 'pad': case 'junction': return 'pad';
    case 'via': return 'via';
    default: return 'line';
  }
}

function sizeForActiveTool(): number {
  const d = doc();
  // A schematic wire is one fixed width, so the bar must show that and not the
  // line default it does not use.
  if (toolbox.active.id === 'track' && d.kind === 'sch') return SCHEMATIC_WIRE_WIDTH;
  switch (pickerTarget()) {
    case 'pad': return d.defaults.pad?.size ?? 0.06;
    case 'via': return d.defaults.via?.size ?? 0.05;
    default: return d.defaults.line?.width ?? 0.01;
  }
}

function pickerContext(): PickerContext {
  return {
    doc: doc(),
    units: state.units,
    target: pickerTarget(),
    onChanged(message) {
      documentChanged();
      setMessage(message);
      updateStatus();
      draw();
    },
    openLayerSetup: () => openLayersCommand(),
    setActiveLayer(layer) {
      state.activeLayer = layer;
      buildLayerPanel();
    },
  };
}

/**
 * Nets ▸ Ratsnest (N R) and Ratsnest Signals Only (N T).
 *
 * It used to set the flag and return without a word, so on a schematic — where
 * the overlay was drawn only for boards — the command was indistinguishable
 * from a dead key. The overlay now covers both kinds, and the command says what
 * it found either way: a ratsnest with nothing in it is a real answer, and the
 * operator is entitled to know whether it means "all joined" or "nothing named".
 */
function showRatsnest(signalsOnly: boolean): void {
  state.showRatsnest = true;
  state.ratsnestSignalsOnly = signalsOnly;
  el<HTMLInputElement>('opt-rats').checked = true;
  resumeRatsnest();
  updateRatsnestStatus();
  draw();

  const n = cachedRatsnest(doc()).length;
  const what = signalsOnly ? 'Ratsnest, signals only' : 'Ratsnest';
  if (n > 0) {
    setMessage(`${what} — ${n} connection${n === 1 ? '' : 's'} still to make`);
    return;
  }
  const nets = routingProgress(doc()).nets.length;
  setMessage(nets === 0
    ? `${what} — nothing to show: no pin carries a signal name`
    : `${what} — nothing to show: all ${nets} nets are joined`);
}

/**
 * Say what the ratsnest is doing.
 *
 * A board whose pads carry no signals has nothing to connect, so the ratsnest
 * is correctly empty — but an empty ratsnest and a broken one look identical,
 * and the checkbox appearing to do nothing reads as a fault. State the reason.
 */
function updateRatsnestStatus(): void {
  const box = el('st-rats');
  const d = doc();
  if (!state.showRatsnest) { box.textContent = 'ratsnest off'; return; }

  const progress = routingProgress(d);
  const hasPads = d.entities.some(
    (e) => e.kind === 'component' && e.children.some((c) => c.kind === 'pad'));

  if (progress.nets.length === 0) {
    // A sheet gets its own reason. "Load the netlist from the schematic" is
    // nonsense advice when the schematic is what you are looking at, and the
    // real cause there is that no pin carries a signal name yet — nothing has
    // been wired, or nothing named.
    box.textContent = hasPads
      ? (d.kind === 'sch'
        ? 'no nets — no pin carries a signal name yet'
        : 'no nets — load the netlist from the schematic')
      : 'no nets';
    box.title = hasPads && d.kind === 'pcb'
      ? 'These footprints carry no signal names, so there is nothing to '
        + 'connect. Open Netlist and use "Load onto board".'
      : '';
    return;
  }

  box.title = '';
  box.textContent = progress.open === 0
    ? `ratsnest: all ${progress.routed} nets routed`
    : `ratsnest: ${progress.open} to route · ${progress.routed} done`;
}

// --- Layer panel ------------------------------------------------------------

function buildLayerPanel(): void {
  // The side layer list was removed in favour of the status-bar picker; keep the
  // function (many call sites) but do nothing when the element is absent.
  const list = document.getElementById('layer-list');
  if (!list) return;
  const d = doc();
  list.replaceChildren();

  d.layers.forEach((layer) => {
    const row = document.createElement('div');
    row.className = 'layer'
      + (layer.num === state.activeLayer ? ' active' : '')
      + (layer.visible ? '' : ' hidden');

    const sw = document.createElement('span');
    sw.className = 'sw';
    sw.style.background = `#${layer.screen.toString(16).padStart(6, '0')}`;

    const nm = document.createElement('span');
    nm.className = 'nm';
    nm.textContent = layer.name;

    // The layer *type* is what drives CAM, so it is shown, not just the name.
    const ty = document.createElement('span');
    ty.className = 'ty';
    ty.textContent = layerTypeLabel(layer);

    const eye = document.createElement('span');
    eye.className = 'eye';
    eye.textContent = layer.visible ? '◉' : '○';
    eye.title = 'Show or hide this layer';
    eye.addEventListener('click', (ev) => {
      ev.stopPropagation();
      layer.visible = !layer.visible;
      // Hiding a layer lets go of anything held on it: what cannot be seen
      // cannot be moved by the next keystroke.
      pruneSelection();
      buildLayerPanel();
      updateInfo();
      updateStatus();
      draw();
    });

    row.append(sw, nm, ty, eye);
    row.addEventListener('click', () => {
      state.activeLayer = layer.num;
      // With picking restricted to one layer, changing which layer that is
      // changes what may be held.
      applyPickScope();
      pruneSelection();
      buildLayerPanel();
      updateInfo();
      updateStatus();
      draw();
    });
    list.append(row);
  });
}

// --- Selection info ---------------------------------------------------------

function updateInfo(): void {
  // The right-hand panel is gone: the selection shows as a one-line summary on
  // the status bar, and a double-click opens the pop-up editor for the details.
  const sel = document.getElementById('st-sel');
  if (!sel) return;
  const n = state.selected.size;
  if (n === 0) { sel.textContent = ''; return; }
  if (n > 1) { sel.textContent = `${n} items selected`; return; }
  const id = [...state.selected][0]!;
  const e = doc().entities.find((x) => x.id === id);
  sel.textContent = e ? describeSelection(e) : '';
}

/** A compact one-line summary of an entity for the status bar. */
function describeSelection(e: Entity): string {
  const L = (v: number): string => formatDisplay(v, state.units);
  const sig = 'signal' in e && e.signal ? ` · ${e.signal}` : '';
  switch (e.kind) {
    case 'line': return `Line ${L(e.width)}${sig}`;
    case 'arc': return `Arc r${L(e.radius)}${sig}`;
    case 'pad': {
      const size = e.sizeY !== e.sizeX ? `${L(e.sizeX)}×${L(e.sizeY)}` : L(e.sizeX);
      // `data` carries the pin's name on an imported footprint — worth showing,
      // since "pin 8" tells you far less than "pin 8 VCC".
      const pin = e.pin ? ` pin ${e.pin}${e.data ? ` ${e.data}` : ''}` : '';
      return `Pad ${e.type} ${size}${pin}${sig}`;
    }
    case 'text': {
      const t = (e.text ?? '').replace(/[\r\n\xff]/g, ' ');
      return `Text "${t.length > 24 ? `${t.slice(0, 24)}…` : t}"`;
    }
    case 'component': return `Component ${e.name}`;
    case 'fill': return `Fill${sig}`;
    case 'spiral': return `Spiral${sig}`;
    default: return 'Item';
  }
}

// --- Part browser -----------------------------------------------------------

let currentParts: PartInfo[] = [];
let selectedPartId: string | null = null;
/** Every library the server knows about, unfiltered. */
let allLibraries: LibraryInfo[] = [];

async function loadLibraries(): Promise<void> {
  try {
    allLibraries = await parts.libraries();
    if (allLibraries.length === 0) {
      showPartsError(
        'The server returned no libraries.',
        'The database is reachable but its catalogue is empty. Run server/install.php.',
      );
      return;
    }
    await populateLibrarySelect(true);
  } catch (err) {
    showPartsError('Could not load the part catalogue.', (err as Error).message);
  }
}

/**
 * Show only the libraries that belong in the current editor.
 *
 * A schematic library holds symbols and a PCB library holds footprints; they
 * are not interchangeable, and offering both meant a 7400 symbol could be
 * dropped onto a board. The kind comes from the library itself — the compact
 * reader infers it from whether the catalogue declares pins.
 */
async function populateLibrarySelect(announce = false): Promise<void> {
  const select = el<HTMLSelectElement>('library-select');
  const wanted = allLibraries.filter((l) => l.kind === state.mode);

  select.replaceChildren();
  for (const l of wanted) {
    const opt = document.createElement('option');
    opt.value = l.id;
    opt.textContent = `${l.name} — ${l.parts}`;
    select.append(opt);
  }

  if (wanted.length === 0) {
    showPartsError(
      `No ${state.mode === 'pcb' ? 'footprint' : 'symbol'} libraries available.`,
      `The catalogue has no ${state.mode} libraries loaded.`,
    );
    return;
  }

  // Prefer a sensible default per mode rather than whatever sorts first.
  const preferred = state.mode === 'pcb' ? ['THD', 'SMD', 'PCB'] : ['TTL', 'DISCRETE'];
  const first = preferred
    .map((n) => wanted.find((l) => l.name.toUpperCase() === n))
    .find((l) => l !== undefined) ?? wanted[0]!;

  select.value = first.id;
  await showLibrary(first.id);
  // Only say so when the catalogue is what the user asked for. This runs as a
  // background refresh on every mode switch too, and it finishes *after* the
  // command that caused the switch — so announcing unconditionally wiped out
  // whatever that command had just reported, including an import's own result.
  if (announce) {
    setMessage(`${wanted.length} ${state.mode === 'pcb' ? 'footprint' : 'symbol'} libraries`);
  }
}

/**
 * Surface a catalogue failure where the parts would be. An empty list with a
 * one-line status message reads as "there are no parts", which is a different
 * and much less actionable problem than "the request failed".
 */
function showPartsError(headline: string, detail: string): void {
  const box = el('part-list');
  box.replaceChildren();

  const wrap = document.createElement('div');
  wrap.className = 'panel-error';

  const h = document.createElement('strong');
  h.textContent = headline;

  const p = document.createElement('p');
  p.textContent = detail;

  wrap.append(h, p);
  box.append(wrap);
  setMessage(headline);
}

async function showLibrary(id: string): Promise<void> {
  currentParts = await parts.listParts(id);
  renderPartList(currentParts);
}

function renderPartList(list: PartInfo[]): void {
  const box = el('part-list');
  box.replaceChildren();

  let category = '';
  // Long catalogues (DISCRETE has 782) would blow out the DOM, so cap and say so.
  const shown = list.slice(0, 400);

  for (const p of shown) {
    if (p.category !== category) {
      category = p.category;
      const h = document.createElement('div');
      h.className = 'cat';
      h.textContent = category || 'Uncategorised';
      box.append(h);
    }

    const row = document.createElement('div');
    row.className = 'part' + (p.id === selectedPartId ? ' sel' : '');
    row.dataset.id = p.id;

    const nm = document.createElement('span');
    nm.className = 'nm';
    nm.textContent = p.name;

    const ds = document.createElement('span');
    ds.className = 'ds';
    ds.textContent = p.description;

    row.append(nm, ds);

    // A part with several gates is placed one gate at a time, as in the
    // original — so surface the count rather than hiding it.
    if (p.sections > 1) {
      const g = document.createElement('span');
      g.className = 'gates';
      g.textContent = `${p.sections}×`;
      g.title = `${p.sections} gates`;
      row.append(g);
    }

    // A part in one of your own libraries can be removed from it. Stock parts
    // have no cross, because they are shared and not yours to delete.
    if (p.deletable) {
      const del = document.createElement('button');
      del.className = 'part-del';
      del.type = 'button';
      del.textContent = '✕';
      del.title = `Delete ${p.name} from ${p.library}`;
      del.addEventListener('click', (ev) => {
        ev.stopPropagation(); // not a click on the row: do not place it
        void deletePartCommand(p);
      });
      row.append(del);
    }

    // Hovering shows what the part looks like; clicking picks it up. The
    // preview means you can browse a library without placing anything.
    row.addEventListener('mouseenter', () => { void showPartPreview(p); });
    row.addEventListener('click', () => { void beginPlace(p); });
    box.append(row);
  }

  if (list.length > shown.length) {
    const more = document.createElement('div');
    more.className = 'cat';
    more.textContent = `…and ${list.length - shown.length} more — refine your search`;
    box.append(more);
  }
}

/** Section counter so repeated placement walks through a package's gates. */
const gateCursor = new Map<string, number>();

/**
 * Draw a part into the picker's preview box, the way the original shows what a
 * part looks like before you commit to placing it.
 *
 * It renders through the real Renderer against a throwaway document that
 * borrows the current one's layers, so the preview uses the same colours and
 * pad shapes the part will have once placed — not an approximation of them.
 */
let previewToken = 0;
async function showPartPreview(p: PartInfo): Promise<void> {
  const box = el('part-side');
  const canvas = el<HTMLCanvasElement>('part-preview-canvas');
  const info = el('part-info');
  const token = ++previewToken;

  let comp: ComponentEntity | null = null;
  try {
    comp = await parts.build(p.id, gateCursor.get(p.id) ?? 0);
  } catch { comp = null; }
  // A slower earlier request must not overwrite a newer selection.
  if (token !== previewToken) return;

  if (!comp) { box.hidden = true; return; }
  box.hidden = false;

  // What the part is, under the picture of it.
  info.replaceChildren();
  const row = (label: string, value: string): void => {
    if (!value) return;
    const dt = document.createElement('dt');
    dt.textContent = label;
    const dd = document.createElement('dd');
    dd.textContent = value;
    info.append(dt, dd);
  };
  const title = document.createElement('dd');
  title.className = 'wide';
  title.textContent = p.name;
  info.append(title);
  row('Pins', String(comp.children.filter((c) => c.kind === 'pad').length));
  // A multi-gate part is one physical package however many gates you place.
  if (p.sections > 1) row('Gates', `${p.sections} per package`);
  row('Footprint', p.footprint ?? '');
  row('Category', p.category);
  row('Library', p.library);
  row('', p.description);

  const src = doc();
  const preview: CadDocument = {
    ...src,
    entities: [comp],
    // Give it its own display so the preview keeps a plain background whatever
    // the sheet is set to.
    display: src.display ? { ...src.display, dotSpacing: 0 } : undefined,
  };

  const vp = new Viewport();
  const r = new Renderer(canvas, vp);
  vp.width = canvas.clientWidth || canvas.width;
  vp.height = canvas.clientHeight || canvas.height;
  vp.dpr = window.devicePixelRatio || 1;
  canvas.width = Math.max(1, Math.round(vp.width * vp.dpr));
  canvas.height = Math.max(1, Math.round(vp.height * vp.dpr));

  const b = componentRect(comp);
  if (b) vp.fit(b.x1, b.y1, b.x2, b.y2, 0.15);
  else { vp.cx = comp.x; vp.cy = comp.y; vp.scale = 300; }

  r.render(preview, { showGrid: false, activeLayer: state.activeLayer });
}

/** Hide the preview box, e.g. when the picker closes or the list is filtered. */
function hidePartPreview(): void {
  previewToken++;
  const box = document.getElementById('part-side');
  if (box) box.hidden = true;
}

/** Remove a part from the user's own library, after asking. */
async function deletePartCommand(p: PartInfo): Promise<void> {
  if (!window.confirm(`Delete ${p.name} from ${p.library}?\n\nThis cannot be undone.`)) return;
  try {
    await parts.deletePart(p.id);
    if (selectedPartId === p.id) { selectedPartId = null; hidePartPreview(); }
    // Refresh from the source rather than splicing the row out, so the library
    // counts and any open search stay honest.
    const search = el<HTMLInputElement>('part-search').value.trim();
    currentParts = search.length >= 2
      ? await parts.search(search)
      : await parts.listParts(p.library);
    renderPartList(currentParts);
    void populateLibrarySelect();
    setMessage(`Deleted ${p.name}`);
  } catch (err) {
    setMessage(`Could not delete ${p.name}: ${(err as Error).message}`);
  }
}

async function beginPlace(p: PartInfo): Promise<void> {
  // Last line of defence: a symbol on a board, or a footprint on a schematic,
  // is never what was meant. The browser should already have filtered it out,
  // so say so plainly rather than silently doing nothing.
  if (p.kind !== state.mode) {
    setMessage(
      `${p.name} is a ${p.kind === 'sch' ? 'schematic symbol' : 'PCB footprint'} — `
      + `switch to ${p.kind === 'sch' ? 'Schematic' : 'Board'} to place it`,
    );
    return;
  }

  selectedPartId = p.id;
  renderPartList(currentParts);

  const section = gateCursor.get(p.id) ?? 0;
  try {
    const comp = await parts.build(p.id, section);
    if (!comp) { setMessage(`${p.name} has no placeable symbol`); return; }

    // A library part carries the layer *numbers* of the stack it was drawn
    // against, and a number means nothing without its table. Documents that
    // came in from Gerber or KiCad have had their table fitted to what they
    // contain, so index three is no longer necessarily Top Silk — without this
    // a footprint's reference designator lands on whatever now sits there.
    const stock = state.mode === 'sch' ? defaultSchLayers() : defaultPcbLayers();
    const moved = adoptLayers(comp, doc(), stock);
    if (moved > 0) buildLayerPanel();

    state.placing = comp;
    state.placingPort = null; // a part, not a port: grid snap, and one placement
    state.placingLabel = p.sections > 1
      ? `${p.name} — gate ${String.fromCharCode(97 + section)} of ${p.sections}`
      : p.name;
    gateCursor.set(p.id, (section + 1) % Math.max(1, p.sections));

    const hint = el('placing-hint');
    hint.hidden = false;
    hint.textContent = `Placing ${state.placingLabel} · click to place, Esc to cancel, R to rotate`;
    setMessage(`Placing ${p.name}`);
  } catch (err) {
    setMessage(`Could not build ${p.name}: ${(err as Error).message}`);
  }
}

function cancelPlace(): void {
  state.placing = null;
  state.placingPort = null;
  el('placing-hint').hidden = true;
  setMessage('');
  draw();
}

// --- Tools ------------------------------------------------------------------

const toolContext: ToolContext = {
  doc,
  get activeLayer() { return state.activeLayer; },
  get grid() { return doc().grid.current; },
  get ortho() { return state.ortho; },
  get selection() { return state.selected; },
  get pickFilter() { return grabFilter; },
  get grabChain() { return grabWholeLine; },
  setActiveLayer(layer) {
    state.activeLayer = layer;
    buildLayerPanel();
    updateStatus();
  },
  snap(x, y, opts) {
    // Snap radius is a fixed screen distance, so it feels the same at every
    // zoom: close enough to grab a pin you are aiming at, tight enough not to
    // steal the cursor when you are not.
    const tolerance = SNAP_PIXELS / viewport.scale;
    return snapPoint(doc(), x, y, {
      tolerance,
      grid: activeGrid(),
      padsOnly: opts?.padsOnly ?? false,
      candidates: snapCandidateCache(),
    });
  },
  preview,
  beginEdit(label) { history().begin(doc(), label); },
  commit() {
    history().commit();
    documentChanged();
    updateInfo();
    updateStatus();
    draw();
  },
  rollback() {
    history().rollback(doc());
    documentChanged();
    draw();
  },
  editMark() { return history().mark; },
  revertTo(mark) {
    if (!history().revertTo(doc(), mark)) return;
    documentChanged();
    invalidateSnap();
    updateInfo();
    draw();
  },
  redraw: draw,
  setMessage,
};

const toolbox = new ToolBox(toolContext);

/**
 * The keyboard menu, which replaces the tool palette.
 *
 * Tools are not a separate concept in the original — you pick Place > Signal
 * Wire, not a "wire tool" — so selecting one is just another command.
 */
const menus = new MenuSystem({
  setMessage,
  isEnabled(command) {
    // Board-only and schematic-only commands, so the menu tells the truth
    // about the document you are in.
    //
    // Place Signal Wire is a sheet command and nothing else: the original wires
    // it to layer 2 outright (`lyr = 2; setlayer(wp,lyr)`, OGmenu02.cpp:1067),
    // which is Signals on a schematic and CAM Control on a board. Copper is
    // laid with Place Line, which goes on the layer you have chosen at the
    // width the status bar is showing.
    if (command.code === 'PV') return state.mode === 'pcb';
    // Net List In is *not* board-only, though it acts on the board. It is the
    // command that carries a schematic onto one, and its dialog names both
    // documents outright rather than working on whichever is in front. Gated to
    // board mode it was unreachable from the sheet you would be running it
    // from, and on a design with no board yet it was unreachable altogether —
    // which is how opening a schematic on its own became a dead end.
    if (command.code === 'PD' || command.code === 'PW') return state.mode === 'sch';
    return true;
  },
  whyDisabled(command) {
    // Every gate above is about which editor you are in, so the reason is too.
    return state.mode === 'pcb'
      ? `${command.label} belongs to the schematic — on a board, Place Line lays copper`
      : `${command.label} belongs to the board`;
  },
});

/** Pick a tool and report it, the way a menu command would. */
/**
 * The command the armed tool was entered by, when it names itself.
 *
 * Held so the working-command field can say `Place: Bus Wire` rather than
 * `Place: Ortho-Line`: the two are the same tool in the same mode, and only the
 * command that armed it tells them apart. Dropped as soon as another command
 * takes the tool.
 *
 * The colon is the test, and it is not a formality. `Place: Filled Solid` is
 * the manual's name for a command; `Place signal dot` and `Place vertex — click
 * a line to bend it` are sentences told to the operator, and putting one of
 * those in the working-command field says the wrong thing in the wrong voice.
 */
let armedAs: { id: string; label: string } | null = null;

function useTool(id: string, label: string, ortho: OrthoMode = 0): void {
  armedAs = /^(Place|Grab|Delete): /.test(label) ? { id, label } : null;
  clickMode = null; // leaving a click mode (delete, power port) for a real tool
  grabFilter = null; // and any Grab-by-kind restriction; grabOnly re-applies it
  grabWholeLine = false;
  cancelPlace();
  toolbox.select(id);
  // Ortho belongs to the command, not to the session. Nearly every one of the
  // original's menu handlers opens with `setortho(wp,0)`, and the drawing ones
  // then ask for the mode they draw in — so entering a tool is where the old
  // mode is dropped and the new one taken up. Without this, a mode set three
  // commands ago is still bending lines you never asked it to.
  setOrtho(ortho, true);
  setMessage(label);
  updateStatus(); // refresh the working-command field
  draw();
}

/**
 * Place ▸ Signal Dot (P D), without putting the wire down.
 *
 * A junction is something you discover while routing: you bring a wire to a
 * point where another already runs, and it needs a dot to say they join. Making
 * that a tool of its own meant abandoning the run to reach it, placing the dot,
 * and starting the wire again from the corner you had got to — so the one time
 * you actually want the command is the one time it costs you the work.
 *
 * Mid-run it lays a corner where the cursor is — exactly what Enter does — and
 * drops the dot on it. The corner is the point of it: without one the wire has
 * no vertex there and only passes through, where what is wanted is a wire that
 * ends *on* the dot and carries onwards from it. The tool is not touched, so
 * the corners already laid are still laid and the run continues.
 *
 * With nothing being drawn it arms the junction tool as before, for placing
 * dots on their own.
 */
function placeSignalDotCommand(): void {
  if (state.mode !== 'sch') { setMessage('A signal dot belongs on a schematic'); return; }

  if (!toolbox.busy) {
    useTool('junction', 'Place signal dot');
    return;
  }

  const at = cursorWorld ?? ensureCursor();
  // The corner first, so the wire reaches the dot rather than running past it.
  // This is Enter, and it behaves as Enter does — including the tool's own rule
  // that a corner on top of the last one ends the run.
  dispatchPoint(at);
  // The app's own snap rather than the grid alone: the dot is meant to land on
  // the wire, and a wire corner is not always on the grid.
  const hit = snapPoint(doc(), at.x, at.y, {
    tolerance: SNAP_PIXELS / viewport.scale,
    grid: activeGrid(),
    candidates: snapCandidateCache(),
  });

  const d = doc();
  history().begin(d, 'Place signal dot');
  d.entities.push({
    kind: 'pad', id: newId(), layer: state.activeLayer,
    // Forty mil, which is what the original says it is: "a 40-mil diameter
    // surface-via on the signals layer".
    type: 'S3', x: hit.x, y: hit.y, sizeX: 0.04, sizeY: 0.04,
  });
  history().commit();
  documentChanged();
  invalidateSnap();
  draw();
  setMessage('Signal dot — the wire is joined here, carry on');
}

/**
 * Open the manual's parameter dialog for a Place command, then enter the tool.
 *
 * Cancelling the dialog leaves the current tool untouched, as the original
 * does — you have not committed to placing anything.
 */
/**
 * The layer a Place command's own default record names, if it names one.
 *
 * Only the three that offer a Layer row on their dialog have one to name. A
 * pad's layer is decided by its type and by the stack, so Place Pad asks about
 * size and drill instead.
 */
function defaultLayerFor(
  d: CadDocument, target: 'line' | 'arc' | 'pad' | 'text',
): number | undefined {
  // A default record may name its layer rather than number it, which is how
  // CIRCAD writes one; `layerNumber` resolves either.
  const raw = target === 'line' ? d.defaults.line?.layer
    : target === 'arc' ? d.defaults.arc?.layer
      : target === 'text' ? d.defaults.text?.layer
        : undefined;
  if (raw === undefined) return undefined;
  return layerNumber(d, raw);
}

function placeWithParams(
  target: 'line' | 'arc' | 'pad' | 'text', toolId: string, label: string,
  wire = false, immediate = false, ortho: OrthoMode = 0,
): void {
  const enter = (): void => {
    documentChanged(); // defaults may have changed
    // The mode goes on with the tool, not before the dialog: cancelling the
    // parameters must leave the ortho mode exactly as it found it.
    useTool(toolId, label, ortho);
    // And the layer the dialog just asked for is the layer to draw on.
    //
    // Every one of the original's Place commands opens by reading its own
    // default record and moving the working layer to it — `lyr = es->pridx;
    // setlayer(wp,lyr)` in Place Line, Ortho-Line, Arc, Spiral, Text and Filled
    // Solid (OGmenu02.cpp). Ours showed the field, wrote what you chose into
    // the defaults, and then drew on whatever layer happened to be active: the
    // Layer row on that dialog did nothing at all for Line, Ortho-Line and Arc.
    // Applied after `useTool`, because entering a tool steers the layer by type
    // and would otherwise overrule the answer to the question just asked.
    // The chosen layer stands, and is not run past the tool's own preference
    // afterwards: it was asked for outright, and a preference that overruled an
    // answer would make the question a decoration.
    const want = defaultLayerFor(doc(), target);
    if (want !== undefined && doc().layers[want]) {
      state.activeLayer = want;
      updateStatus();
      draw();
    }
  };

  // Typed, there is no dialog at all.
  //
  // The layer and the width are already chosen and already shown in the status
  // bar — that is what the bar is for — so a dialog that asks for them again is
  // asking a question that has been answered. The dialog remains the way in
  // when the command is chosen with the mouse, where the parameters are worth a
  // look on the way past. Alt+G and Alt+W change the grid and the width without
  // stopping, and the status-bar pickers give an exact value.
  //
  // The command arms and waits. It used to lay its first corner at the cursor
  // straight away, which put a point down before the user had said where — the
  // start of a wire is a decision, and typing the command is not making it.
  // Enter or Space drops the first point, as the manual has it: "press it to
  // drop a line's start, again to drop its end".
  if (immediate) {
    enter();
    return;
  }

  showPlaceParams(target, {
    doc: doc(),
    units: state.units,
    wire,
    onConfirm: enter,
  });
}


/**
 * A persistent click mode — the original's Delete mode stays active until you
 * pick another command, so a run of deletions is quick. `clickMode` handles
 * each canvas click while set; Escape clears it.
 */
let clickMode: {
  run: (w: { x: number; y: number }) => void;
  label: string;
  /** What +/- do while this mode is active, if anything. A mode that defines it
   *  takes those keys over from the layer step, which is not what they mean
   *  here — in Highlight Net they walk the nets. */
  step?: (dir: 1 | -1) => void;
} | null = null;

/**
 * What a grab is allowed to pick, or null for anything.
 *
 * The original's Grab menu is a list of kinds — Grab Line, Grab Pad, Grab Arc,
 * Grab Text, Grab Component — so you can pull a track out from under the part
 * sitting on top of it. This is that restriction; Grab Item clears it.
 */
let grabFilter: ((e: Entity) => boolean) | null = null;

/**
 * Whether a grab takes the whole run of segments a line belongs to.
 *
 * Set by Grab Multiline and cleared by every other Grab, so it lasts exactly
 * as long as the command that asked for it.
 */
let grabWholeLine = false;

/** The net lit up by Nets ▸ Highlight Net, or null. Persists until cleared. */
let highlightNet: { name: string; ids: ReadonlySet<number> } | null = null;

/** Cursor into the Net Scan issue list, stepped by Net Scan +/- (−1 = before
 *  the first). The list itself is rebuilt each step so it tracks routing. */
let scanIndex = -1;

/** Setup ▸ Zero Point: report coordinates relative to `doc.zeroPoint`. */
let relativeZero = false;
/** Last cursor position, so the status line can re-render the coordinates when
 *  the zero point or units change rather than waiting for the next mouse move. */
let lastPos: { x: number; y: number } | null = null;
/** Function-key macros (Setup ▸ Keystroke Macros), loaded from local storage. */
let macros: MacroMap = loadMacros();
/** Per-user preferences (Setup ▸ Preferences). */
let prefs: Prefs = loadPrefs();
applyTheme(prefs.theme); // before first paint, so the chrome never flashes
// The two starting documents were built before the preferences were read, so
// they get the user's File Parameters here. Everything loaded later goes
// through blankDocument() or an explicit call at the point it arrives.
applyUserDisplay(state.docs.sch);
applyUserDisplay(state.docs.pcb);

/** A new empty document, carrying the user's File Parameters. */
function blankDocument(kind: DocumentKind, name: string): CadDocument {
  return applyUserDisplay(emptyDocument(kind, name));
}

/**
 * The schematic sheets of the design, in order.
 *
 * A design is one board but may be any number of schematic sheets, which the
 * server has always modelled (`documents.sheet`). The *active* sheet is the same
 * object as `state.docs.sch`, so everything that edits "the schematic" keeps
 * working on the current sheet with no special cases; only saving, opening and
 * the netlist have to know the whole set exists.
 */
let sheets: { doc: CadDocument; id?: number }[] = [];
let sheetIndex = 0;

/**
 * Whether a board is open. Schematics answer this by `sheets` being non-empty;
 * the board needs its own flag because `state.docs.pcb` always holds a working
 * document so `doc()` is never undefined, open or not.
 */
let boardOpen = false;

/**
 * Whether the board should be offered.
 *
 * Explicitly opened, *or* carrying geometry: a board can be filled without ever
 * being opened — loading a netlist onto it is the obvious case — and a board
 * with parts on it and no button to reach it is just lost work. Deriving this
 * from the document covers every route in, rather than needing each one to
 * remember to say so.
 */
function boardIsOpen(): boolean {
  return boardOpen || state.docs.pcb.entities.length > 0;
}

/** Point `state.docs.sch` at the active sheet. */
function useSheet(i: number): void {
  if (sheets.length === 0) return;
  sheetIndex = Math.max(0, Math.min(i, sheets.length - 1));
  state.docs.sch = sheets[sheetIndex]!.doc;
}

/** Reset the sheet list to a single sheet holding the current schematic. */
function resetSheets(): void {
  sheets = [{ doc: state.docs.sch }];
  sheetIndex = 0;
}

/**
 * Text files held open as tabs — generated ones, and any opened from disk.
 *
 * A netlist or a BOM is output rather than a drawing, but it is still a file
 * you open, read, correct and save, so it belongs in the same bar as the
 * sheets and the board rather than vanishing into a download.
 */
interface TextTab {
  id: string;
  title: string;
  /** Suggested download name. */
  file: string;
  mime: string;
  text: string;
  /** Forced language, where the name would not give it away. */
  lang?: string;
  /** A line of context under the title — what made the file. */
  note?: string;
  /** Edited since it was opened or last written. */
  dirty?: boolean;
  /** Row id once it is kept in the project, so Save overwrites rather than adds. */
  docId?: number;
  /** Caret and scroll, so switching away and back lands where it was left. */
  view?: ViewState;
}
let textDocs: TextTab[] = [];
/** The text file being shown, or null when a drawing is. */
let activeText: string | null = null;

/**
 * Tabs closed this session, newest first, each able to put itself back.
 *
 * Closing is easy to do by accident and a generated report can be expensive to
 * rebuild, so nothing closed is unrecoverable until the session ends.
 */
let closedTabs: { label: string; reopen: () => void }[] = [];

function rememberClosed(label: string, reopen: () => void): void {
  closedTabs.unshift({ label, reopen });
  if (closedTabs.length > 12) closedTabs.length = 12;
}

/**
 * Documents closed this session that the project still holds.
 *
 * Closing takes a file out of the session; the next Save takes it out of the
 * project. Doing it at Save rather than at the cross is what makes it safe to
 * ask for: nothing has left the account until you say so, and putting the tab
 * back before saving takes it off this list again.
 */
let removedDocs: { id: number; label: string }[] = [];

/**
 * Mark a stored document for removal when the project is next saved.
 *
 * And mark the project changed, because it has been: a file the account still
 * holds is on its way out. Without this the Save button stayed disabled after
 * closing a tab, so the removal could never be saved and the file came back on
 * the next load — the close having quietly done nothing at all.
 */
function forgetDocument(id: number | undefined, label: string): void {
  if (id === undefined) return;
  if (removedDocs.some((r) => r.id === id)) return;
  removedDocs.push({ id, label });
  markDirty();
}

/** It is open again, so the project keeps it. */
function keepDocument(id: number | undefined): void {
  if (id === undefined) return;
  removedDocs = removedDocs.filter((r) => r.id !== id);
}

/** Show a text file, creating its tab or replacing one of the same name. */
function openTextDoc(tab: TextTab): void {
  const existing = textDocs.findIndex((r) => r.id === tab.id);
  if (existing >= 0) textDocs[existing] = tab;
  else textDocs.push(tab);
  // Running BOM Out twice makes a new file under the same name. The editor
  // reloads only when the tab changes, so say that it has.
  if (editorShowing === tab.id) editorShowing = null;
  activeText = tab.id;
  renderDocTabs();
  showTextView();
  textEditor?.focus();
}

/** A generated file — a BOM, a net list — as a tab. */
function openReportTab(
  id: string, title: string, file: string, mime: string, text: string, note?: string,
): void {
  openTextDoc({ id, title, file, mime, text, ...(note ? { note } : {}) });
}

function closeTextDoc(id: string): void {
  const i = textDocs.findIndex((r) => r.id === id);
  if (i < 0) return;
  if (activeText === id) stashTextView();
  const [gone] = textDocs.splice(i, 1);
  // Remembered with whatever was typed into it, so reopening is not a rewind.
  if (gone) {
    forgetDocument(gone.docId, gone.title);
    rememberClosed(gone.title, () => { keepDocument(gone.docId); openTextDoc(gone); });
  }
  if (activeText === id) activeText = textDocs[0]?.id ?? null;
  renderDocTabs();
  showTextView();
}

/** The tab showing, if it is a text file. */
function activeTextDoc(): TextTab | null {
  return textDocs.find((x) => x.id === activeText) ?? null;
}

/**
 * The editor, built the first time a text file is opened.
 *
 * One instance for every tab: a textarea per open file would multiply the
 * DOM for nothing, and each tab keeps its own caret and scroll here instead.
 */
let textEditor: TextEditor | null = null;
/** Which tab the editor currently holds, so switching knows what to save. */
let editorShowing: string | null = null;

function ensureEditor(): TextEditor {
  if (textEditor) return textEditor;
  textEditor = createTextEditor({
    onChange(text) {
      const doc = activeTextDoc();
      if (!doc || doc.text === text) return;
      doc.text = text;
      if (!doc.dirty) { doc.dirty = true; renderDocTabs(); }
      // A text file is a document of the project like any other, so editing one
      // is an unsaved change and Save has to say so.
      markDirty();
    },
    // Chosen by hand, so it survives switching tabs and coming back.
    onLanguage(id) {
      const doc = activeTextDoc();
      if (doc) doc.lang = id;
    },
    onSave: () => saveTextDoc(false),
    onSaveAs: () => saveTextDoc(true),
  });
  el('text-view').append(textEditor.el);
  return textEditor;
}

/** Put the caret and scroll back on the tab before the editor moves off it. */
function stashTextView(): void {
  if (!textEditor || editorShowing === null) return;
  const held = textDocs.find((x) => x.id === editorShowing);
  if (held) held.view = textEditor.getViewState();
}

/** Swap the canvas and the editor to match the active tab. */
function showTextView(): void {
  const view = document.getElementById('text-view');
  if (!view) return;
  const r = activeTextDoc();
  if (!r) {
    stashTextView();
    editorShowing = null;
    view.hidden = true;
    return;
  }
  view.hidden = false;
  const ed = ensureEditor();
  if (editorShowing !== r.id) {
    stashTextView();
    ed.load({
      text: r.text,
      fileName: r.file,
      ...(r.lang ? { lang: r.lang } : {}),
      ...(r.note ? { note: r.note } : {}),
    });
    if (r.view) ed.setViewState(r.view);
    editorShowing = r.id;
  }
}

/** Write the open text file out. Save As always asks for the name. */
function saveTextDoc(askName: boolean): void {
  const r = activeTextDoc();
  if (!r || !textEditor) { setMessage('No text file is open'); return; }
  r.text = textEditor.getText();
  let name = r.file;
  if (askName) {
    const chosen = window.prompt('Save text file as:', r.file);
    if (chosen === null) { setMessage('Save cancelled'); return; }
    name = chosen.trim() || r.file;
    r.file = name;
    r.title = name;
    // Renamed, so the editor's own title and language guess are out of date.
    r.view = textEditor.getViewState();
    editorShowing = null;
    showTextView();
  }
  downloadText(name, r.text, r.mime);
  r.dirty = false;
  renderDocTabs();
  setMessage(`Wrote ${name}`);
}

/** File ▸ New Text (F t) — an empty file to type into. */
function newTextCommand(): void {
  const n = textDocs.filter((t) => t.id.startsWith('text-')).length + 1;
  openTextDoc({
    id: `text-${untitledCount++}`,
    title: `Untitled ${n}`,
    file: `untitled${n === 1 ? '' : n}.txt`,
    mime: 'text/plain',
    text: '',
  });
  setMessage('New text file — Esc leaves the editor');
}
let untitledCount = 1;

/** Nothing open at all — the state the app boots into. */
function closeAllDocuments(): void {
  sheets = [];
  sheetIndex = 0;
  boardOpen = false;
  textDocs = [];
  activeText = null;
}

/**
 * Where a netlist comes from: every schematic sheet when there are any, else
 * the board.
 *
 * A board carries its own connectivity — copper joining pads is a net whether or
 * not anyone drew a schematic for it — so a design that only ever existed as a
 * PCB can still be scanned and have its netlist written out.
 *
 * **The sheets themselves, not a merged copy of them.** This used to hand back
 * one document holding every sheet's entities with their ids shifted apart, and
 * it was wrong twice over. Geometrically: sheets are each drawn in their own
 * frame around the same origin, so stacking them put pins and wires on top of
 * one another, and the scan joins what touches — two sheets sharing nothing at
 * all came back as two nets, each shorting a pin on one sheet to a pin on the
 * other, and Net List In then wrote those invented names onto the board's pads.
 * And practically: it was a copy, so anything that wrote to it — naming a
 * footprint, say — wrote to a document that was thrown away a moment later.
 *
 * Sheets join by *name*, which is what a signal port is for. See
 * `extractDesignNetlist`.
 */
function netlistSheets(): CadDocument[] {
  if (sheets.length > 0) return sheets.map((s) => s.doc);
  return boardIsOpen() ? [state.docs.pcb] : [];
}

/** True when the netlist is being scavenged from the board for want of a sheet. */
function netlistFromBoard(): boolean {
  return sheets.length === 0 && boardIsOpen();
}

/** Refresh the status-bar sheet field; hidden unless the schematic is showing. */
function updateSheetStatus(): void {
  const box = document.getElementById('st-sheet');
  if (!box) return;
  if (state.mode !== 'sch' || sheets.length === 0) {
    box.textContent = '';
    box.hidden = true;
    return;
  }
  box.hidden = false;
  box.textContent = sheets.length > 1
    ? `sheet ${sheetIndex + 1}/${sheets.length}`
    : 'sheet 1';
}

/**
 * The document bar: one button per open document, named after it.
 *
 * Each schematic sheet gets a button carrying its own name, the board gets one
 * carrying the board's name, and the netlist — which is derived from them
 * rather than stored — gets one of its own. With nothing open the bar is empty,
 * so the app never offers a document that is not there.
 */
function renderDocTabs(): void {
  const bar = document.getElementById('docs');
  if (!bar) return;
  bar.replaceChildren();

  const tab = (
    label: string, active: boolean, title: string,
    onClick: () => void, onClose?: () => void, onRename?: () => void,
  ): void => {
    const wrap = document.createElement('span');
    wrap.className = 'doc-tab' + (active ? ' active' : '');
    wrap.title = onRename ? `${title}\nDouble-click to rename` : title;

    const b = document.createElement('button');
    b.className = 'doc-tab-name';
    b.textContent = label;
    b.addEventListener('click', onClick);
    // Renaming where the name is: the tab is the only place a file's name is
    // shown, so it is where you would go to change it. Right-click as well as
    // double-click, since one of the two is what anybody tries first.
    if (onRename) {
      b.addEventListener('dblclick', (ev) => { ev.stopPropagation(); onRename(); });
      b.addEventListener('contextmenu', (ev) => {
        ev.preventDefault();
        ev.stopPropagation();
        onRename();
      });
    }
    wrap.append(b);

    if (onClose) {
      const x = document.createElement('button');
      x.className = 'doc-tab-close';
      x.textContent = '✕';
      x.title = `Close ${label}`;
      x.addEventListener('click', (ev) => { ev.stopPropagation(); onClose(); });
      wrap.append(x);
    }
    bar.append(wrap);
  };

  // A report tab being active means no drawing is.
  const drawingActive = activeText === null;
  const showDrawing = (): void => { activeText = null; showTextView(); };

  sheets.forEach((s, i) => {
    tab(
      s.doc.name || `Sheet ${i + 1}`,
      drawingActive && state.mode === 'sch' && i === sheetIndex,
      `Schematic sheet ${i + 1}`,
      () => {
        if (drawingActive && state.mode === 'sch' && i === sheetIndex) return;
        showDrawing();
        if (state.mode !== 'sch') enterMode('sch');
        showSheet(i);
        renderDocTabs();
      },
      () => closeSheetTab(i),
      () => renameFile(s.doc.name || `Sheet ${i + 1}`, (name) => {
        s.doc.name = name;
        markDirty();
      }),
    );
  });

  if (boardIsOpen()) {
    tab(state.docs.pcb.name || 'Board', drawingActive && state.mode === 'pcb', 'Circuit board',
      () => {
        if (drawingActive && state.mode === 'pcb') return;
        showDrawing();
        enterMode('pcb');
        renderDocTabs();
      },
      () => closeBoardTab(),
      () => renameFile(state.docs.pcb.name || 'Board', (name) => {
        state.docs.pcb.name = name;
        markDirty();
      }));
  }

  // A dot rather than a word: the bar is narrow and it says the same thing.
  for (const r of textDocs) {
    tab(`${r.title}${r.dirty ? ' •' : ''}`, activeText === r.id,
      `${r.file} — text file${r.dirty ? ', edited' : ''}`,
      () => {
        if (activeText === r.id) return;
        stashTextView();
        activeText = r.id;
        renderDocTabs();
        showTextView();
        textEditor?.focus();
      },
      () => closeTextDoc(r.id),
      () => renameFile(r.file, (name) => applyTextRename(r, name)));
  }

  // Anything closed can be put back, so a mis-aimed click on a cross — or a
  // report that took real work to produce — is not the end of it.
  if (closedTabs.length > 0) {
    const b = document.createElement('button');
    b.className = 'doc-tab reopen';
    b.textContent = '⟲';
    b.title = 'Reopen a closed tab';
    b.addEventListener('click', (ev) => { ev.stopPropagation(); reopenPicker(b); });
    bar.append(b);
  }

}

/**
 * File ▸ Rename (F R) — rename whatever is on show.
 *
 * The same thing the tab's own double-click does, reachable by name: a
 * tooltip on a tab is not a command anybody finds.
 */
function renameCommand(): void {
  const text = activeTextDoc();
  if (text) {
    renameFile(text.file, (name) => applyTextRename(text, name));
    return;
  }
  if (state.mode === 'sch') {
    const sheet = sheets[sheetIndex];
    if (!sheet) { setMessage('Nothing open to rename'); return; }
    renameFile(sheet.doc.name || `Sheet ${sheetIndex + 1}`, (name) => {
      sheet.doc.name = name;
      markDirty();
    });
    return;
  }
  if (!boardIsOpen()) { setMessage('Nothing open to rename'); return; }
  renameFile(state.docs.pcb.name || 'Board', (name) => {
    state.docs.pcb.name = name;
    markDirty();
  });
}

/** Rename a text file, and tell the editor showing it. */
function applyTextRename(r: TextTab, name: string): void {
  r.file = name;
  r.title = name;
  r.dirty = true;
  // The editor shows the name and guesses the language from it, so it has to
  // be told; the caret is put back where it was.
  if (textEditor && editorShowing === r.id) {
    r.view = textEditor.getViewState();
    editorShowing = null;
    showTextView();
  }
  markDirty();
}

/** Ask for a new name for an open file, and apply it. */
function renameFile(current: string, apply: (name: string) => void): void {
  const asked = window.prompt('Rename to:', current);
  if (asked === null) return;
  const name = asked.trim();
  if (!name || name === current) return;
  apply(name);
  renderDocTabs();
  updateSaveButton();
  setMessage(`Renamed to ${name}`);
}

/** The dropdown of tabs closed this session. */
function reopenPicker(anchor: HTMLElement): void {
  if (closedTabs.length === 0) { setMessage('Nothing has been closed'); return; }
  const items: PickerItem[] = closedTabs.map((c, i) => ({
    label: c.label,
    onPick: () => {
      closedTabs.splice(i, 1);
      c.reopen();
      renderDocTabs();
      updateSheetStatus();
      draw();
      setMessage(`Reopened ${c.label}`);
    },
  }));
  items.push({ label: '', separator: true });
  items.push({
    label: 'Forget closed tabs',
    onPick: () => { closedTabs = []; renderDocTabs(); },
  });
  showPicker(anchor, items);
}

/** Close one sheet from its tab, remembering how to put it back. */
function closeSheetTab(i: number): void {
  const sheet = sheets[i];
  if (!sheet) return;
  if (dirty && !window.confirm(
    `Close ${sheet.doc.name}? Unsaved changes to it will be lost.`)) return;

  sheets.splice(i, 1);
  forgetDocument(sheet.id, sheet.doc.name);
  rememberClosed(sheet.doc.name, () => {
    keepDocument(sheet.id);
    sheets.push(sheet);
    useSheet(sheets.length - 1);
    activeText = null;
    showTextView();
    enterMode('sch');
  });
  histories.sch.clear();
  if (sheets.length > 0) useSheet(Math.min(i, sheets.length - 1));
  else state.docs.sch = blankDocument('sch', 'Sheet 1');

  state.selected.clear();
  invalidateSnap();
  renderDocTabs();
  buildLayerPanel();
  updateInfo();
  updateSheetStatus();
  updateStatus();
  zoomFit();
  setMessage(`Closed ${sheet.doc.name}`);
}

/**
 * File ▸ New Board (F Y) — start the board a design has not got one of.
 *
 * A schematic opened on its own is a dead end. `state.docs.pcb` is blank and
 * was never opened, so there is no board tab; with no board tab there is no
 * board mode; and every board command is unavailable in schematic mode —
 * including Net List In, which is the one command that would fill a board from
 * the sheet in front of you. The only way to get a board was File ▸ New, which
 * makes one by throwing the schematic away.
 *
 * This is the inverse of closing the board from its tab, and nothing else: the
 * schematic is not touched.
 */
function newBoardCommand(): void {
  if (boardIsOpen()) {
    if (state.mode !== 'pcb' || activeText !== null) {
      activeText = null;
      showTextView();
      enterMode('pcb');
      renderDocTabs();
      buildLayerPanel();
      updateInfo();
      updateStatus();
      draw();
    }
    setMessage(`${state.docs.pcb.name || 'Board'} is already open`);
    return;
  }

  state.docs.pcb = blankDocument('pcb', 'Board');
  delete documentIds.pcb;
  histories.pcb.clear();
  boardOpen = true;
  state.selected.clear();

  activeText = null;
  showTextView();
  enterMode('pcb');
  markDirty();

  invalidateSnap();
  renderDocTabs();
  buildLayerPanel();
  updateInfo();
  updateStatus();
  draw();

  setMessage(sheets.length > 0
    ? 'New board — Nets ▸ Net List In brings the schematic onto it'
    : 'New board');
}

/** Close the board from its tab. */
function closeBoardTab(): void {
  if (dirty && !window.confirm(
    `Close ${state.docs.pcb.name}? Unsaved changes to it will be lost.`)) return;

  const kept = state.docs.pcb;
  const keptId = documentIds.pcb;
  forgetDocument(keptId, kept.name);
  rememberClosed(kept.name, () => {
    keepDocument(keptId);
    state.docs.pcb = kept;
    if (keptId !== undefined) documentIds.pcb = keptId;
    boardOpen = true;
    activeText = null;
    showTextView();
    enterMode('pcb');
  });

  boardOpen = false;
  state.docs.pcb = blankDocument('pcb', 'Board');
  delete documentIds.pcb;
  histories.pcb.clear();
  state.selected.clear();
  if (state.mode === 'pcb' && sheets.length > 0) enterMode('sch');

  invalidateSnap();
  renderDocTabs();
  buildLayerPanel();
  updateInfo();
  updateStatus();
  draw();
  setMessage(`Closed ${kept.name}`);
}

/**
 * Switch editor. Extracted from the old mode buttons so the document bar, the
 * menu and `switchMode` all go through one path.
 */
function enterMode(mode: DocumentKind): void {
  state.mode = mode;
  closePicker();
  closePartPicker();
  clickMode = null;   // a click mode from the other editor must not carry over
  highlightNet = null; // its ids belong to the document we are leaving
  scanIndex = -1;      // restart the Net Scan walk in the other editor
  state.selected.clear();
  cancelPlace();
  toolbox.cancel();
  state.activeLayer = workingLayer(state.docs[mode]);
  toolbox.ensureValid(mode);
  toolbox.refreshLayer();
  renderDocTabs();
  buildLayerPanel();
  updateInfo();
  updateSheetStatus();
  updateStatus();
  zoomFit();

  // The catalogue is per-editor: symbols for a schematic, footprints for a
  // board. Rebuild it, and clear any stale search.
  el<HTMLInputElement>('part-search').value = '';
  selectedPartId = null;
  void populateLibrarySelect();
}

/** Switch to a sheet, keeping the editor state consistent. */
function showSheet(i: number): void {
  // Re-selecting the sheet already on screen is a no-op: it would otherwise
  // clear the undo stack below for nothing.
  if (i < 0 || i >= sheets.length || i === sheetIndex) return;
  state.selected.clear();
  highlightNet = null;
  clickMode = null;
  cancelPlace();
  toolbox.cancel();
  useSheet(i);
  // The undo stack holds snapshots of the sheet that was on screen, so it does
  // not carry over — replaying one onto a different sheet would overwrite it.
  histories.sch.clear();
  invalidateSnap();
  renderDocTabs();
  buildLayerPanel();
  updateInfo();
  updateSheetStatus();
  updateStatus();
  zoomFit();
  setMessage(`Sheet ${sheetIndex + 1}: ${doc().name}`);
}

/** Add a schematic sheet and switch to it. */
function newSheetCommand(): void {
  const name = window.prompt('Name for the new sheet:', `Sheet ${sheets.length + 1}`);
  if (name === null) return;
  const fresh = blankDocument('sch', name.trim() || `Sheet ${sheets.length + 1}`);
  // Carry the current sheet's layer table over, so every sheet of a design
  // draws with the same layers rather than reverting to the stock set.
  fresh.layers = structuredClone(doc().layers);
  sheets.push({ doc: fresh });
  if (state.mode !== 'sch') switchMode('sch');
  markDirty();
  showSheet(sheets.length - 1);
}

/** The status-bar sheet picker: switch sheets, or add one. */
function sheetPicker(): void {
  const items: PickerItem[] = sheets.map((s, i) => ({
    label: `${i + 1}  ${s.doc.name}`,
    checked: i === sheetIndex,
    onPick: () => showSheet(i),
  }));
  items.push({ label: '', separator: true });
  items.push({ label: 'New sheet…', onPick: () => newSheetCommand() });
  showPicker(el('st-sheet'), items);
}

/** Enter a click-to-delete mode that removes the topmost matching entity. */
/**
 * Edit ▸ Line / Arc / Pad / Text / Component — the original's edit modes.
 *
 * Each is a click mode restricted to one kind: "click on the line you've
 * selected and a dialog box of line parameters will appear" (manual ch.3). The
 * restriction is the point — it lets you reach the track under a pad without
 * having to hit it exactly, since nothing else can be picked.
 *
 * Clicking where there is nothing of that kind lists them instead, so an item
 * too small or too buried to click is still reachable.
 */
function editMode(match: (e: Entity) => boolean, label: string): void {
  cancelPlace();
  toolbox.cancel();
  clickMode = {
    label,
    run: (w) => {
      const hit = hitTest(
        doc(), w.x, w.y, doc().grid.current || 0.01, state.activeLayer, match);
      if (hit) {
        // Anchor the editor at the click, as a double-click edit does.
        openEdit(hit, {
          x: canvasRect.left + viewport.toScreenX(w.x),
          y: canvasRect.top + viewport.toScreenY(w.y),
        });
        return;
      }
      chooseItemToEdit(match, label);
    },
  };
  setMessage(`${label} — click one to edit it, or click clear space to list them`);
  updateStatus();
}

/**
 * Where an item sits on the sheet, whatever it sits inside.
 *
 * A part's children are stored relative to its origin, so a chip's pin 1 reads
 * as being near zero. Mapping it out through each part it belongs to gives the
 * place on the board you would point at — which is what the list shows and
 * what the view jumps to.
 */
function placedExtent(
  e: Entity, owners: readonly ComponentEntity[],
): { x1: number; y1: number; x2: number; y2: number } | null {
  const b = entityExtent(e) ?? (e.kind === 'component'
    ? { x1: e.x, y1: e.y, x2: e.x, y2: e.y } : null);
  if (!b) return null;
  let pts = [
    { x: b.x1, y: b.y1 }, { x: b.x2, y: b.y1 },
    { x: b.x2, y: b.y2 }, { x: b.x1, y: b.y2 },
  ];
  for (const o of owners) pts = pts.map((p) => placeInComponent(p, o));
  return {
    x1: Math.min(...pts.map((p) => p.x)), y1: Math.min(...pts.map((p) => p.y)),
    x2: Math.max(...pts.map((p) => p.x)), y2: Math.max(...pts.map((p) => p.y)),
  };
}

/** List every item of a kind and edit the one chosen. */
function chooseItemToEdit(match: (e: Entity) => boolean, label: string): void {
  const d = doc();
  // The same rule the click test uses. Listing what a click cannot reach
  // offers the user a way round their own layer settings.
  //
  // Parts are opened up, because most of the pads on a board are pins of a
  // chip rather than loose vias: a list of pads that stopped at the part edge
  // would leave out the ones you are most likely to want.
  const items: { entity: Entity; owners: ComponentEntity[] }[] = [];
  const collect = (list: readonly Entity[], owners: ComponentEntity[]): void => {
    for (const e of list) {
      if (match(e) && selectable(d, e)) items.push({ entity: e, owners });
      if (e.kind === 'component') collect(e.children, [e, ...owners]);
    }
  };
  collect(d.entities, []);
  if (items.length === 0) { setMessage(`${label} — none on this sheet`); return; }

  openDialog({
    title: label,
    build(body, handle) {
      const list = document.createElement('div');
      list.className = 'pick-list';
      body.append(list);

      for (const { entity: e, owners } of items) {
        const row = document.createElement('button');
        row.type = 'button';
        row.className = 'pick-row';
        const what = document.createElement('span');
        // Say which part a pin belongs to, or one list of pads reads as a
        // hundred rows all called "Pad".
        const owner = owners[0];
        what.textContent = owner
          ? `${describeSelection(e)} · ${refDesOf(owner) || owner.name}`
          : describeSelection(e);
        const where = document.createElement('span');
        where.className = 'pick-where';
        const b = placedExtent(e, owners);
        where.textContent = b
          ? `${formatDisplay(b.x1, state.units)}, ${formatDisplay(b.y1, state.units)}`
          : '';
        row.append(what, where);
        row.addEventListener('click', () => {
          handle.close();
          // Put the view on it first: picking from a list is no use if the
          // thing you picked is off screen behind the dialog.
          if (b) {
            viewport.rememberView();
            const pad = 0.3;
            viewport.fit(b.x1 - pad, b.y1 - pad, b.x2 + pad, b.y2 + pad);
          }
          state.selected.clear();
          state.selected.add(e.id);
          updateStatus();
          draw();
          openEdit(e);
        });
        list.append(row);
      }

      const note = document.createElement('p');
      note.className = 'modal-hint';
      note.textContent = `${items.length} on this sheet`;
      body.append(note);

      const row = buttons(body);
      button(row, 'Close', () => handle.close(), true);
    },
  });
}

function deleteMode(
  match: (e: Entity) => boolean, label: string, atCursor = false,
): void {
  cancelPlace();
  toolbox.cancel();
  clickMode = {
    label,
    run: (w) => {
      // The kind goes *into* the scan, not over the top of it. Each of the
      // original's Delete commands calls a finder of its own — `fcline`,
      // `fcpad`, `fctext` (OGmenu05.cpp) — which only ever looks at elements of
      // that kind. Picking whatever was topmost and then asking whether it
      // happened to be a line meant Delete Line on a wire with a pad sitting on
      // it reported "nothing here", with the wire plainly under the cursor.
      // Grab has always passed its filter down; Delete was the odd one out.
      const hit = hitTest(doc(), w.x, w.y, doc().grid.current || 0.01,
        state.activeLayer, match);
      // A kind-restricted pick reaches inside parts, and Delete must not: a
      // symbol's own outline is not a line you delete by pointing at the
      // symbol. Delete Component is how a part goes.
      if (!hit || !doc().entities.includes(hit)) {
        setMessage(`${label} — nothing here`);
        return;
      }
      history().begin(doc(), label);
      doc().entities = doc().entities.filter((e) => e.id !== hit.id);
      state.selected.delete(hit.id);
      history().commit();
      documentChanged();
      updateInfo();
      updateStatus();
      draw();
      setMessage(`${label} ✓`);
    },
  };
  setMessage(`${label} — click items to delete, Esc to stop`);
  updateStatus();
  if (atCursor) runClickModeAtCursor();
}

const EPS = 1e-6;
const coincident = (ax: number, ay: number, bx: number, by: number): boolean =>
  Math.abs(ax - bx) <= EPS && Math.abs(ay - by) <= EPS;

/**
 * Delete Multi-line: click one line and remove the whole contiguous run of
 * top-level lines joined end to end, as the manual describes ("deletes any
 * contiguous group of lines... stopping only at components and pads").
 */
function enterDeleteMultiLine(atCursor = false): void {
  cancelPlace();
  toolbox.cancel();
  clickMode = {
    label: 'Delete Multi-line',
    run: (w) => {
      const start = hitTest(doc(), w.x, w.y, doc().grid.current || 0.01);
      if (!start || start.kind !== 'line') { setMessage('Delete Multi-line — click a line'); return; }

      const lines = doc().entities.filter((e): e is Extract<Entity, { kind: 'line' }> => e.kind === 'line');
      const run = new Set<number>([start.id]);
      // Grow the set across shared endpoints until nothing new joins.
      let grew = true;
      while (grew) {
        grew = false;
        for (const a of lines) {
          if (!run.has(a.id)) continue;
          for (const b of lines) {
            if (run.has(b.id)) continue;
            const joined =
              coincident(a.x1, a.y1, b.x1, b.y1) || coincident(a.x1, a.y1, b.x2, b.y2) ||
              coincident(a.x2, a.y2, b.x1, b.y1) || coincident(a.x2, a.y2, b.x2, b.y2);
            if (joined) { run.add(b.id); grew = true; }
          }
        }
      }

      history().begin(doc(), 'Delete Multi-line');
      doc().entities = doc().entities.filter((e) => !run.has(e.id));
      history().commit();
      documentChanged();
      updateInfo();
      updateStatus();
      draw();
      setMessage(`Deleted ${run.size} segment${run.size === 1 ? '' : 's'}`);
    },
  };
  setMessage('Delete Multi-line — click a line, Esc to stop');
  updateStatus();
  if (atCursor) runClickModeAtCursor();
}

/**
 * Delete Vertex: click a corner where two lines meet and rejoin them into one,
 * "as though the vertex was never there" (manual). Only merges when exactly
 * two lines share the clicked endpoint.
 */
function enterDeleteVertex(atCursor = false): void {
  cancelPlace();
  toolbox.cancel();
  clickMode = {
    label: 'Delete Vertex',
    run: (w) => {
      const lines = doc().entities.filter((e): e is Extract<Entity, { kind: 'line' }> => e.kind === 'line');
      const tol = (doc().grid.current || 0.01);
      // Find a shared corner near the click.
      let corner: { x: number; y: number } | null = null;
      for (const l of lines) {
        for (const [x, y] of [[l.x1, l.y1], [l.x2, l.y2]] as const) {
          if (Math.hypot(x - w.x, y - w.y) <= tol) { corner = { x, y }; break; }
        }
        if (corner) break;
      }
      if (!corner) { setMessage('Delete Vertex — click a corner'); return; }

      const touching = lines.filter((l) =>
        coincident(l.x1, l.y1, corner!.x, corner!.y) || coincident(l.x2, l.y2, corner!.x, corner!.y));
      if (touching.length !== 2) {
        setMessage(`Delete Vertex — ${touching.length} lines meet here, need exactly 2`);
        return;
      }

      // The far endpoint of each line becomes the merged line's endpoints.
      const far = (l: typeof touching[number]) =>
        coincident(l.x1, l.y1, corner!.x, corner!.y) ? { x: l.x2, y: l.y2 } : { x: l.x1, y: l.y1 };
      const [a, b] = touching as [typeof touching[number], typeof touching[number]];
      const p = far(a); const q = far(b);

      history().begin(doc(), 'Delete Vertex');
      a.x1 = p.x; a.y1 = p.y; a.x2 = q.x; a.y2 = q.y;
      doc().entities = doc().entities.filter((e) => e.id !== b.id);
      history().commit();
      documentChanged();
      updateInfo();
      updateStatus();
      draw();
      setMessage('Vertex removed');
    },
  };
  setMessage('Delete Vertex — click a corner, Esc to stop');
  updateStatus();
  if (atCursor) runClickModeAtCursor();
}

/**
 * Start a fresh, blank project: an empty schematic and board, detached from
 * any saved project. Reached from the File▸erase menu (FE) and the toolbar New
 * button. Save persists the whole project, so New clears both documents rather
 * than only the one on screen — a blank sheet paired with a stale board would
 * be saved together and surprise the user.
 */
function newFileCommand(): void {
  if (dirty && !window.confirm(
    'You have unsaved changes. Start a new blank file? This clears the current schematic and board.')) {
    return;
  }

  closePicker();
  closePartPicker();
  cancelPlace();
  toolbox.cancel();
  state.selected.clear();

  state.docs.sch = blankDocument('sch', 'Sheet 1');
  state.docs.pcb = blankDocument('pcb', 'Board');
  resetSheets();
  boardOpen = true; // New starts a design: one blank sheet and a blank board
  histories.sch.clear();
  histories.pcb.clear();

  // Detach from the loaded project so Save starts a new one.
  projectId = null;
  projectName = null;
  delete documentIds.pcb;
  dirty = false;
  updateSaveButton();

  // A fresh drawing opens on the layer it names, the same as a loaded one.
  state.activeLayer = workingLayer(doc());
  toolbox.refreshLayer();
  renderDocTabs();
  buildLayerPanel();
  updateInfo();
  updateSheetStatus();
  updateStatus();
  zoomFit();
  invalidateSnap();
  draw();
  setMessage('New blank file');
}

/**
 * File ▸ Close — close the page on screen: this schematic sheet, or the board.
 *
 * Closing only takes the document out of this session. A sheet stored in a
 * project is still in the project, and opening it again brings the sheet back —
 * closing is not deleting, and quietly making it so would be a nasty surprise.
 */
function closeDocumentCommand(): void {
  closePicker();
  closePartPicker();

  // Close whatever tab is showing, through the same path the tab's own cross
  // uses. They used to be separate, and this one forgot to record what it
  // closed — so a file shut from the menu could not be reopened, while the
  // identical action from the cross could.
  if (activeText !== null) { closeTextDoc(activeText); return; }
  if (state.mode === 'sch') {
    if (sheets.length === 0) { setMessage('Nothing open to close'); return; }
    closeSheetTab(sheetIndex);
    return;
  }
  if (!boardIsOpen()) { setMessage('Nothing open to close'); return; }
  closeBoardTab();
}

/** File ▸ Close All — close every open document and detach from the project. */
function closeAllCommand(): void {
  if (sheets.length === 0 && !boardIsOpen() && textDocs.length === 0) {
    setMessage('Nothing open to close');
    return;
  }
  if (dirty && !window.confirm(
    'Close all files? Unsaved changes will be lost.')) return;

  closePicker();
  closePartPicker();
  cancelPlace();
  toolbox.cancel();
  clickMode = null;
  highlightNet = null;
  scanIndex = -1;
  state.selected.clear();

  // Record everything before dropping it, so Close All is as recoverable as
  // closing the same tabs one at a time would have been.
  const keptSheets = sheets;
  const keptBoard = boardIsOpen() ? state.docs.pcb : null;
  const keptBoardId = documentIds.pcb;
  const keptReports = textDocs;
  for (const s of keptSheets) {
    rememberClosed(s.doc.name, () => {
      sheets.push(s);
      useSheet(sheets.length - 1);
      activeText = null;
      showTextView();
      enterMode('sch');
    });
  }
  if (keptBoard) {
    rememberClosed(keptBoard.name, () => {
      state.docs.pcb = keptBoard;
      if (keptBoardId !== undefined) documentIds.pcb = keptBoardId;
      boardOpen = true;
      activeText = null;
      showTextView();
      enterMode('pcb');
    });
  }
  for (const r of keptReports) {
    rememberClosed(r.title, () => openTextDoc(r));
  }

  state.docs.sch = blankDocument('sch', 'Sheet 1');
  state.docs.pcb = blankDocument('pcb', 'Board');
  closeAllDocuments();
  showTextView();
  histories.sch.clear();
  histories.pcb.clear();
  projectId = null;
  projectName = null;
  delete documentIds.pcb;
  dirty = false;
  updateSaveButton();

  invalidateSnap();
  renderDocTabs();
  buildLayerPanel();
  updateInfo();
  updateSheetStatus();
  updateStatus();
  zoomFit();
  draw();
  setMessage('Closed all files');
}

/**
 * Load parsed documents into the editors, replacing what is open.
 *
 * Shared by Import and by opening the drawing attached to a support report:
 * both arrive with documents already parsed and both mean "this is the design
 * now". Keeping one implementation is what stops the two drifting — the second
 * route would otherwise have to remember the layer reset, the footprint
 * harvest and the detach from the saved project.
 */
function adoptDocuments(docs: CadDocument[]): { sheets: number; kinds: Set<DocumentKind> } {
  // Load each file into its editor. Several schematics become the sheets of
  // one design; the last board wins.
  state.selected.clear();
  const loaded = new Set<DocumentKind>();
  const importedSheets: { doc: CadDocument; id?: number }[] = [];
  for (const newDoc of docs) {
    // Every file brings its own display record. The user's File Parameters
    // win over it, so importing cannot change settings they chose.
    applyUserDisplay(newDoc);
    // Gerber and KiCad documents arrive already fitted — the stack there was
    // ours to invent. A CIRCAD file declares its own, so it is only fitted on
    // request, since saving it back would then write a smaller table than the
    // file arrived with.
    if (prefs.fitCircadLayers && newDoc.source?.program) {
      pruneUnusedLayers(newDoc);
    }
    if (newDoc.kind === 'sch') importedSheets.push({ doc: newDoc });
    else { state.docs.pcb = newDoc; boardOpen = true; }
    histories[newDoc.kind].clear();
    loaded.add(newDoc.kind);
    // A board brings its own footprints; keep them in memory for netlist-in.
    footprints.harvest(newDoc);
  }
  if (importedSheets.length > 0) {
    sheets = importedSheets;
    useSheet(0);
  } else if (sheets.every((s) => s.doc.entities.length === 0)) {
    // Nothing but the blank sheet File ▸ New puts up. Importing a board left it
    // there, so every import appeared to open an empty sheet of its own — the
    // scaffolding of the last command showing up as part of this one. A sheet
    // with anything drawn on it stays: importing a board to go with the
    // schematic you are working on is the ordinary reason to do it.
    sheets = [];
    sheetIndex = 0;
  }
  // And the same the other way: a blank board left over from File ▸ New is not
  // part of a set of imported schematics.
  if (!loaded.has('pcb') && state.docs.pcb.entities.length === 0) {
    boardOpen = false;
  }

  // Detach from any saved project; a set of imported files is its own thing.
  projectId = null;
  delete documentIds.pcb;
  // Name the "project" after a base name shared by the files, if any.
  const base = docs[0]!.name.replace(/\.[^.]+$/, '');
  projectName = docs.every((d) => d.name.replace(/\.[^.]+$/, '') === base) ? base : null;

  // Show a loaded editor: prefer the current mode if it was loaded, else the
  // first kind we got.
  const show: DocumentKind = loaded.has(state.mode) ? state.mode : [...loaded][0]!;
  if (state.mode !== show) {
    switchMode(show);
  } else {
    // Back to the layer the drawing names, so it never opens on a copper plane
    // left active from before.
    state.activeLayer = workingLayer(state.docs[show]);
    toolbox.refreshLayer();
    buildLayerPanel();
    updateInfo();
    updateStatus();
    zoomFit();
  }
  renderDocTabs();
  updateSheetStatus();

  documentChanged();
  // A sheet is only as good as the board parts it can become. Ask now, while
  // nothing has been placed and the answer is cheap.
  if (loaded.has('sch')) void checkFootprintsAfterLoad();
  return { sheets: importedSheets.length, kinds: loaded };
}

/**
 * Import a legacy CIRCAD file, replacing the document of its kind. Reached from
 * the File menu — normally Import (FD), or Open (FO) when the CIRCAD file
 * commands preference has swapped the two.
 */
async function runImport(): Promise<void> {
  const { openImportDialog } = await import('./ui/import.ts');
  openImportDialog((docs, imported) => {
    if (dirty && !window.confirm('You have unsaved changes. Import anyway?')) return;

    const { sheets: sheetsIn, kinds } = adoptDocuments(docs);
    // A KiCad import is never lossless — courtyard and fabrication layers have
    // no CIRCAD equivalent, and a legacy schematic keeps its symbol bodies in a
    // separate file — so say what happened rather than let it be discovered.
    const foreign = imported.filter((i) => i.format !== 'CIRCAD');
    const notes = imported.flatMap((i) => i.warnings);
    setMessage(sheetsIn > 1
      ? `Opened ${projectName ?? 'project'} — ${sheetsIn} sheets`
      : kinds.size > 1
        ? `Opened ${projectName ?? 'project'} — schematic and board`
        : foreign.length > 0
          ? `Imported ${docs[0]!.name} from ${foreign[0]!.format}`
            + (notes.length ? ` — ${notes.length} note${notes.length === 1 ? '' : 's'}, see console` : '')
          : `Imported ${docs[0]!.name}`);
    if (foreign.length > 0 && notes.length > 0) {
      console.info(`KiCad import notes:\n  ${notes.join('\n  ')}`);
    }

    // Persist every part — symbols and footprints — into the personal library.
    for (const d of docs) void persistParts(d);
  },
  // Not a design: open it in the editor. A netlist or a BOM chosen here is
  // exactly the file the person meant to look at.
  (texts) => {
    for (const t of texts) {
      openTextDoc({
        id: `file-${untitledCount++}`,
        title: t.name,
        // A file out of one of our own projects remembers what it was called
        // and what it was recognised as; one picked off the disk has only its
        // name, and the editor works the language out from that.
        file: t.file ?? t.name,
        mime: 'text/plain',
        text: t.text,
        ...(t.lang ? { lang: t.lang } : {}),
      });
    }
    setMessage(texts.length === 1
      ? `Opened ${texts[0]!.name}`
      : `Opened ${texts.length} text files`);
  });
}

// --- Support ----------------------------------------------------------------

/**
 * Help ▸ Support (?R) — send a bug report or a feature request.
 *
 * The drawings that are open go with it. A fault in a CAD program is a fault
 * about geometry, and a description of geometry is not the geometry: the arc
 * that plots the wrong way round has to be looked at to be understood.
 */
async function supportCommand(): Promise<void> {
  if (!user) {
    setMessage('Sign in to send a report');
    showAccountDialog((u) => {
      user = u;
      renderAccount();
      updateSaveButton();
      void supportCommand();
    });
    return;
  }

  const { showFeedbackDialog } = await import('./ui/feedback.ts');
  const drawings: OpenDrawing[] = [];
  sheets.forEach((s, i) => {
    drawings.push({
      document: s.doc,
      kind: 'sch',
      label: s.doc.name || `Sheet ${i + 1}`,
    });
  });
  if (boardIsOpen()) {
    drawings.push({
      document: state.docs.pcb,
      kind: 'pcb',
      label: state.docs.pcb.name || 'Board',
    });
  }

  showFeedbackDialog({
    drawings,
    // What the program was doing, so a report can be placed without asking.
    context: [
      `mode ${state.mode}`,
      `${sheets.length} sheet${sheets.length === 1 ? '' : 's'}`,
      boardIsOpen() ? 'board open' : 'no board',
      `layer ${state.activeLayer}`,
      `units ${state.units}`,
    ].join(', '),
    version: __APP_VERSION__,
    onSent: (message) => setMessage(message),
  });
}

/**
 * Help ▸ Reports (?B) — read what has come in.
 *
 * Everyone can reach it: staff to work through every report, a manager to see
 * their people, anyone else to look back at what they sent.
 */
async function reportsCommand(): Promise<void> {
  if (!user) {
    setMessage('Sign in to see reports');
    showAccountDialog((u) => {
      user = u;
      renderAccount();
      updateSaveButton();
      void reportsCommand();
    });
    return;
  }

  const { showAdminDialog } = await import('./ui/admin.ts');

  showAdminDialog({
    user,
    onMessage: (m) => setMessage(m),
    onOpenDrawing: (files) => {
      if (dirty && !window.confirm('You have unsaved changes. Open the attached drawing anyway?')) {
        return;
      }
      const docs: CadDocument[] = [];
      const bad: string[] = [];
      for (const f of files) {
        try {
          docs.push(parseKeywordDocument(f.content, f.name).document);
        } catch (e) {
          bad.push(`${f.name}: ${(e as Error).message}`);
        }
      }
      if (docs.length === 0) {
        setMessage(bad[0] ?? 'Nothing to open');
        return;
      }
      adoptDocuments(docs);
      setMessage(
        `Opened ${docs.map((d) => d.name).join(', ')} from the report`
        + (bad.length > 0 ? ` — ${bad.length} could not be read` : ''),
      );
    },
  });
}

/**
 * File ▸ Gerber In / View (FH) — open a set of photoplot files.
 *
 * The viewer comes first and the import is a button on it: a heap of files
 * with names that mean something only by convention has to be *seen* before it
 * can be trusted, and looking at a set someone sent is a good enough reason on
 * its own.
 */
async function gerberInCommand(): Promise<void> {
  const { openGerberDialog } = await import('./ui/gerber-in.ts');
  openGerberDialog((imported, note) => {
    if (dirty && !window.confirm('You have unsaved changes. Open these Gerbers anyway?')) return;
    adoptDocuments([imported]);
    setMessage(note);
  });
}

/**
 * Views ▸ Board View (VB) and Render (VR) — the board as it would be made.
 *
 * Over the top of the drawing, not beside it: this is for looking at rather
 * than working in, so it takes the whole window and gives it back. The editor
 * underneath is not redrawn while it is up, so it costs the drawing nothing.
 *
 * The two codes only choose which view it opens on — Back returns, and the
 * 2D/3D switch inside moves between them without coming back out here.
 */
async function boardViewCommand(mode: '2d' | '3d'): Promise<void> {
  if (state.mode !== 'pcb') {
    setMessage('The board view is for boards — switch to the board first');
    return;
  }
  const { openBoardView } = await import('./view/board-view.ts');
  openBoardView(doc(), mode);
  setMessage('Esc or Back returns to the drawing');
}

/** Save all of a design's parts into the user's personal library, if signed in. */
async function persistParts(newDoc: CadDocument): Promise<void> {
  if (!user) return;
  const items = harvestParts(newDoc);
  if (items.length === 0) return;
  try {
    const r = await parts.savePart(items);
    const n = r.added + r.updated;
    if (n > 0) {
      setMessage(`Imported ${newDoc.name} · ${n} part${n === 1 ? '' : 's'} saved to your library`);
    }
  } catch (e) {
    setMessage(`Imported ${newDoc.name} · could not save parts: ${(e as Error).message}`);
  }
}

/**
 * The name each port command last used — the original's `wp->SignalName`.
 *
 * Kept per kind so the bus you were numbering is not forgotten because you
 * dropped a ground in the middle of it.
 */
const portNames: Record<PortKind, string> = {
  signal: 'SIG', bus: 'D0', gnd: 'GND', earth: 'EARTH', 'named-gnd': 'GND', power: 'VCC',
};

/**
 * Place ▸ Signal Port / Bus Port / +− Port / the three grounds.
 *
 * Each is a `$$` component with one pin, exactly as `placepart` builds them
 * (OGmenu00.cpp:1120). The pin's signal is the connection — merge by name is
 * what puts every `D3` on one net whether or not a wire joins them — and the
 * label is the `&@` marker, so renaming the net renames the label.
 *
 * The port is taken into hand rather than dropped on the next click: it follows
 * the cursor, R turns it, and it re-arms after each placement so a bus is
 * clicked out rather than typed out. A signal or bus port numbers up as it goes
 * (`plincr`), which is what makes `D0`…`D7` eight clicks.
 */
function placePortCommand(kind: PortKind): void {
  if (state.mode !== 'sch') {
    setMessage(`${PORT_LABELS[kind]} goes on the schematic`
      + (kind === 'gnd' || kind === 'power'
        ? ' — on the board, power and ground are poured (Nets ▸ Pour Planes)' : ''));
    return;
  }
  cancelPlace();
  toolbox.cancel();
  closePicker();

  // The two fixed grounds have no name to ask for: the original hardcodes
  // `Signal:GND` and `Signal:EARTH` into their blocks.
  if (kind !== 'gnd' && kind !== 'earth') {
    const typed = window.prompt(`${PORT_LABELS[kind]} — signal name:`, portNames[kind]);
    if (typed === null) return;
    const trimmed = typed.trim();
    if (!trimmed) { setMessage(`${PORT_LABELS[kind]} — a port needs a name`); return; }
    portNames[kind] = kind === 'power' ? trimmed.toUpperCase() : trimmed;
  }
  armPort(kind);
}

/** Put a port in hand, built for the name this kind is currently on. */
function armPort(kind: PortKind): void {
  const d = doc();
  const name = portNames[kind];
  state.placing = buildPort(kind, name, schLayers(d));
  state.placingPort = kind;
  state.placingLabel = `${PORT_LABELS[kind]} ${portSignal(kind, name)}`;
  const hint = el('placing-hint');
  hint.hidden = false;
  hint.textContent = `Placing ${state.placingLabel} · click a pin or wire, `
    + 'Esc to stop, R to rotate';
  setMessage(`${state.placingLabel} — click a pin or wire end`);
  draw();
}

function registerCommands(): void {
  menus.registerAll({
    // --- File. FE (Erase) starts a fresh blank file; FD (Load) imports a
    // legacy design; FO opens a saved project; FA saves-as a native file.
    FE: () => newFileCommand(),
    FY: () => newBoardCommand(),
    // A blank text file. The original's own letters for it, and the same idea:
    // a text file is a document in this window, not a side trip.
    Ft: () => newTextCommand(),
    FW: () => renameCommand(),
    // Save means the project, whatever is showing — text files go with it. The
    // editor's own Save button and Ctrl+S write the file out, and File ▸ Save
    // As does too; those are the ones that mean a file on disk.
    FS: () => void saveProject(),
    // The Open and Import slots swap when the CIRCAD file commands preference
    // is on, so File ▸ Open reaches for a design file as the original did.
    FO: () => { if (prefs.legacyFileVerbs) void runImport(); else openProjectsCommand(); },
    FC: () => closeDocumentCommand(),
    FQ: () => closeAllCommand(),
    FP: () => printCommand(),
    FM: () => imageSaveCommand(),
    FB: () => bomCommand(),
    FD: () => { if (prefs.legacyFileVerbs) openProjectsCommand(); else void runImport(); },
    // A design is one board and any number of schematic sheets. New Sibling
    // adds a sheet; Next/Prev step through them.
    Fi: () => newSheetCommand(),
    FN: () => showSheet(sheetIndex + 1),
    FV: () => showSheet(sheetIndex - 1),
    FH: () => void gerberInCommand(),
    FA: () => { if (activeText !== null) saveTextDoc(true); else void saveAsCommand(); },
    FF: () => void saveToProjectCommand(),
    FK: () => void exportKicadCommand(),
    FJ: () => { void circadOutCommand(); },
    // Re-Open. Ten slots, registered whether or not the list is that long —
    // the menu shows only as many as there are, and a handler that is missing
    // reports the command as unbuilt rather than as empty.
    ...Object.fromEntries(
      Array.from({ length: RECENT_MAX }, (_, i) => [`F${i}`, () => void reopenRecent(i)]),
    ),
    'F?': () => purgeRecent(),
    'F>': () => void purgeMissingRecent(),
    FG: () => void gerberOutCommand(),
    FX: () => {
      if (!user) { setMessage('Not signed in'); return; }
      if (dirty && !window.confirm('Quit and sign out? Unsaved changes will be lost.')) return;
      void signOut();
    },

    // --- Place. Chosen with the mouse, each opens the manual's parameter
    // dialog: set the parameters, Confirm, draw. Typed, there is no dialog —
    // the layer and width are already set and already on show, so the tool is
    // entered and the placement begins at the cursor. Via has its own dialog
    // (Setup Via Params) and Dot is a fixed schematic marker, so those enter
    // their tool directly.
    // Each of these sets the ortho mode it draws in, which is the original's
    // model: `placeline(wp,mod)` takes the mode as its argument, and the four
    // line commands pass 1, 0 and 3 respectively (OGmenu02.cpp:106,157,1097).
    // A signal wire is held to the vertical and the horizontal and cannot run
    // diagonally at all; a plain line is not held to anything.
    PW: (how) => placeWithParams('line', 'track', 'Place: Signal Wire', true, how?.viaKeyboard, 1),
    PL: (how) => placeWithParams('line', 'line', 'Place: Line', false, how?.viaKeyboard, 0),
    PO: (how) => placeWithParams('line', 'line', 'Place: Ortho-Line', false, how?.viaKeyboard, 3),
    PA: (how) => placeWithParams('arc', 'arc', 'Place: Arc', false, how?.viaKeyboard),
    PP: (how) => placeWithParams('pad', 'pad', 'Place: Pad', false, how?.viaKeyboard),
    // Place Via opens its parameter dialog first, like the other Place
    // commands, since Setup has no separate entry for it in the original.
    PV: () => placeViaCommand(),
    PT: (how) => placeWithParams('text', 'text', 'Place: Text', false, how?.viaKeyboard),
    'P-': () => placeRatLineCommand(),
    PD: () => placeSignalDotCommand(),
    PE: () => useTool('vertex', 'Place vertex — click a line to bend it'),
    PC: () => openPartPicker(),
    PI: () => placeBusWire(),
    PB: () => placePortCommand('bus'),
    PN: () => placeSignalNameCommand(),
    PX: () => placeAnnotation('dimension'),
    PR: () => placeAnnotation('arrow'),
    // Build Component: draw a part, then declare it one. Block ▸ Build
    // Component is the same command reached from the block menu.
    PU: () => buildComponentCommand(),
    PF: () => useTool('fill', 'Place: Filled Solid'),
    PY: () => useTool('arc-points', 'Place: Arc by points'),

    // --- Ports. Each is a `$$` component with one pin, and the pin's signal is
    // the connection: every port of the same name is one net, wire or no wire.
    PG: () => placePortCommand('gnd'),
    PH: () => placePortCommand('earth'),
    PM: () => placePortCommand('named-gnd'),
    'P+': () => placePortCommand('power'),
    PS: () => placePortCommand('signal'),

    // --- Grab (select and transform). These are the manual's Ch.13 shortcut
    // keys — R, A, X, Y, M, K on a grabbed item — given their menu entries too,
    // so they are discoverable rather than only known to whoever read the book.
    GI: (how) => { useTool('select', 'Grab item'); if (!how?.fromDefault) grabAtCursor(); },
    // Grab by kind, so a track can be pulled out from under the part on top.
    GL: (how) => grabOnly((e) => e.kind === 'line', 'Grab: Line', false, !how?.fromDefault),
    GP: (how) => grabOnly((e) => e.kind === 'pad', 'Grab: Pad', false, !how?.fromDefault),
    GA: (how) => grabOnly((e) => e.kind === 'arc', 'Grab: Arc', false, !how?.fromDefault),
    GT: (how) => grabOnly((e) => e.kind === 'text', 'Grab: Text', false, !how?.fromDefault),
    GC: (how) => grabOnly((e) => e.kind === 'component', 'Grab: Component', false, !how?.fromDefault),
    GO: (how) => grabOnly((e) => e.kind === 'component', 'Grab: Component only', false, !how?.fromDefault),
    GF: (how) => grabOnly((e) => e.kind === 'fill', 'Grab: Filled Solid', false, !how?.fromDefault),
    GW: (how) => grabOnly((e) => e.kind === 'line', 'Grab: Multiline', true, !how?.fromDefault),
    GE: (how) => { useTool('grabvertex', 'Grab: Vertex'); if (!how?.fromDefault) grabAtCursor(); },
    // The three grips on a curve. No grab-at-cursor: each waits for you to
    // point at the edge of the curve you mean, which is what the original asks
    // for and what makes the handle it jumps to predictable.
    GG: () => useTool('arc-origin', 'Grab: Arc Origin'),
    GD: () => useTool('arc-end', 'Grab: Arc End Point'),
    GV: () => useTool('arc-centre', 'Grab: Curve Centre'),
    GR: () => transformSelection('rotate'),
    GN: () => rotateSelectionBy(),
    GX: () => mirrorSelection('x'),
    GY: () => mirrorSelection('y'),
    GM: () => transformSelection('mirror'),
    GS: () => scaleSelection(),

    // --- Edit (the original edits an item by clicking it; Query Items is the
    // closest command, and opens the same details dialog)
    EQ: () => editSelection(),
    ES: () => snapItemCommand(),
    // Six of these moved off the original's codes rather than displace the item
    // editor above — see RECODED. Replace and Replace Next keep theirs, whose
    // uppercase halves are not built.
    EW: () => lineToArcCommand(),
    EH: () => compareTextCommand('align'),
    EI: () => compareTextCommand('compare'),
    EJ: () => findCommand('find'),
    E5: () => findCommand('next'),
    Er: () => findCommand('replace'),
    Ed: () => findCommand('again'),
    E8: () => findCommand('confirm'),
    // The clipboard is the Block one: Edit and Block are two doors on the same
    // held geometry in the original too, and two clipboards would be a way to
    // lose work by copying into the wrong one.
    EY: () => blockCopyClipboardCommand(),
    EB: () => blockPasteClipboardCommand(),
    // Edit by kind: click one of that kind to open its parameters, exactly as
    // the Delete modes click one to remove it.
    EL: () => editMode((e) => e.kind === 'line', 'Edit: Line'),
    EA: () => editMode((e) => e.kind === 'arc', 'Edit: Arc'),
    EP: () => editMode((e) => e.kind === 'pad', 'Edit: Pad'),
    ET: () => editMode((e) => e.kind === 'text', 'Edit: Text'),
    EX: () => editMode((e) => e.kind === 'text', 'Edit: Text Parameters'),
    // Two scopes, because a sheet is mostly not parts: on 5300quoteB, 93 of the
    // 138 blocks are ground, power and signal-port symbols. Component lists
    // everything placed; Components lists only what is actually a part.
    EN: () => editMode((e) => e.kind === 'component', 'Edit: Component (all blocks)'),
    EC: () => editMode(isPhysicalPart, 'Edit: Components (parts only)'),
    EF: () => editMode((e) => e.kind === 'fill', 'Edit: Filled Solid'),

    // --- Delete. DG acts on the grabbed selection; the rest are click modes.
    DG: () => deleteSelection(),
    DL: (how) => deleteMode((e) => e.kind === 'line', 'Delete Line', how?.viaKeyboard && !how?.fromDefault),
    DP: (how) => deleteMode((e) => e.kind === 'pad', 'Delete Pad/Via', how?.viaKeyboard && !how?.fromDefault),
    DA: (how) => deleteMode((e) => e.kind === 'arc', 'Delete Arc', how?.viaKeyboard && !how?.fromDefault),
    DT: (how) => deleteMode((e) => e.kind === 'text', 'Delete Text', how?.viaKeyboard && !how?.fromDefault),
    DC: (how) => deleteMode((e) => e.kind === 'component', 'Delete Component', how?.viaKeyboard && !how?.fromDefault),
    DF: (how) => deleteMode((e) => e.kind === 'fill', 'Delete Filled Solid', how?.viaKeyboard && !how?.fromDefault),
    DM: (how) => enterDeleteMultiLine(how?.viaKeyboard && !how?.fromDefault),
    DE: (how) => enterDeleteVertex(how?.viaKeyboard && !how?.fromDefault),
    DO: () => { void removeFloodCommand(); },
    // --- Copper cleanup, the tidying a pour needs before it can be made.
    DI: () => isolationIslandsCommand(),
    DR: () => deleteTeardropsCommand(),
    DH: () => deleteHighlightedCommand(),
    BA: () => addTeardropsCommand(),

    // --- Un-Do
    UU: () => undoRedo(false),
    UR: () => undoRedo(true),
    UA: () => undoRedoAll(false),
    UE: () => undoRedoAll(true),
    UP: () => purgeHistory('redo'),
    US: () => purgeHistory('undo'),

    // --- Block (fills and planes)
    BF: () => { void floodCommand(); },
    BB: () => buildComponentCommand(),
    BC: () => blockCopyCommand(),
    BM: () => blockMoveCommand(),
    BR: () => blockRotateCommand(),
    BD: () => blockDeleteCommand(),
    BS: () => blockDeleteOutsideCommand(),
    BY: () => blockCopyClipboardCommand(),
    BP: () => blockPasteClipboardCommand(),
    BL: () => blockLayersCommand(),
    BN: () => blockNestCommand(),
    // Isolation milling: cut the board rather than etch it.
    BT: () => { void trackIsolationCommand(); },
    // Marking the region the rest of the menu acts on.
    BG: () => blockRectangleCommand(),
    BI: () => blockPolylineCommand(),
    BE: () => blockReleaseCommand(),
    BO: () => { void isolationOutputCommand(); },
    BQ: () => { void isolationSequenceCommand(); },

    // --- Setup
    SL: () => openLayersCommand(),
    SO: () => showOptionsDialog({
      prefs,
      onChange(next) {
        prefs = next;
        savePrefs(prefs);
        // Both display switches change what is on screen this instant, so the
        // page is judged on the drawing rather than on its own wording.
        draw();
      },
    }),
    SF: () => showLibraryManager({
      parts,
      signedIn: user !== null,
      onPlace: (part) => { void beginPlace(part); },
    }),
    SM: () => drawModesCommand(),
    SP: () => zeroPointCommand(),
    SW: () => mirrorViewCommand(),
    SK: () => macrosCommand(),
    SR: () => recordMacroCommand(true),
    SB: () => void playMacroCommand(true),
    SU: () => showPreferencesDialog({
      prefs,
      onChange(next) {
        prefs = next;
        applyTheme(next.theme);
        applyFileMenuLayout(); // the Open/Import labels follow the setting
        // Picking scope may have changed, and with it what may still be held.
        applyPickScope();
        pruneSelection();
        updateInfo();
        updateStatus();
        draw();
        setMessage('Preferences saved');
      },
    }),
    ST: () => cycleOrtho(),
    SN: () => setSnap(!state.snap),
    SG: () => cycleGrid(),

    // --- Nets
    NI: () => { void openNetlistCommand(); },
    NS: () => netScanCommand(),
    NO: () => exportNetlistCommand(),
    NL: () => ratLineCommand(),
    NH: () => highlightNetCommand(),
    NC: () => clearHighlightsCommand(),
    NN: () => nameNetCommand(),
    'N+': () => stepScan(1),
    'N-': () => stepScan(-1),
    NG: () => netScanLog(),
    ND: () => dfmCommand(),
    NP: () => { void densityCommand(); },
    NB: () => { void autoPlaceCommand(); },
    NJ: () => { void routeOneCommand(); },
    NA: () => { void autoRouteCommand(); },
    NE: () => { void pourPlanesCommand(); },
    NF: () => footprintCheckCommand(),
    NW: () => { void tidyCopperCommand(); },
    NR: () => showRatsnest(false),
    NT: () => showRatsnest(true),
    // "Clear and remove all lines and arcs from the Ratnest layer" — so it has
    // to take away the ones N L and P - drew as well as turning the live
    // overlay off, or a drawn set would survive the command meant to clear it.
    NU: () => {
      state.showRatsnest = false;
      state.ratsnestSignalsOnly = false;
      el<HTMLInputElement>('opt-rats').checked = false;
      const removed = clearRatnestLayer();
      updateRatsnestStatus();
      draw();
      setMessage(removed > 0
        ? `Ratsnest off — and ${removed} drawn ratline${removed === 1 ? '' : 's'} removed`
        : 'Ratsnest off');
    },

    // --- Zoom
    // Zoom factors are the manual's: In ×1.414 (halves the area), Out ×0.707.
    ZX: () => { viewport.rememberView(); zoomFit(); },
    ZI: () => { viewport.rememberView(); viewport.zoomAt(viewport.width / 2, viewport.height / 2, 1.414); updateStatus(); draw(); },
    ZO: () => { viewport.rememberView(); viewport.zoomAt(viewport.width / 2, viewport.height / 2, 0.707); updateStatus(); draw(); },
    ZR: () => draw(),
    ZL: () => showLayerPicker(el('st-layer'), pickerContext(), state.activeLayer),
    ZW: () => beginZoomWindow(),
    ZC: () => beginZoomCenter(),
    ZB: () => zoomBoard(),
    ZS: () => zoomScaleCommand(),
    ZA: () => zoomImageActualSize(),
    VB: () => void boardViewCommand('2d'),
    VR: () => void boardViewCommand('3d'),
    VP: () => setShowPins(!state.showPins),
    // Photo under the drawing. One command per operation — two keypresses
    // beats a dialog when you do the same few things repeatedly.
    VI: () => photoCommand(),
    VL: () => photoLoadCommand(),
    VM: () => photoMoveCommand(),
    VS: () => photoScaleCommand(),
    VC: () => photoCornerCommand(),
    VN: () => photoNudgeCommand(),
    VT: () => photoTurnCommand(),
    VF: () => photoFadeCommand(),
    VD: () => photoDropCommand(),

    // --- Help
    '?A': () => showAboutDialog({ version: __APP_VERSION__ }),
    '?H': () => showHelp({ implemented: (c) => menus.implemented(c) }),
    '?R': () => supportCommand(),
    '?B': () => reportsCommand(),
    '?I': () => install.offer(),
  });

  // The greeting used to be a count of commands wired up, which was worth
  // saying while the menus were half-built and is a strange thing to open with
  // now they are not. What a new user needs is the way in.
  setMessage('Press a menu letter, then a command letter · ? H for help');
}

// --- Component picker -------------------------------------------------------

function openPartPicker(): void {
  const picker = el('part-picker');
  picker.hidden = false;
  const search = el<HTMLInputElement>('part-search');
  search.focus();
  search.select();
  setMessage('Place component — pick a part, then click to place');
}

function closePartPicker(): void {
  el('part-picker').hidden = true;
  hidePartPreview();
}

/** True for an LCSC part number, e.g. C25804. */
function isLcscId(s: string): boolean {
  return /^C\d{1,15}$/i.test(s.trim());
}

/**
 * Import a part from LCSC/EasyEDA: fetch through the server proxy, convert its
 * symbol and footprint, and save both to the personal library. The new parts
 * then appear in search and resolve in netlist-in like any other.
 */
async function importLcsc(id: string): Promise<void> {
  const part = id.trim().toUpperCase();
  if (!user) { setMessage('Sign in to import parts from LCSC'); return; }
  setMessage(`Fetching ${part} from LCSC…`);
  try {
    const resp = await parts.lcsc(part);
    if (!resp.success || !resp.result) {
      const box = el('part-list');
      box.replaceChildren();
      note(box, `Could not fetch ${part} — no component data at any known endpoint.`);
      if (resp.tried?.length) {
        note(box, 'Endpoints tried: ' + resp.tried.map((t) => `${t.url} → ${t.status}`).join('  ·  '));
      }
      setMessage(`Could not fetch ${part} from LCSC`);
      return;
    }
    const conv = convertEasyEda(resp);
    const items: SavedPart[] = [];
    if (conv.symbol) {
      items.push({
        kind: 'sch', name: conv.name, description: conv.description,
        category: 'LCSC', footprint: conv.footprintName, sections: 1,
        data: { component: conv.symbol },
      });
    }
    if (conv.footprint) {
      items.push({
        kind: 'pcb', name: conv.footprintName, description: conv.description,
        category: 'LCSC', footprint: conv.footprintName, sections: 1,
        data: { component: conv.footprint },
      });
      footprints.harvest({ kind: 'pcb', entities: [conv.footprint] } as CadDocument);
    }
    if (items.length === 0) { setMessage(`${part}: nothing to import`); return; }

    const r = await parts.savePart(items);
    await populateLibrarySelect();
    el<HTMLInputElement>('part-search').value = conv.name;
    void refreshPartSearch(conv.name);
    setMessage(`Imported ${part} — ${conv.name} (${r.added + r.updated} part`
      + `${r.added + r.updated === 1 ? '' : 's'}) into your library`);
  } catch (e) {
    setMessage(`LCSC import failed: ${(e as Error).message}`);
  }
}

function partPickerOpen(): boolean {
  return !el('part-picker').hidden;
}

// --- Editing ----------------------------------------------------------------

/**
 * Open the edit dialog for the entity at a point.
 *
 * Returns true when something was opened, so the caller can fall through to
 * the tool's own double-click when nothing was hit.
 */
function editItemAt(x: number, y: number, at?: { x: number; y: number }): boolean {
  const hit = hitTest(doc(), x, y, doc().grid.current || 0.01);
  if (!hit) return false;
  openEdit(hit, at);
  return true;
}

/** Open the edit dialog for a specific entity, snapshotting for undo. */
function openEdit(entity: Entity, at?: { x: number; y: number }): void {
  state.selected.clear();
  state.selected.add(entity.id);
  updateInfo();
  draw();

  // What the part said it becomes, before the dialog touched it. Compared
  // rather than watched, so that opening the dialog and closing it can never
  // re-fit anything: only a name that actually changed does that.
  const wasFootprint = entity.kind === 'component' ? entity.footprint ?? '' : '';

  // Snapshot before the dialog mutates the entity live, so Apply lands one
  // undo step and closing without change leaves the stack alone.
  history().begin(doc(), `Edit ${entity.kind}`);
  showEditDialog(entity, {
    doc: doc(),
    units: state.units,
    anchor: at,
    pickFootprint: pickFootprintFor,
    onApply(label) {
      void finishEdit(entity, wasFootprint, label);
    },
    onCancel() {
      // Nothing changed — drop the snapshot so undo is not padded with a no-op.
      history().abandon();
    },
  });
}

/**
 * Commit an edit, fitting a new pattern first where one was named.
 *
 * On a schematic a footprint name is a label and writing it is the whole of
 * the change. On a board it is not a label — the pads *are* the part — so a
 * board part told to be an SOIC has to become one, and until now it did not:
 * the field was written, nothing on the board moved, and the only sign was
 * that the next Net List In placed nothing new.
 *
 * The re-fit joins the same undo step the dialog opened, so one Undo puts back
 * the package that was there.
 */
async function finishEdit(entity: Entity, wasFootprint: string, label: string): Promise<void> {
  let said = label;

  if (doc().kind === 'pcb' && entity.kind === 'component'
    && (entity.footprint ?? '') !== wasFootprint) {
    const ref = entity.params[1] ?? entity.name;
    const wanted = entity.footprint?.trim() ?? '';
    const fp = wanted ? await resolveFootprint(wanted) : null;
    if (wanted && !fp) {
      said = `No pattern called ${wanted} in the libraries — ${ref} left as ${entity.name}`;
    } else if (fp && !samePattern(fp.name, entity.name)) {
      const next = refitComponent(doc(), entity, fp, wanted);
      if (next) {
        // The old part is gone from the document; grabbing must follow it.
        state.selected.clear();
        state.selected.add(next.id);
        // Said plainly, because it is the one part of this that is not
        // reversible by eye: the pads have moved and the copper has not.
        said = `${ref} re-fitted as ${next.name} — its pads have moved, check its tracks`;
      }
    }
  }

  history().commit();
  documentChanged();
  setMessage(said);
  buildLayerPanel();
  updateInfo();
  updateStatus();
  draw();
}

/**
 * Enter the grab tool restricted to one kind of item.
 *
 * The restriction is a filter on picking rather than a click mode, so a
 * restricted grab still behaves like a grab — drag to move, shift to add,
 * R/X/Y/K to transform — it simply cannot pick up the wrong sort of thing.
 */
function grabOnly(
  match: (e: Entity) => boolean, label: string, chain = false, atCursor = true,
): void {
  useTool('select', label); // clears any previous filter…
  grabFilter = match;       // …then this one applies
  grabWholeLine = chain;
  state.selected.clear();
  updateInfo();
  updateStatus();
  draw();
  if (atCursor) grabAtCursor();
}

/**
 * A Grab chosen from the keyboard takes hold of what is under the cursor.
 *
 * Naming a Grab is a decision about the thing you are pointing at — there is no
 * second gesture coming, and asking for a click would make the command do
 * nothing at the moment it was asked for. It works from the crosshair and from
 * the mouse pointer alike, since both keep a position.
 *
 * Not called when a menu's *default* fires: pressing `G` opens the Grab menu
 * and runs Grab Item to arm it, and that must not pick anything up before the
 * user has said what they wanted.
 */
function grabAtCursor(): void {
  if (!cursorWorld) return;
  toolbox.pointerDown(cursorWorld);
  updateInfo();
  updateStatus();
  draw();
}

/**
 * Run the click mode just entered on whatever is under the cursor.
 *
 * A Delete command typed at the keyboard is the same kind of statement a typed
 * Grab is: the cursor is already on the thing, and there is no second gesture
 * coming. `D M` means delete the multi-line I am pointing at, not "now go and
 * click one". The mode stays on afterwards, as the original's does, so a run of
 * deletions is still a run.
 */
function runClickModeAtCursor(): void {
  if (!cursorWorld || !clickMode) return;
  clickMode.run(cursorWorld);
}

/**
 * Build Component — make the grabbed geometry into a part and library it.
 *
 * The geometry is re-based on the selection's centre so the part has a sensible
 * insertion point, grouped into a component, and saved to the personal library.
 * Saving under an existing name replaces it, so this is also how a part is
 * edited: place it, change it, build it again under the same name.
 */
function buildComponentCommand(): void {
  if (state.selected.size === 0) {
    setMessage('Grab the geometry to build into a part first');
    return;
  }
  const d = doc();
  const picked = d.entities.filter((e) => state.selected.has(e.id));
  const b = selectionBounds();
  if (!b) { setMessage('Nothing with an extent to build'); return; }

  const g = d.grid.current || 0.05;
  const origin = { x: snap((b.x1 + b.x2) / 2, g), y: snap((b.y1 + b.y2) / 2, g) };

  showBuildPartDialog({
    kind: d.kind,
    count: picked.length,
    onConfirm(details) {
      // Children are origin-relative, as every component in memory is.
      const children = picked.map((e) => instantiate(e));
      for (const c of children) translateEntity(c, -origin.x, -origin.y);

      const comp: ComponentEntity = {
        kind: 'component',
        id: newId(),
        layer: state.activeLayer,
        name: details.name,
        bounds: {
          x1: b.x1 - origin.x, y1: b.y1 - origin.y,
          x2: b.x2 - origin.x, y2: b.y2 - origin.y,
        },
        x: origin.x,
        y: origin.y,
        params: details.refPrefix ? { 1: details.refPrefix } : {},
        ...(details.footprint ? { footprint: details.footprint } : {}),
        children,
      };

      if (details.replaceInPlace) {
        history().begin(d, 'Build Component');
        d.entities = d.entities.filter((e) => !state.selected.has(e.id));
        d.entities.push(comp);
        state.selected.clear();
        state.selected.add(comp.id);
        history().commit();
        documentChanged();
        updateInfo();
        draw();
      }

      // Library it, so it can be placed again. A component kept in the drawing
      // and one stored in the library must not share entity ids.
      void parts.savePart([{
        kind: d.kind,
        name: details.name,
        description: details.description,
        category: details.category,
        footprint: details.footprint || null,
        sections: 1,
        data: { component: instantiate(comp) },
      }]).then(async (res) => {
        footprints.harvest(d);
        // Refreshing the catalogue reports its own count, so the result has to
        // be said after it — otherwise the only feedback the command gives is
        // overwritten and it looks as though nothing happened.
        await populateLibrarySelect();
        setMessage(res.updated > 0
          ? `Replaced ${details.name} in the library`
          : `Built ${details.name} — ${picked.length} items, saved to your library`);
      }).catch(async (err: Error) => {
        await populateLibrarySelect();
        setMessage(user
          ? `Built ${details.name}, but saving to the library failed: ${err.message}`
          : `Built ${details.name} — sign in to keep it in your library`);
      });
    },
  });
}

// --- The Edit menu ----------------------------------------------------------

/**
 * Ctrl+C and Ctrl+X — hold the selection, and on a cut take it away.
 *
 * Into the same clipboard Block Copy fills, so the two are one thing. The
 * origin is the lower-left of what was taken, which is what makes a paste land
 * where you point rather than offset by wherever the geometry happened to sit.
 */
function copySelectionToClipboard(cut: boolean): void {
  const d = doc();
  const picked = d.entities.filter((e) => state.selected.has(e.id));
  if (picked.length === 0) { setMessage('Nothing grabbed to copy'); return; }

  const b = selectionBounds();
  const ox = b?.x1 ?? 0;
  const oy = b?.y1 ?? 0;
  const held = picked.map((e) => instantiate(e));
  for (const e of held) translateEntity(e, -ox, -oy);
  blockClipboard = { kind: state.mode, items: held, origin: { x: ox, y: oy } };

  if (cut) {
    history().begin(d, 'Cut');
    const gone = new Set(picked.map((e) => e.id));
    d.entities = d.entities.filter((e) => !gone.has(e.id));
    history().commit();
    state.selected.clear();
    documentChanged();
    updateInfo();
    draw();
  }
  setMessage(`${cut ? 'Cut' : 'Copied'} ${held.length} item${held.length === 1 ? '' : 's'}`);
}

/**
 * Ctrl+V — put the clipboard down at the cursor.
 *
 * At the cursor rather than asking for a point, which is the difference between
 * this and Block ▸ Paste: the key is the quick one and the command is the
 * deliberate one. The pasted geometry is left grabbed, so it can be nudged into
 * place immediately or dropped where it fell.
 */
function pasteClipboardAtCursor(): void {
  const clip = blockClipboard;
  if (!clip) { setMessage('Clipboard is empty'); return; }
  if (clip.kind !== state.mode) {
    setMessage(`Clipboard holds ${clip.kind === 'pcb' ? 'board' : 'schematic'} geometry`);
    return;
  }

  const d = doc();
  const at = cursorWorld ?? ensureCursor();
  const g = activeGrid();
  const x = snap(at.x, g);
  const y = snap(at.y, g);

  history().begin(d, 'Paste');
  const made = clip.items.map((e) => instantiate(e));
  ensureUniqueIds(made);
  for (const e of made) translateEntity(e, x, y);
  d.entities.push(...made);
  history().commit();
  documentChanged();
  invalidateSnap();
  state.selected.clear();
  for (const e of made) state.selected.add(e.id);
  updateInfo();
  draw();
  setMessage(`Pasted ${made.length} item${made.length === 1 ? '' : 's'} — still grabbed`);
}

/**
 * Edit ▸ Snap Item (E S) — put the cursor exactly on the nearest real point.
 *
 * `itemsnap` in the original (OGmenu00.cpp:606): it scans for the closest point
 * belonging to an object — a line end, an arc centre, a pad — and moves the
 * cursor there. Not a snap *while* drawing, which happens on its own within a
 * tolerance, but a deliberate one: land on that pad centre now, exactly,
 * whatever the grid says and however far away it is.
 *
 * Which is the answer to the awkward case the ordinary snap cannot cover. A
 * pin at ±.125" on a tenth-inch grid is off-grid by .025", and reaching it by
 * eye at low magnification means missing it — the drawing then says the wire
 * ends near the pad rather than on it, and the netlist is right to disagree.
 */
function snapItemCommand(): void {
  const at = cursorWorld ?? ensureCursor();
  const d = doc();
  const candidates = snapCandidateCache();
  let best: { x: number; y: number; kind: string; ref?: string; pin?: string } | null = null;
  let bestD = Infinity;
  for (const c of candidates) {
    const dist = (c.x - at.x) ** 2 + (c.y - at.y) ** 2;
    if (dist < bestD) { bestD = dist; best = c; }
  }
  if (!best) { setMessage('Snap Item — nothing on the drawing to snap to'); return; }

  cursorWorld = { x: best.x, y: best.y };
  // The tool in hand follows, so this works mid-run: bring a wire to the pad
  // and the corner is laid where the pad actually is.
  if (state.placing) { state.placing.x = best.x; state.placing.y = best.y; }
  else toolbox.pointerMove(cursorWorld);
  keepCursorInView();
  updateStatus(best.x, best.y);
  draw();

  const what = best.kind === 'pad'
    ? `pin ${[best.ref, best.pin].filter(Boolean).join('-') || 'pad'}`
    : 'corner';
  setMessage(`Snapped to the nearest ${what} at `
    + `${formatDisplay(best.x, state.units)}, ${formatDisplay(best.y, state.units)}`
    + ` — ${formatDisplay(Math.sqrt(bestD), state.units)} away`);
  void d;
}

/**
 * Edit ▸ Line to Arc (E t) — bow the selected straight lines.
 *
 * The original declares this command and never implements it: `Edit_LinetoArc`
 * appears once, in OG2.h, with no handler anywhere. So there is no behaviour to
 * be faithful to and the shape of it is ours.
 *
 * A bulge, which is what the format already carries on every line — the
 * included angle of the arc the segment becomes, positive bowing one way and
 * negative the other. Asked for in degrees because that is what the file stores
 * and what the item editor shows; zero puts the line back straight, which is
 * how the command undoes itself without needing a second one.
 */
function lineToArcCommand(): void {
  const d = doc();
  const lines = d.entities.filter(
    (e): e is LineEntity => e.kind === 'line' && state.selected.has(e.id));
  if (lines.length === 0) {
    setMessage('Line to Arc — grab one or more lines first');
    return;
  }

  const now = lines[0]!.bulge ?? 0;
  const raw = window.prompt(
    `Bow ${lines.length === 1 ? 'this line' : `these ${lines.length} lines`} by how many `
    + 'degrees? (negative bows the other way, 0 straightens)', String(now));
  if (raw === null) return;
  const deg = parseFloat(raw);
  if (!Number.isFinite(deg)) { setMessage('Line to Arc — that is not an angle'); return; }
  // Beyond a full turn the arc doubles back on itself, which is not a segment
  // any more and not something the writer can express.
  if (Math.abs(deg) >= 360) {
    setMessage('Line to Arc — the bow must be less than a full turn');
    return;
  }

  history().begin(d, 'Line to arc');
  for (const l of lines) {
    if (deg === 0) delete l.bulge;
    else l.bulge = deg;
  }
  history().commit();
  documentChanged();
  updateInfo();
  draw();
  setMessage(deg === 0
    ? `Straightened ${lines.length} line${lines.length === 1 ? '' : 's'}`
    : `Bowed ${lines.length} line${lines.length === 1 ? '' : 's'} by ${deg}°`);
}

/**
 * The text files open, for Align and Compare.
 *
 * The original takes the file in front and the next one open without asking.
 * Here the one in front is `activeText` and the rest are offered in tab order,
 * seeded with the next — so with two files open it is still one keypress.
 */
function compareTextCommand(mode: 'align' | 'compare'): void {
  const current = activeTextDoc();
  if (!current) {
    setMessage(`${mode === 'align' ? 'Align' : 'Compare'} works on text files — open one first`);
    return;
  }
  const live = (t: TextTab): string =>
    (t.id === activeText && textEditor ? textEditor.getText() : t.text);
  showCompareDialog({
    mode,
    left: { id: current.id, title: current.title, text: live(current) },
    others: textDocs
      .filter((t) => t.id !== current.id)
      .map((t) => ({ id: t.id, title: t.title, text: live(t) })),
  });
}

/** Find, and its four followers, driving the text editor's own bar. */
function findCommand(what: 'find' | 'next' | 'replace' | 'again' | 'confirm'): void {
  const ed = activeTextDoc() ? textEditor : null;
  if (!ed) {
    setMessage('Find works in a text file — open one first');
    return;
  }
  switch (what) {
    case 'find': ed.find(false); break;
    case 'next': ed.findNext(); break;
    case 'replace': ed.find(true); break;
    // Replace Next is one and move on; Replace Confirm is the whole file, which
    // is the original's own split between `Ed` and `Ec`.
    case 'again': ed.replaceNext(); break;
    case 'confirm': ed.replaceEvery(); break;
  }
}

/** Edit the current single selection, e.g. from a menu command. */
function editSelection(): void {
  if (state.selected.size !== 1) {
    setMessage(state.selected.size === 0 ? 'Nothing grabbed' : 'Grab one item to edit');
    return;
  }
  const id = [...state.selected][0]!;
  const e = doc().entities.find((x) => x.id === id);
  if (e) openEdit(e);
}

// --- Command implementations ------------------------------------------------

function deleteSelection(): void {
  if (state.selected.size === 0) { setMessage('Nothing grabbed'); return; }
  const n = state.selected.size;
  history().begin(doc(), 'Delete');
  doc().entities = doc().entities.filter((e) => !state.selected.has(e.id));
  state.selected.clear();
  history().commit();
  documentChanged();
  updateInfo();
  updateStatus();
  draw();
  setMessage(`Deleted ${n} item${n === 1 ? '' : 's'}`);
}

function transformSelection(what: 'rotate' | 'mirror'): void {
  if (state.selected.size === 0) { setMessage('Nothing grabbed'); return; }
  if (what === 'rotate') { rotateSelection(90, 'Rotate', 'Rotated 90°'); return; }
  history().begin(doc(), 'Mirror');
  for (const e of doc().entities) {
    if (!state.selected.has(e.id) || e.kind !== 'component') continue;
    e.mirrored = !e.mirrored;
  }
  history().commit();
  documentChanged();
  updateInfo();
  draw();
  setMessage('Mirrored');
}

/**
 * Turn the grabbed items about the centre of what is grabbed.
 *
 * This used to bump the rotation on components and nothing else, so rotating a
 * grabbed track, arc, pad or piece of text did nothing at all, and a mixed
 * selection came apart — half of it turned and half stayed put. It is the same
 * fault {@link mirrorSelection} carried, and the same answer: one centre, every
 * kind, real geometry.
 */
function rotateSelection(deg: number, undoLabel: string, done: string): void {
  const d = doc();
  const b = selectionBounds();
  if (!b) { setMessage('Nothing to rotate'); return; }
  const cx = (b.x1 + b.x2) / 2;
  const cy = (b.y1 + b.y2) / 2;
  history().begin(d, undoLabel);
  for (const e of d.entities) {
    if (state.selected.has(e.id)) rotateEntity(e, deg, cx, cy);
  }
  history().commit();
  // A carried block turns with its contents, about the same centre.
  const rad = (deg * Math.PI) / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  moveCarriedRegion((p) => ({
    x: cx + (p.x - cx) * cos - (p.y - cy) * sin,
    y: cy + (p.x - cx) * sin + (p.y - cy) * cos,
  }));
  documentChanged();
  updateInfo();
  draw();
  setMessage(done);
}

/**
 * Is there already a via or pad on this point?
 *
 * Both ways of dropping one need to know. Place Via mid-run is the original's
 * own route — trace, `P V`, change layer, carry on — and stepping the layer
 * drops one too, as a convenience; someone doing it the original's way would
 * otherwise get two vias stacked on the same hole.
 */
function viaAt(at: { x: number; y: number }): boolean {
  const d = doc();
  const near = Math.max(d.grid.current * 0.5, 1e-6);
  return d.entities.some((e) => e.kind === 'pad'
    && Math.abs(e.x - at.x) <= near && Math.abs(e.y - at.y) <= near);
}

/**
 * Put a via down at a point, on the pad master.
 *
 * Layer zero because a through via reaches every copper layer and the Pad
 * Master is where the stock stack keeps what does — `padThrough` decides that
 * from the pad's *type*, so the layer is what it is drawn in rather than what
 * it connects to, and drawing it as top copper would be a lie about a hole that
 * goes all the way through.
 */
function dropVia(at: { x: number; y: number }, label: string): void {
  const d = doc();
  const def = d.defaults.via;
  const g = activeGrid();
  history().begin(d, label);
  d.entities.push({
    kind: 'pad', id: newId(), layer: 0, type: def?.type ?? 'T3',
    x: snap(at.x, g), y: snap(at.y, g),
    sizeX: def?.size ?? 0.05, sizeY: def?.size ?? 0.05, hole: def?.hole ?? 0.028,
  });
  history().commit();
  documentChanged();
  invalidateSnap();
}

/**
 * Place ▸ Via (P V) — and mid-route, the layer change itself.
 *
 * This is how the original crosses layers, and it is one command doing three
 * things: `placevia` commits the run so far up to the cursor, drops the via
 * there, and leaves the ghost line running on from it (OGmenu02.cpp:2356). You
 * then press `+` or `-` and carry on, and only the copper after the via is on
 * the new layer.
 *
 * It used to switch to the via tool, which abandoned the run — so the one
 * command whose whole purpose is to *continue* a route threw it away, and
 * changing layer was left with nothing to do but take the finished run with it.
 *
 * With nothing being drawn it is the ordinary command: the parameter dialog,
 * then the tool, for dropping vias on their own.
 */
function placeViaCommand(): void {
  if (state.mode !== 'pcb') { setMessage('A via belongs on a board'); return; }

  if (toolbox.routing) {
    const at = cursorWorld ?? ensureCursor();
    // The corner first, so the run ends *on* the via rather than crossing it.
    toolbox.viaCorner(at);
    if (!viaAt(at)) dropVia(at, 'Place via');
    draw();
    setMessage('Via — change layer with + or -, and the run carries on across it');
    return;
  }

  viaParamsCommand(() => useTool('via', 'Place via'));
}

/**
 * Step the active layer up (+1) or down (-1) through the visible layers, as
 * the numpad +/- keys do in the original.
 *
 * When the track tool is mid-run and the step moves between two copper layers,
 * a via is dropped at the current corner and drawing continues on the new
 * layer — the original's Automatic Via Placement.
 */
function stepLayer(dir: 1 | -1): void {
  const d = doc();
  const n = d.layers.length;
  if (n === 0) return;

  const from = state.activeLayer;
  let to = from;
  for (let i = 0; i < n; i++) {
    to = (to + dir + n) % n;
    if (d.layers[to]?.visible) break;
  }
  if (to === from) return;

  // Automatic via: on a board, between two copper layers, and only with a run
  // actually in progress — the tool merely being armed is not enough, or a
  // stray via lands every time the layer is stepped before anything is drawn.
  //
  // This is ours rather than the original's. There the two layer keys only step
  // the displayed layer (OGxyz02.cpp:964) and the via comes from Place Via, which
  // commits the run so far, drops the via and carries on. Doing it on the layer
  // key means the whole change of layers is one keypress, which is what anyone
  // routing expects — but it has to do the same three things in the same order.
  const fromCu = d.layers[from]?.type === LayerType.Copper;
  const toCu = d.layers[to]?.type === LayerType.Copper;
  const routing = toolbox.routing;
  if (d.kind === 'pcb' && fromCu && toCu && routing && cursorWorld) {
    // A corner first, on the layer being left. Without one the via sits in the
    // middle of a segment with no vertex under it: the copper crosses it
    // instead of ending on it, and the whole span belongs to one layer.
    toolbox.viaCorner(cursorWorld);

    // Not a second one where Place Via has already put a via: the original's
    // own way round is P V and then the layer key, and that must not leave two
    // holes in the same place.
    if (!viaAt(cursorWorld)) dropVia(cursorWorld, 'Via');
    setMessage(`Via — carrying on across ${d.layers[to]?.name}`);
  } else {
    setMessage(`Layer: ${d.layers[to]?.name}`);
  }

  state.activeLayer = to;
  buildLayerPanel();
  updateStatus();
  draw();
}

/**
 * A single-key transform on the grabbed selection. Returns true if handled.
 * Mirrors the manual's Ch.13 shortcut keys.
 */
function grabShortcut(key: string): boolean {
  switch (key) {
    case 'r': case 'R': transformSelection('rotate'); return true;
    case 'x': case 'X': mirrorSelection('x'); return true;
    case 'y': case 'Y': mirrorSelection('y'); return true;
    case 'm': case 'M': transformSelection('mirror'); return true;
    case 'a': case 'A': rotateSelectionBy(); return true;
    case 'k': case 'K': scaleSelection(); return true;
    default: return false;
  }
}

/**
 * Mirror the grabbed items about the centre of what is selected (manual ch.13:
 * "The <X> and <Y> keys will mirror a grabbed item in the x and y directions").
 *
 * Every kind is reflected properly, not just components. Toggling a component's
 * mirror flag while leaving the lines, arcs, pads and text around it untouched —
 * which is what this used to do — tore a mixed selection apart, since half of it
 * moved and half of it did not.
 */
function mirrorSelection(axis: 'x' | 'y'): void {
  if (state.selected.size === 0) { setMessage('Nothing grabbed'); return; }
  const d = doc();
  const b = selectionBounds();
  if (!b) { setMessage('Nothing to mirror'); return; }
  const cx = (b.x1 + b.x2) / 2;
  const cy = (b.y1 + b.y2) / 2;

  history().begin(d, 'Mirror');
  for (const e of d.entities) {
    if (state.selected.has(e.id)) mirrorEntity(e, axis, cx, cy);
  }
  history().commit();
  moveCarriedRegion((p) => (axis === 'x'
    ? { x: 2 * cx - p.x, y: p.y }
    : { x: p.x, y: 2 * cy - p.y }));
  documentChanged();
  updateInfo();
  draw();
  setMessage(`Mirrored in ${axis}`);
}

/** World-space extent of the selection, including components and fills. */
function selectionBounds(): { x1: number; y1: number; x2: number; y2: number } | null {
  let x1 = Infinity; let y1 = Infinity; let x2 = -Infinity; let y2 = -Infinity;
  const add = (ax1: number, ay1: number, ax2: number, ay2: number): void => {
    x1 = Math.min(x1, ax1, ax2); x2 = Math.max(x2, ax1, ax2);
    y1 = Math.min(y1, ay1, ay2); y2 = Math.max(y2, ay1, ay2);
  };
  for (const e of doc().entities) {
    if (!state.selected.has(e.id)) continue;
    const b = entityExtent(e);
    if (b) { add(b.x1, b.y1, b.x2, b.y2); continue; }
    // The kinds entityExtent does not cover: a component's bounds are relative
    // to its insertion point, a fill's are already in world space.
    if (e.kind === 'component') {
      add(e.x + e.bounds.x1, e.y + e.bounds.y1, e.x + e.bounds.x2, e.y + e.bounds.y2);
    } else if (e.kind === 'fill') {
      add(e.bounds.x1, e.bounds.y1, e.bounds.x2, e.bounds.y2);
    } else if (e.kind === 'spiral') {
      const r = Math.max(e.r1, e.r2);
      add(e.cx - r, e.cy - r, e.cx + r, e.cy + r);
    }
  }
  return Number.isFinite(x1) ? { x1, y1, x2, y2 } : null;
}

const normDeg = (a: number): number => ((a % 360) + 360) % 360;

/** Reflect one entity about the line x=cx (axis 'x') or y=cy (axis 'y'). */
function mirrorEntity(e: Entity, axis: 'x' | 'y', cx: number, cy: number): void {
  const horiz = axis === 'x';
  const fx = (x: number): number => 2 * cx - x;
  const fy = (y: number): number => 2 * cy - y;

  switch (e.kind) {
    case 'line':
      if (horiz) { e.x1 = fx(e.x1); e.x2 = fx(e.x2); } else { e.y1 = fy(e.y1); e.y2 = fy(e.y2); }
      // A reflection reverses which way a bulged segment bows.
      if (e.bulge) e.bulge = -e.bulge;
      break;
    case 'arc':
      if (horiz) { e.cx = fx(e.cx); e.angle = normDeg(180 - e.angle); }
      else { e.cy = fy(e.cy); e.angle = normDeg(-e.angle); }
      e.sweep = -e.sweep;
      break;
    case 'spiral':
      if (horiz) { e.cx = fx(e.cx); e.a1 = normDeg(180 - e.a1); e.a2 = normDeg(180 - e.a2); }
      else { e.cy = fy(e.cy); e.a1 = normDeg(-e.a1); e.a2 = normDeg(-e.a2); }
      break;
    case 'pad':
    case 'text': {
      if (horiz) e.x = fx(e.x); else e.y = fy(e.y);
      const rot = e.rotation ?? 0;
      if (rot) e.rotation = normDeg(horiz ? 180 - rot : -rot);
      if (e.kind === 'text' && e.anchor) e.anchor = flipAnchor(e.anchor, horiz);
      break;
    }
    case 'fill':
      if (horiz) {
        const nx1 = fx(e.bounds.x2); const nx2 = fx(e.bounds.x1);
        e.bounds.x1 = nx1; e.bounds.x2 = nx2;
        for (const v of e.vertices) v.x = fx(v.x);
      } else {
        const ny1 = fy(e.bounds.y2); const ny2 = fy(e.bounds.y1);
        e.bounds.y1 = ny1; e.bounds.y2 = ny2;
        for (const v of e.vertices) v.y = fy(v.y);
      }
      break;
    case 'component': {
      // A component is drawn as translate ∘ rotate ∘ mirror-x, so reflecting it
      // in the world flips the mirror flag and negates the rotation. Mirroring
      // vertically is the same reflection turned through 180°.
      if (horiz) e.x = fx(e.x); else e.y = fy(e.y);
      const rot = e.rotation ?? 0;
      e.rotation = normDeg(horiz ? -rot : 180 - rot);
      e.mirrored = !e.mirrored;
      break;
    }
  }
}

/** Flip a text anchor across the mirrored axis, so it keeps the same side. */
function flipAnchor(a: TextAnchor, horiz: boolean): TextAnchor {
  const v = a.charAt(0);
  const h = a.charAt(1);
  if (horiz) return `${v}${h === 'L' ? 'R' : h === 'R' ? 'L' : h}` as TextAnchor;
  return `${v === 'B' ? 'T' : v === 'T' ? 'B' : v}${h}` as TextAnchor;
}

function rotateSelectionBy(): void {
  const raw = window.prompt('Rotate by how many degrees (counter-clockwise)?', '45');
  if (raw === null) return;
  const deg = parseFloat(raw);
  if (!Number.isFinite(deg)) return;
  if (state.selected.size === 0) { setMessage('Nothing grabbed'); return; }
  rotateSelection(deg, 'Rotate n°', `Rotated ${deg}°`);
}

/**
 * Scale the grabbed items about the centre of the selection (manual ch.13: "The
 * <K> key will scale any grabbed item").
 *
 * This used to resize pads, pen widths and text heights only — it never moved
 * anything and had no case for components at all, so scaling a part put up its
 * dialog and then did nothing whatsoever. A scale has to take the geometry with
 * it, including a component's own children, or the part keeps its old size.
 */
function scaleSelection(): void {
  if (state.selected.size === 0) { setMessage('Nothing grabbed'); return; }
  const raw = window.prompt('Scale factor?', '2');
  if (raw === null) return;
  const f = parseFloat(raw);
  if (!Number.isFinite(f) || f <= 0) { setMessage('Scale must be a positive number'); return; }
  const b = selectionBounds();
  if (!b) { setMessage('Nothing to scale'); return; }
  const cx = (b.x1 + b.x2) / 2;
  const cy = (b.y1 + b.y2) / 2;

  const d = doc();
  history().begin(d, 'Scale');
  for (const e of d.entities) {
    if (state.selected.has(e.id)) scaleEntity(e, f, cx, cy);
  }
  history().commit();
  moveCarriedRegion((p) => ({ x: cx + (p.x - cx) * f, y: cy + (p.y - cy) * f }));
  documentChanged();
  updateInfo();
  updateStatus();
  draw();
  setMessage(`Scaled ×${f}`);
}

/**
 * Scale one entity by `f` about (cx, cy). Sizes and positions both scale, so a
 * selection keeps its shape and spacing rather than growing about its own parts.
 */
function scaleEntity(e: Entity, f: number, cx: number, cy: number): void {
  const sx = (x: number): number => cx + (x - cx) * f;
  const sy = (y: number): number => cy + (y - cy) * f;

  switch (e.kind) {
    case 'line':
      e.x1 = sx(e.x1); e.y1 = sy(e.y1); e.x2 = sx(e.x2); e.y2 = sy(e.y2);
      e.width *= f;
      break;
    case 'arc':
      e.cx = sx(e.cx); e.cy = sy(e.cy); e.radius *= f; e.width *= f;
      break;
    case 'spiral':
      e.cx = sx(e.cx); e.cy = sy(e.cy); e.r1 *= f; e.r2 *= f; e.width *= f;
      break;
    case 'pad':
      e.x = sx(e.x); e.y = sy(e.y);
      e.sizeX *= f; e.sizeY *= f;
      if (e.hole) e.hole *= f;
      break;
    case 'text':
      e.x = sx(e.x); e.y = sy(e.y);
      e.height *= f;
      if (e.length !== undefined) e.length *= f;
      break;
    case 'fill':
      e.bounds.x1 = sx(e.bounds.x1); e.bounds.x2 = sx(e.bounds.x2);
      e.bounds.y1 = sy(e.bounds.y1); e.bounds.y2 = sy(e.bounds.y2);
      for (const v of e.vertices) { v.x = sx(v.x); v.y = sy(v.y); }
      break;
    case 'component': {
      // The insertion point moves with the selection; the children scale about
      // the component's own origin, which is what makes the part itself bigger.
      e.x = sx(e.x); e.y = sy(e.y);
      e.bounds.x1 *= f; e.bounds.x2 *= f;
      e.bounds.y1 *= f; e.bounds.y2 *= f;
      for (const c of e.children) scaleEntity(c, f, 0, 0);
      break;
    }
  }
}

function undoRedo(redo: boolean): void {
  const label = redo ? history().redo(doc()) : history().undo(doc());
  if (!label) {
    // The stack in this tab is spent. There may still be history on the server:
    // every save keeps the state it replaced, so the trail carries on past the
    // session that made it.
    void walkRevisions(redo);
    return;
  }
  // Stepping back into this session's own history leaves the saved trail: the
  // two are separate stacks and the deeper one is only entered when this is
  // empty.
  revisionWalk = null;
  setMessage(`${redo ? 'Redid' : 'Undid'} ${label}`);
  documentChanged();
  state.selected.clear();
  updateInfo();
  updateStatus();
  draw();
}

/**
 * Un-Do ▸ All (U A) and Re-Do ▸ All (U E).
 *
 * Everything on the stack at once. Deliberately not a jump to the oldest state:
 * each step is taken in turn so it lands in the other stack, which is what
 * makes the two the inverse of each other rather than a way out with no way
 * back.
 *
 * Stops at this session's stack rather than walking on into the saved
 * revisions. "Undo everything" meaning "and then keep going through every save
 * this design has ever had" is not what anybody presses it for.
 */
function undoRedoAll(redo: boolean): void {
  const n = redo ? history().redoAll(doc()) : history().undoAll(doc());
  if (n === 0) {
    setMessage(redo ? 'Nothing to redo' : 'Nothing to undo');
    return;
  }
  revisionWalk = null;
  setMessage(`${redo ? 'Redid' : 'Undid'} ${n} step${n === 1 ? '' : 's'}`);
  documentChanged();
  state.selected.clear();
  updateInfo();
  updateStatus();
  draw();
}

/**
 * Un-Do ▸ Purge Re-Do (U P) and Purge Un-Do (U S).
 *
 * Both happen on their own — the redo stack the moment you draw after undoing,
 * the undo stack when a file is written — and both are offered as commands
 * because doing it deliberately is how you say a branch is not coming back
 * before carrying on over the top of it.
 *
 * Purging the undo stack does not touch the saved revisions. Those belong to
 * the account rather than to this window, and a command about *this session's*
 * stack has no business reaching into them.
 */
function purgeHistory(which: 'undo' | 'redo'): void {
  const n = which === 'redo' ? history().purgeRedo() : history().purgeUndo();
  updateStatus();
  setMessage(n === 0
    ? `The ${which === 'redo' ? 'Re-Do' : 'Un-Do'} stack is already empty`
    : `Purged ${n} ${which === 'redo' ? 'Re-Do' : 'Un-Do'} step${n === 1 ? '' : 's'}`);
}

// --- Undo that outlives the session -----------------------------------------

/**
 * Where the walk back through saved states has got to.
 *
 * The undo stack lives in the tab and dies with it, which used to mean that
 * closing a drawing drew a line under everything done to it. It need not: the
 * server keeps the state before each save, fifty deep, so the work of a
 * previous session is still there to be stepped back through.
 *
 * `at` counts saves back from the live document — 0 is where you are, 1 is what
 * it looked like before the last save. `live` is that document, held so redo
 * can come all the way forward again rather than only as far as the newest
 * revision.
 */
let revisionWalk: {
  docId: number;
  list: RevisionSummary[];
  at: number;
  live: CadDocument;
} | null = null;

/** The server's id for the drawing on screen, or undefined if it has none. */
function currentDocId(): number | undefined {
  return state.mode === 'sch' ? sheets[sheetIndex]?.id : documentIds.pcb;
}

/**
 * Step back or forward through the saved states.
 *
 * Applying one is a load, not an edit: it does not mark the drawing dirty and
 * so does not start an autosave. Nothing is committed by looking. The moment
 * the operator does anything of their own the walk is abandoned and that edit
 * saves from wherever they had got back to — which is what makes this undo
 * rather than a viewer.
 */
async function walkRevisions(forward: boolean): Promise<void> {
  const docId = currentDocId();
  if (!user || docId === undefined) {
    setMessage(`Nothing to ${forward ? 'redo' : 'undo'}`);
    return;
  }

  try {
    if (!revisionWalk || revisionWalk.docId !== docId) {
      if (forward) { setMessage('Nothing to redo'); return; }
      const list = await Projects.revisions(docId);
      if (list.length === 0) { setMessage('Nothing to undo'); return; }
      revisionWalk = { docId, list, at: 0, live: structuredClone(doc()) };
    }

    const walk = revisionWalk;
    const want = walk.at + (forward ? -1 : 1);
    if (want < 0) { setMessage('Nothing to redo'); return; }
    if (want > walk.list.length) {
      setMessage(`That is as far back as the saved history goes (${walk.list.length} saves)`);
      return;
    }

    // 0 is the live document, which is held here rather than fetched: it is the
    // one state the server's revision list does not contain.
    const entry = want === 0 ? null : walk.list[want - 1]!;
    const restored = entry === null
      ? structuredClone(walk.live)
      : (await Projects.revision(entry.id)).document as CadDocument;
    if (restored.kind !== state.mode) {
      setMessage('That saved state is a different kind of drawing');
      return;
    }

    // A saved state is by definition an older document, so this is the likeliest
    // place of all to meet one written against an older model — and it was the
    // one load path that neither migrated nor even seeded the id counter, so
    // the next object drawn after stepping back could take an id the restored
    // document was already using.
    try {
      migrateDocument(restored);
    } catch (err) {
      if (!(err instanceof FutureVersionError)) throw err;
      setMessage(err.message);
      return;
    }

    walk.at = want;
    state.docs[state.mode] = restored;
    if (state.mode === 'sch' && sheets[sheetIndex]) sheets[sheetIndex]!.doc = restored;

    // Everything a load refreshes, and nothing a change would: no markDirty, so
    // no autosave writes a revision for merely having looked at one.
    state.selected.clear();
    invalidateSnap();
    renderDocTabs();
    buildLayerPanel();
    updateInfo();
    updateStatus();
    draw();

    setMessage(want === 0
      ? 'Back to the latest saved state'
      : `${want} save${want === 1 ? '' : 's'} back${entry ? ` — ${when(entry.created_at)}` : ''}`
        + ' · Ctrl+Y comes forward, and your next edit carries on from here');
  } catch (err) {
    setMessage(`Could not reach the saved history: ${(err as Error).message}`);
  }
}

/** A saved-at stamp, as a person would say it. */
function when(stamp: string): string {
  // The server sends MySQL's `YYYY-MM-DD HH:MM:SS`, which Safari will not parse
  // without the T.
  const t = new Date(stamp.replace(' ', 'T'));
  if (Number.isNaN(t.getTime())) return stamp;
  const today = new Date().toDateString() === t.toDateString();
  return today
    ? t.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })
    : t.toLocaleString(undefined, {
      month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
    });
}

/** Step through the document's own grid table, as Setup > Grid Snaps does. */
function cycleGrid(): void {
  const d = doc();
  const table = d.grid.table.length > 0 ? d.grid.table : [0.001, 0.005, 0.025, 0.05, 0.1];
  const i = table.findIndex((g) => Math.abs(g - d.grid.current) < 1e-9);
  d.grid.current = table[(i + 1) % table.length] ?? table[0]!;
  d.grid.index = (i + 1) % table.length;
  updateStatus();
  draw();
  setMessage(`Grid ${formatDisplay(d.grid.current, state.units)}`);
}

/**
 * Step the default line width to the next one the drawing already uses.
 *
 * The list is the widths on the board rather than a fixed table, for the same
 * reason the size picker uses it: a design's own widths are the ones you want
 * to return to, and they describe themselves. A drawing with nothing in it yet
 * falls back to a plain run of common track widths.
 */
function cycleWidth(): void {
  const d = doc();
  const used = usedWidths(d, ['line', 'fill']);
  const table = used.length > 1 ? used : [0.008, 0.01, 0.012, 0.016, 0.025, 0.05];
  const current = d.defaults.line?.width ?? table[0]!;
  const i = table.findIndex((w) => Math.abs(w - current) < 1e-9);
  const next = table[(i + 1) % table.length] ?? table[0]!;
  d.defaults.line = { ...(d.defaults.line ?? { style: 0 }), width: next };
  updateStatus();
  draw();
  setMessage(`Width ${formatDisplay(next, state.units)}`);
}

function openLayersCommand(): void {
  showLayerDialog({
    doc: doc(),
    onChange(label) {
      history().begin(doc(), label);
      history().commit();
      documentChanged();
      setMessage(label);
      buildLayerPanel();
      toolbox.refreshLayer();
      updateInfo();
      updateStatus();
      draw();
    },
  });
}

/**
 * A closed shape in the selection, to pour inside.
 *
 * The most direct way to say "pour here and nowhere else" is to draw the area
 * and select it: Place ▸ Filled Solid (P F) to draw it, Grab ▸ Filled Solid
 * (G F) to pick it up, Flood Fill (B F) to pour into it. Anything else —
 * nothing selected, a trace selected, several things at once — means the whole
 * board, which is what pouring has always done.
 *
 * The shape is *consumed*: it becomes the pour rather than sitting underneath
 * it. Left in place it would be a solid of its own on a copper layer, so the
 * pour would clear a gap around it and the plot would carry both — a ring of
 * bare board around your own boundary. Afterwards the pour is an ordinary fill
 * with an outline, so changing the area means grabbing the pour and moving its
 * corners.
 */
function selectedBoundary(): { id: number; pts: { x: number; y: number }[] } | undefined {
  if (state.selected.size !== 1) return undefined;
  const id = [...state.selected][0]!;
  const e = doc().entities.find((x) => x.id === id);
  if (!e || e.kind !== 'fill' || e.vertices.length < 3) return undefined;
  // A pour is a fill too. Re-pouring inside an existing pour is a reasonable
  // thing to do, and it is the same shape either way.
  return { id, pts: e.vertices.map((v) => ({ x: v.x, y: v.y })) };
}

/** Block▸Flood Fill — pour a plane on a copper/plane layer for a chosen net. */
async function floodCommand(): Promise<void> {
  if (doc().kind !== 'pcb') { setMessage('Flood Fill is a board command'); return; }
  const w = await needBoardwork();
  const area = selectedBoundary();
  const nets = extractNetlist(doc()).nets
    .filter((n) => n.named).map((n) => n.name).sort();

  const { showFloodDialog } = await import('./ui/flood.ts');
  showFloodDialog({
    doc: doc(),
    units: state.units,
    activeLayer: state.activeLayer,
    nets,
    // A selected closed shape is where the pour goes, if the user wants it
    // there. That is what lets one layer carry a pour per net: two pours over
    // the whole board would be two nets touching.
    boundary: area?.pts,
    onConfirm(o) {
      history().begin(doc(), 'Flood Fill');
      const d = doc();
      // Remember the net on the layer, so a re-flood defaults to it.
      const layer = d.layers[o.targetLayer];
      if (layer) layer.net = o.net || undefined;
      // And record the clearances as the board's rule. They are seeded from
      // the file's FloodFill:: record, so without writing back a value typed
      // here would be forgotten the moment the dialog closed and the next
      // pour would silently use the old one.
      d.floodFill = {
        ...(d.floodFill ?? {
          floodLine: 0.011, hatchGrid: 0.05, targetLayer: o.targetLayer, sourceLayers: [],
        }),
        floodToPad: o.toPad,
        floodToLine: o.toLine,
        targetLayer: o.targetLayer,
      };
      // The shape that said where to pour becomes the pour. Leaving it would
      // put a second solid on the layer for the pour to clear around.
      if (o.boundary && area) {
        d.entities = d.entities.filter((x) => x.id !== area.id);
        state.selected.delete(area.id);
      }
      const fill = w.floodLayer(d, o);
      history().commit();
      documentChanged();
      buildLayerPanel();
      updateInfo();
      updateRatsnestStatus();
      draw();
      const where = o.boundary ? 'the selected area' : (layer?.name ?? String(o.targetLayer));
      if (!fill) {
        setMessage('Nothing to flood — the board has no extent');
        return;
      }
      // Say what the pour actually joined. A pour that relieves nothing is
      // copper connected to nothing, and the only visible difference is that
      // every pad has a ring round it — which looks much like a pour that
      // worked. The count is the thing that tells them apart.
      const g = w.pourGeometry(d, fill);
      const settled = w.resolvePour(fill, g);
      const joined = fill.flood?.connect === 'solid'
        ? `${g.pads.length} cleared, the rest flooded solid`
        : `${g.thermals} relieved, ${g.cleared} cleared`;
      const tidied = [
        settled.stranded > 0 ? 'stranded copper removed' : '',
        settled.splintered > 0 ? 'splinters removed' : '',
      ].filter(Boolean).join(', ');
      // A pad whose every arm was dropped is relieved into copper it never
      // reaches — connected to the plane by nothing at all. That is a fault,
      // not a tidy-up, so it is said first and said plainly.
      const orphans = settled.orphans > 0
        ? `${settled.orphans} pad${settled.orphans === 1 ? '' : 's'} could not reach the pour — `
          + 'widen the spokes, reduce the pad clearance, or wire them by hand. '
        : '';
      setMessage(orphans
        + `Flooded ${where}${o.net ? ` with ${o.net}` : ''} · ${joined}`
        + (tidied ? ` · ${tidied}` : ''));
    },
  });
}

/**
 * The layer buses belong on, creating it if the sheet has none.
 *
 * This matters for more than tidiness: the netlist treats only Schematic-type
 * layers as conductive, so a bus drawn on its own Drawing-type layer is inert —
 * it crosses signal wires without shorting them, which is exactly what a bus
 * drawing is. Putting one on the signals layer would join every net it touched.
 */
function busLayerIndex(d: CadDocument): number {
  const found = d.layers.findIndex((l) => /bus/i.test(l.name));
  if (found >= 0) return found;
  const at = insertLayer(d, d.layers.length, {
    name: 'Busses', type: LayerType.Drawing, screen: 0x00c0ff,
  });
  buildLayerPanel();
  return at;
}

/**
 * Place ▸ Bus Wire — the fat line a bus is drawn as.
 *
 * Layer 3 and .05" wide, both fixed, because that is what the original sets
 * before it hands over to `placeline` (OGmenu02.cpp:1102-1112). A bus is drawing
 * and not wiring: nothing rides it electrically, and the ports tapped off it are
 * what carry the signals. So a bus may cross a wire without shorting it, and the
 * eight lines of a byte are one stroke.
 */
function placeBusWire(): void {
  const d = doc();
  const def = (d.defaults.line ??= { width: 0.05, style: 0 });
  def.width = 0.05; // the original's own figure, and it should read heavier
  // The layer is set before the dialog opens, not after it closes. It is
  // forced either way — `setlayer(wp,3)` is the first thing the original's
  // handler does — and a dialog that showed `0 Master` while about to draw on
  // Busses was telling the operator something untrue about their own command.
  def.layer = busLayerIndex(d);
  showPlaceParams('line', {
    doc: d,
    units: state.units,
    onConfirm() {
      documentChanged();
      // The plain line tool, not the wire tool. A bus is drawing: it takes the
      // width it was given (.05") and stays on the layer it was put on, where
      // the wire tool holds every schematic line to .01" and steers itself back
      // onto a Schematic-type layer — which drew the bus as a signal wire, in
      // the signals colour, a fifth of the thickness. The original is plain
      // about it too: Bus Wire ends in `placeline(wp,3)`, the same call Line
      // and Ortho-Line make, and only the mode differs. Mode 3 is the axes and
      // the diagonals, which is what a bus needs and a signal wire is denied.
      useTool('line', 'Place: Bus Wire', 3);
      state.activeLayer = busLayerIndex(d);
      toolbox.refreshLayer();
      updateStatus();
      draw();
    },
  });
}

/**
 * Place ▸ Signal Name — letter a wire with the net it carries.
 *
 * The original places a plain .06" string on the Signals layer at the cursor
 * (`placesignalname`, OGmenu00.cpp:963) and lets the net scan pick it up: a
 * label whose insertion point sits on a wire *is* that wire's name. Ours does
 * the same, and pushes the name onto the net's primitives as well, so the sheet
 * says it twice and cannot contradict itself.
 *
 * The layer matters. Lettering on the Symbols layer is decoration — the scan
 * only reads labels on a conducting layer — so this ignores whatever layer
 * happens to be active and writes on Signals, as the original does.
 */
function placeSignalNameCommand(): void {
  cancelPlace();
  toolbox.cancel();
  const what = 'Signal Name';
  clickMode = {
    label: what,
    run: (w) => {
      const found = netAtPoint(w);
      const suggested = found?.net.named ? found.name : '';
      const name = window.prompt(`${what} — name:`, suggested);
      if (name === null) return;
      const trimmed = name.trim();
      if (!trimmed) return;

      const d = doc();
      history().begin(d, what);
      d.entities.push({
        kind: 'text',
        id: newId(),
        layer: schLayers(d).signals,
        // At the click, not above it: the insertion point is what the net scan
        // tests against the wire, and a label nudged clear of the wire to look
        // tidy is a label that names nothing.
        x: w.x,
        y: w.y,
        height: 0.06,
        text: trimmed,
        anchor: 'BL',
      });
      // Naming a signal names its whole net, as Name Net does.
      if (found) applyNetName(new Set(found.net.entityIds), trimmed);
      history().commit();
      documentChanged();
      updateInfo();
      updateStatus();
      draw();
      setMessage(found
        ? `Labelled net ${trimmed}`
        : `Placed signal name ${trimmed} — it names nothing until it sits on a wire`);
    },
  };
  setMessage(`${what} — click a wire to label it, Esc to stop`);
  updateStatus();
}

/**
 * Place ▸ Dimension / Arrow Head — the manual's annotation pair. Both take two
 * points: a dimension measures between them and labels the distance, an arrow
 * head points from the first to the second.
 */
function placeAnnotation(kind: 'dimension' | 'arrow'): void {
  cancelPlace();
  toolbox.cancel();
  const what = kind === 'dimension' ? 'Dimension' : 'Arrow Head';
  let first: { x: number; y: number } | null = null;

  clickMode = {
    label: what,
    run: (w) => {
      const g = doc().grid.current;
      const p = { x: snap(w.x, g), y: snap(w.y, g) };
      if (!first) {
        first = p;
        setMessage(`${what} — select the second point`);
        return;
      }
      const a = first;
      first = null;
      if (Math.hypot(p.x - a.x, p.y - a.y) < 1e-9) {
        setMessage(`${what} — the two points must differ`);
        return;
      }

      const d = doc();
      history().begin(d, `Place ${what}`);
      for (const e of annotationEntities(d, kind, a, p)) d.entities.push(e);
      history().commit();
      documentChanged();
      updateInfo();
      updateStatus();
      draw();
      setMessage(kind === 'dimension'
        ? `Dimension ${formatDisplay(Math.hypot(p.x - a.x, p.y - a.y), state.units)}`
        : 'Arrow head placed');
    },
  };
  setMessage(`${what} — select the first point, Esc to stop`);
  updateStatus();
}

/** Build the geometry for a dimension or a lone arrow head. */
function annotationEntities(
  d: CadDocument, kind: 'dimension' | 'arrow',
  a: { x: number; y: number }, b: { x: number; y: number },
): Entity[] {
  const layer = state.activeLayer;
  const width = d.defaults.line?.width ?? 0.01;
  const len = Math.hypot(b.x - a.x, b.y - a.y);
  // The head grows with the arrow, so a long one is not tipped by a speck. The
  // stored default is the floor, and a third of the shaft the ceiling, so a
  // short arrow is still an arrow rather than a solid triangle.
  const head = Math.min(
    Math.max(d.defaults.xyDimension?.size ?? 0.06, len * 0.15),
    Math.max(len / 3, 1e-6),
  );
  const out: Entity[] = [];

  const ang = Math.atan2(b.y - a.y, b.x - a.x);
  // Two short barbs swept back from the tip make the head; drawn as lines so
  // they plot like every other stroke rather than needing a filled polygon.
  const barbs = (tip: { x: number; y: number }, dir: number): void => {
    for (const spread of [Math.PI - 0.35, Math.PI + 0.35]) {
      out.push({
        kind: 'line', id: newId(), layer, width,
        x1: tip.x, y1: tip.y,
        x2: tip.x + Math.cos(dir + spread) * head,
        y2: tip.y + Math.sin(dir + spread) * head,
      });
    }
  };

  out.push({ kind: 'line', id: newId(), layer, width, x1: a.x, y1: a.y, x2: b.x, y2: b.y });
  barbs(b, ang);

  if (kind === 'dimension') {
    barbs(a, ang + Math.PI); // a dimension is arrowed at both ends
    const height = d.defaults.text?.height ?? 0.06;
    // The measurement sits above the middle of the line, turned to match it and
    // kept upright so it never reads upside-down.
    let deg = (ang * 180) / Math.PI;
    if (deg > 90 || deg < -90) deg += 180;
    const nx = -Math.sin(ang) * height * 0.5;
    const ny = Math.cos(ang) * height * 0.5;
    out.push({
      kind: 'text', id: newId(), layer, height,
      x: (a.x + b.x) / 2 + nx,
      y: (a.y + b.y) / 2 + ny,
      text: formatDisplay(len, state.units),
      anchor: 'BC',
      ...(deg ? { rotation: deg } : {}),
    });
  }
  return out;
}

/**
 * Curate the File menu — the essentials in a sensible order, Quit at the bottom,
 * and none of the original's Re-Open / New-Graphics/Sibling clutter.
 *
 * The Open and Import entries are labelled for what they actually do, which the
 * CIRCAD-file-commands preference swaps: on, Open takes a design file from disk
 * and Import opens a stored project; off, it is the other way round. Naming the
 * source in the label means the two are never ambiguous whichever way they sit.
 */
function applyFileMenuLayout(): void {
  const legacy = prefs.legacyFileVerbs;
  menus.setLayout({
    F: [
      { heading: 'Open' },
      { code: 'FE', label: 'New' },
      { code: 'Ft', label: 'New Text File' },
      { code: 'FO', label: legacy ? 'Open File…' : 'Open Project…' },
      { code: 'FD', label: legacy ? 'Import Project…' : 'Import File…' },
      { code: 'FW', label: 'Rename…' },
      { code: 'FC', label: 'Close' },
      { code: 'FQ', label: 'Close All' },
      // A design is one board and any number of sheets, so the board belongs
      // in this group and not among the file-level commands above it.
      { heading: 'Sheets and board' },
      { code: 'Fi', label: 'New Sheet…' },
      { code: 'FN', label: 'Next Sheet' },
      { code: 'FV', label: 'Previous Sheet' },
      { code: 'FY', label: 'New Board' },
      { heading: 'Save' },
      { code: 'FS', label: 'Save' },
      { code: 'FF', label: 'Save to Project (web)…' },
      { code: 'FA', label: 'Save As… (file)' },
      { heading: 'Send out' },
      { code: 'FJ', label: 'Export to CIRCAD…' },
      { code: 'FK', label: 'Export to KiCad…' },
      { code: 'FG', label: 'Gerber Out…' },
      { code: 'FH', label: 'Gerber In / View…' },
      { code: 'FP', label: 'Print…' },
      { code: 'FM', label: 'Image Save…' },
      { code: 'FB', label: 'BOM Out…' },
      // A column of its own, always: the list of what you were working on is
      // the reason most people open this menu, and it was landing at a
      // different height on every window.
      { heading: 'Re-Open', column: true },
      // The Re-Open list. Named entries rather than numbered slots: "Preamp rev
      // B" is a thing you can choose and "Re Open 1" is not, and the popup
      // already prints each command's own key in a column of its own — so a
      // label that began with the digit as well showed it twice.
      //
      // Above Quit, not below it. The original keeps the list at the foot of
      // the menu, but its File menu is a dozen entries and this one is thirty:
      // a list of what you were working on, put underneath the command that
      // signs you out, is at the bottom of a scroll and nobody finds it.
      ...(prefs.recentProjects ?? []).map((r, i) => ({
        code: `F${i}`,
        label: r.name,
      })),
      ...((prefs.recentProjects ?? []).length > 0
        ? [
            { code: 'F>', label: 'Purge Missing' },
            { code: 'F?', label: 'Purge List' },
          ]
        : []),
      { heading: 'Finish' },
      { code: 'FX', label: 'Quit' },
    ],
    // The original names E N "Component" and E C "Components" — a difference of
    // one letter for two commands that now do noticeably different things, so
    // the menu says which is which even though the codes are unchanged.
    E: [
      { code: 'EQ', label: 'Query Items' },
      { code: 'ES', label: 'Snap Item — jump to the nearest point' },
      { code: 'EL', label: 'Line' },
      { code: 'EA', label: 'Arc' },
      { code: 'EP', label: 'Pad' },
      { code: 'ET', label: 'Text' },
      { code: 'EX', label: 'Text Parameters' },
      { code: 'EF', label: 'Filled Solid' },
      { code: 'EN', label: 'Component — anything placed' },
      { code: 'EC', label: 'Components — parts only' },
      { code: 'EW', label: 'Line to Arc — bow a straight line' },
      { code: 'EY', label: 'Copy to Clipboard' },
      { code: 'EB', label: 'Paste from Clipboard' },
      { code: 'EJ', label: 'Find' },
      { code: 'E5', label: 'Find Next' },
      { code: 'Er', label: 'Replace' },
      { code: 'Ed', label: 'Replace Next' },
      { code: 'E8', label: 'Replace All' },
      { code: 'EH', label: 'Align — two text files, lined up' },
      { code: 'EI', label: 'Compare — what differs between two' },
    ],
    // Nets is two columns: what the original has, and what routes the board.
    //
    // Curated, which means listed-or-hidden — a command left out here appears
    // nowhere however well it works. `NM` (Image) is left out deliberately: it
    // is unimplemented and belongs to the pixel-image side of the original that
    // is not being built.
    //
    // Auto Route stays on the left. It is the original's own command and its
    // own letter, and the right-hand column is the six this program added.
    N: [
      { code: 'NS', label: 'Net Scan' },
      { code: 'N+', label: 'Net Scan Plus' },
      { code: 'N-', label: 'Net Scan Minus' },
      { code: 'NG', label: 'Net Scan Log' },
      { code: 'NH', label: 'Highlight Net' },
      { code: 'NC', label: 'Clear Highlights' },
      { code: 'NN', label: 'Name Net' },
      { code: 'NI', label: 'Net List In' },
      { code: 'NO', label: 'Net List Out' },
      { code: 'NL', label: 'Rat Line' },
      { code: 'NR', label: 'Ratsnest' },
      { code: 'NT', label: 'Ratsnest (lite)' },
      { code: 'NU', label: 'Un-Ratsnest' },
      { code: 'NA', label: 'Auto Route…' },

      { heading: 'Autorouter', column: true },
      { code: 'NP', label: 'Placement Density' },
      { code: 'NB', label: 'Auto Place…' },
      { code: 'NJ', label: 'Route Connection' },
      { code: 'NE', label: 'Pour Planes…' },
      { code: 'NF', label: 'Footprint Check…' },
      { code: 'NW', label: 'Tidy Copper' },
      { code: 'ND', label: 'DFM Check…' },
    ],
    // The original labels both of these "Event" — `UnDo_Event` and `ReDo_Event`
    // told them apart by symbol, which nothing on screen ever showed. Say which
    // way each one goes.
    // The original has two of each: one step, and the lot. Purging is offered
    // separately because both stacks are cleared on their own — the redo stack
    // the moment you draw after undoing, the undo stack when a file is written
    // — and doing it deliberately is a decision rather than a side effect.
    U: [
      { code: 'UU', label: 'Undo' },
      { code: 'UR', label: 'Redo' },
      { code: 'UA', label: 'Undo All' },
      { code: 'UE', label: 'Redo All' },
      { code: 'UP', label: 'Purge Redo' },
      { code: 'US', label: 'Purge Undo' },
    ],
    // The original's Support was a telephone number on a card. Here it sends
    // the report — and Reports is where it is read. Help itself leads, because
    // a Help menu whose first entry is not help is a poor joke.
    //
    // Curated, which means listed-or-hidden: a command registered but left out
    // of this list does not appear at all, however well it works.
    '?': [
      { code: '?H', label: 'Help — search the manual…' },
      { code: '?R', label: 'Support — report a problem…' },
      { code: '?B', label: 'Reports…' },
      // Hidden in the installed window: an entry offering to install the app
      // you are already running is a menu telling you it has not been paying
      // attention. The bar hides what is not implemented; this hides what has
      // nothing left to do.
      ...(isInstalled() ? [] : [{ code: '?I', label: 'Install as App…' }]),
      { code: '?A', label: 'About SirCAD…' },
    ],
  });
}

/**
 * Setup ▸ File Parameters — draw modes, grid dots and the display colours.
 *
 * What is set here belongs to the user, not to the file: it is written to the
 * open document so the drawing changes at once, and kept in the preferences so
 * the next file opened cannot undo it.
 */
function drawModesCommand(): void {
  showDrawModesDialog({
    doc: doc(),
    units: state.units,
    prefs,
    onPref(next) {
      prefs = next;
      savePrefs(prefs);
      // The other editor's document is not on screen, but it should agree the
      // moment it is shown.
      applyUserDisplay(state.docs.sch);
      applyUserDisplay(state.docs.pcb);
    },
    onChange(label) {
      documentChanged();
      setMessage(label);
      draw();
    },
  });
}

/**
 * Put the user's File Parameters onto a document.
 *
 * Every document carries its own `Display::` record, so loading or importing
 * one used to replace the draw modes and colours the user had chosen — there
 * was no way to keep a preference across files. This is applied wherever a
 * document arrives or is created, so only the user can change those settings
 * and they hold until the user changes them again.
 */
function applyUserDisplay<T extends CadDocument>(target: T): T {
  applyDisplayPrefs(target, prefs);
  return target;
}

/** Setup ▸ Via Parameters — the pad and drill Place Via will use. */
function viaParamsCommand(onConfirm?: () => void): void {
  showViaParamsDialog({
    doc: doc(),
    units: state.units,
    onChange(label) { documentChanged(); setMessage(label); updateStatus(); },
    onConfirm,
  });
}

/**
 * Setup ▸ Zero Point — the manual's relative addressing. The first invocation
 * pins the origin at the cursor, the second restores absolute coordinates.
 */
function zeroPointCommand(): void {
  if (relativeZero) {
    relativeZero = false;
    setMessage('Absolute coordinates');
  } else {
    const at = cursorWorld ?? lastPos ?? { x: 0, y: 0 };
    doc().zeroPoint = { x: at.x, y: at.y };
    relativeZero = true;
    documentChanged();
    setMessage(
      `Zero point set at ${formatDisplay(at.x, state.units)}, `
      + `${formatDisplay(at.y, state.units)} — relative addressing`);
  }
  updateStatus();
  draw();
}

/** Setup ▸ Mirror View — look at the sheet from its other side. */
function mirrorViewCommand(): void {
  viewport.mirrored = !viewport.mirrored;
  setMessage(viewport.mirrored ? 'Mirror view — viewed from the back' : 'Normal view');
  updateStatus();
  draw();
}

/** Setup ▸ Keystroke Macros — assign command runs to the function keys. */
function macrosCommand(): void {
  showMacrosDialog({
    macros,
    known: menus.implementedCommands(),
    onChange(next) {
      macros = next;
      setMessage(`Keystroke macros saved — ${Object.keys(next).length} assigned`);
    },
  });
}

// ---------------------------------------------------------------------------
// The macro recorder
// ---------------------------------------------------------------------------

/**
 * The install offer, built at boot.
 *
 * Declared here and assigned there because Help ▸ Install asks it for its
 * answer, and the commands are registered before the boot sequence reaches the
 * elements this needs.
 */
let install: InstallUi;

const recorder = new MacroRecorder();
/** True while a recorded macro is being played, so it does not record itself. */
let playing = false;

/**
 * Setup ▸ Record Macro (S R) — start recording, or stop and keep it.
 *
 * One macro, deliberately. A list of them is a different feature with a screen
 * to manage it, and the original already has that for written keystrokes.
 *
 * `byCode` says it was reached by its two letters rather than by F11, which
 * matters on the way out: those letters are already in the recording by the
 * time the menu runs the command they spell.
 */
function recordMacroCommand(byCode = false): void {
  if (recorder.recording) {
    if (byCode) recorder.forgetTrailing(['R', 'S']);
    const steps = recorder.stop();
    if (steps.length === 0) {
      setMessage('Nothing was recorded');
      return;
    }
    prefs = { ...prefs, macro: steps };
    savePrefs(prefs);
    if (recorder.full) {
      setMessage(`Macro recorded — ${summarise(steps)}, which is as much as one holds.`);
      return;
    }
    setMessage(`Macro recorded — ${summarise(steps)}. F12 plays it back.`);
    return;
  }
  recorder.start();
  setMessage('Recording — do it once, then F11 (or S R) again to stop');
}

/**
 * Setup ▸ Play Macro (S B) — do it again, here.
 *
 * Anchored at the cursor: the first click of the recording lands where the
 * pointer is now and the rest keep their offsets from it. Without the cursor
 * over the drawing there is nowhere to anchor, so it plays where it was
 * recorded — which is right for a macro that is all keys and no clicks.
 */
async function playMacroCommand(byCode = false): Promise<void> {
  // Recording first: mid-recording there is no macro yet by definition, and
  // "none recorded" would be the answer to a question nobody asked.
  if (recorder.recording) {
    // The letters that asked for this are in the recording, and the answer is
    // no — so they come back out rather than being kept as work.
    if (byCode) recorder.forgetTrailing(['B', 'S']);
    setMessage('Still recording — F11 stops it first');
    return;
  }
  const steps = prefs.macro ?? [];
  if (steps.length === 0) { setMessage('No macro recorded — F11 records one'); return; }
  if (playing) return;

  const at = cursorWorld ?? { x: 0, y: 0 };
  playing = true;
  try {
    const r = await playMacro(steps, at, macroSink());
    setMessage(r.stoppedAt === undefined
      ? `Macro played — ${summarise(steps)}`
      : `Macro stopped at step ${r.stoppedAt + 1}: something is waiting for an answer`);
  } finally {
    playing = false;
  }
  draw();
}

/** Where a replayed macro's events go: at the real canvas, as a hand would. */
function macroSink(): MacroSink {
  const send = (type: string, init: Record<string, unknown>): void => {
    canvas.dispatchEvent(new MouseEvent(type, {
      bubbles: true, cancelable: true, ...init,
    }));
  };
  return {
    key: (step) => {
      window.dispatchEvent(new KeyboardEvent('keydown', {
        key: step.key, shiftKey: step.shift, ctrlKey: step.ctrl, altKey: step.alt,
        bubbles: true, cancelable: true,
      }));
    },
    click: (x, y, button) => {
      const clientX = canvasRect.left + viewport.toScreenX(x);
      const clientY = canvasRect.top + viewport.toScreenY(y);
      // A move before the press, because half the tools read the cursor rather
      // than the event: a click with no move behind it lands at wherever the
      // pointer was last left.
      send('mousemove', { clientX, clientY });
      send('mousedown', { clientX, clientY, button });
      window.dispatchEvent(new MouseEvent('mouseup', {
        bubbles: true, cancelable: true, clientX, clientY, button,
      }));
    },
    // A frame between steps, so anything a command started has run before the
    // next one arrives.
    settle: () => new Promise<void>((done) => { window.setTimeout(done, 16); }),
    blocked: () => document.querySelector('dialog[open]') !== null,
  };
}

/** Run a function key's macro, if it has one. Returns true when handled. */
function runMacro(key: string): boolean {
  const seq = macros[key];
  if (!seq) return false;
  const codes = seq.split(/\s+/).filter(Boolean);
  for (const code of codes) {
    if (!menus.runByCode(code)) {
      setMessage(`${key}: no command ${code}`);
      return true;
    }
  }
  return true;
}

/** Delete▸Flood Lines — remove the pour on the active layer. */
async function removeFloodCommand(): Promise<void> {
  const w = await needBoardwork();
  history().begin(doc(), 'Delete Flood');
  const n = w.removeFlood(doc(), state.activeLayer);
  history().commit();
  if (n > 0) { documentChanged(); updateRatsnestStatus(); draw(); }
  setMessage(n > 0
    ? `Removed flood on ${doc().layers[state.activeLayer]?.name ?? state.activeLayer}`
    : 'No flood on the active layer');
}

// ---------------------------------------------------------------------------
// Copper cleanup
// ---------------------------------------------------------------------------

/** Shared shape: run an edit over the board, report what it did, or say why not. */
function boardEdit(
  label: string, run: (d: CadDocument) => number, describe: (n: number) => string,
): void {
  const d = doc();
  if (d.kind !== 'pcb') { setMessage(`${label} is a board command`); return; }
  history().begin(d, label);
  const n = run(d);
  if (n === 0) {
    history().commit();
    setMessage(`${label}: nothing to do`);
    return;
  }
  history().commit();
  documentChanged();
  state.selected.clear();
  invalidateSnap();
  updateInfo();
  updateStatus();
  draw();
  setMessage(describe(n));
}

/**
 * Delete ▸ Iso Islands — copper that reaches no pad.
 *
 * This removes real copper, and on a board carrying a legend drawn in copper
 * it will take the lettering with it: the original's rule is simply "not
 * connected to a pad", and lettering is not. So it says how much it found and
 * asks, rather than quietly deleting a fifth of the artwork.
 */
function isolationIslandsCommand(): void {
  const d = doc();
  if (d.kind !== 'pcb') { setMessage('Iso Islands is a board command'); return; }
  // Count first, on a copy, so the question can be specific.
  const preview = deleteIsolationIslands(structuredClone(d));
  if (preview.length === 0) { setMessage('Iso Islands: no stranded copper'); return; }
  if (!window.confirm(
    `Delete ${preview.length} piece${preview.length === 1 ? '' : 's'} of copper that `
    + 'connect to no pad?\n\nAnything drawn in copper but not wired — a legend, '
    + 'a logo, a test coupon — counts as stranded and will go too.')) {
    setMessage('Iso Islands cancelled');
    return;
  }
  boardEdit('Delete Iso Islands', (b) => deleteIsolationIslands(b).length,
    (n) => `Removed ${n} isolated piece${n === 1 ? '' : 's'} of copper`);
}

/** Block ▸ Tear Drop — reinforce every track/pad junction. */
function addTeardropsCommand(): void {
  boardEdit('Tear Drops', (b) => addTeardrops(b).length,
    (n) => `Added ${n} teardrop${n === 1 ? '' : 's'}`);
}

/** Delete ▸ Tear Drops — remove them again. */
function deleteTeardropsCommand(): void {
  boardEdit('Delete Tear Drops', (b) => deleteTeardrops(b).length,
    (n) => `Removed ${n} teardrop${n === 1 ? '' : 's'}`);
}

/**
 * Delete ▸ Hilighted Items — remove whatever Highlight Net last lit up.
 *
 * Deliberately not silent when nothing is highlighted: the command reads as
 * "delete the selection" and doing nothing without saying so looks broken.
 */
function deleteHighlightedCommand(): void {
  if (!highlightNet || highlightNet.ids.size === 0) {
    setMessage('Nothing highlighted — use Nets ▸ Highlight Net first');
    return;
  }
  const net = highlightNet.name;
  const ids = highlightNet.ids;
  const d = doc();
  const count = d.entities.filter((e) => ids.has(e.id)).length;
  if (count === 0) { setMessage(`Nothing of ${net} to delete at the top level`); return; }
  if (!window.confirm(`Delete ${count} highlighted item${count === 1 ? '' : 's'} on ${net}?`)) {
    setMessage('Delete Highlighted cancelled');
    return;
  }
  history().begin(d, 'Delete Highlighted');
  const n = deleteHighlighted(d, ids).length;
  history().commit();
  highlightNet = null;
  documentChanged();
  state.selected.clear();
  invalidateSnap();
  updateInfo();
  updateStatus();
  draw();
  setMessage(`Removed ${n} item${n === 1 ? '' : 's'} from ${net}`);
}

// ---------------------------------------------------------------------------
// Placement density (N P)
// ---------------------------------------------------------------------------

/**
 * The congestion report, kept until the geometry changes.
 *
 * A sweep costs a tenth of a second on the largest board here, which is fine
 * on demand and far too much every frame while the overlay is on.
 */
let densityCache:
  { doc: CadDocument; version: number; report: CongestionReport } | null = null;

function cachedDensity(d: CadDocument, w: BoardWork): CongestionReport {
  if (densityCache && densityCache.doc === d && densityCache.version === docVersion) {
    return densityCache.report;
  }
  const report = w.congestionReport(d);
  densityCache = { doc: d, version: docVersion, report };
  return report;
}

/** The report flattened to what a frame needs, faults and all. */
function densityOverlay(d: CadDocument): DensityOverlay | undefined {
  // Nothing before a command has loaded the router. The overlay is only ever
  // on because a command turned it on, and that command loaded these first.
  if (d.kind !== 'pcb' || !bw) return undefined;
  const r = cachedDensity(d, bw);

  const faults: DensityFault[] = [];
  const yards = new Map(bw.courtyardsOf(d).map((y) => [y.comp.id, y]));
  for (const o of r.overlaps) {
    for (const id of o.ids) {
      const y = yards.get(id);
      if (y) faults.push({ pts: y.pts, kind: 'overlap' });
    }
  }
  for (const o of r.outside) {
    const y = yards.get(o.id);
    if (y) faults.push({ pts: y.pts, kind: o.parked ? 'parked' : 'straddle' });
  }

  return {
    x0: r.heat.x0, y0: r.heat.y0, cell: r.heat.cell,
    nx: r.heat.nx, ny: r.heat.ny, pressure: r.heat.pressure, faults,
  };
}

/**
 * Nets ▸ Placement Density (N P) — how hard is this board going to be to route.
 *
 * Asked before anything is moved and before a track is laid. Crossings say how
 * many layers the connections need in principle; pressure says which part of
 * the board is the problem. Nothing here changes the document.
 */
async function densityCommand(): Promise<void> {
  const w = await needBoardwork();
  const d = doc();
  if (d.kind !== 'pcb') {
    setMessage('Placement density is about a board — switch to the board');
    return;
  }
  const report = cachedDensity(d, w);
  showDensityReport({
    report,
    overlayOn: state.showDensity,
    formatLength: (inches) => formatDisplay(inches, state.units),
    onLocate: (x, y, entityIds) => {
      viewport.rememberView();
      viewport.fit(x - 0.3, y - 0.3, x + 0.3, y + 0.3);
      state.selected.clear();
      if (entityIds) for (const id of entityIds) state.selected.add(id);
      updateInfo();
      updateStatus();
      draw();
    },
    onOverlay: (on) => {
      state.showDensity = on;
      draw();
      setMessage(on ? 'Density overlay on — N P again for the report'
        : 'Density overlay off');
    },
    onSave: () => {
      downloadText(`${d.name || 'board'}-density.txt`, w.formatCongestion(report));
    },
  });
}

/**
 * Nets ▸ Auto Place (N B) — put the parts where the netlist wants them.
 *
 * Nothing changes until Apply. The run works on a copy, so a placement that
 * turns out worse than what was there costs a glance and a Close.
 */
async function autoPlaceCommand(): Promise<void> {
  const w = await needBoardwork();
  const d = doc();
  if (d.kind !== 'pcb') {
    setMessage('Auto Place is a board command — switch to the board');
    return;
  }

  const yards = w.courtyardsOf(d);
  if (yards.length === 0) { setMessage('Auto Place: no parts on this board'); return; }
  const report = cachedDensity(d, w);

  // Copper already on the board, so the dialog can say it will not follow.
  const copper = new Set(report.routingLayers);
  let routed = 0;
  for (const e of d.entities) {
    if ((e.kind === 'line' || e.kind === 'arc') && copper.has(e.layer)) routed++;
  }

  showPlaceDialog({
    movable: yards.filter((y) => y.comp.fixed !== true).length,
    fixed: yards.filter((y) => y.comp.fixed === true).length,
    offBoard: report.outside.length,
    routed,
    formatLength: (inches) => formatDisplay(inches, state.units),
    run: async (chosen) => {
      // On a copy: a run that is never applied must leave the board untouched,
      // and the placer reads geometry it would otherwise be moving underneath.
      const copy: CadDocument = { ...d, entities: structuredClone(d.entities) };
      const before = w.congestionReport(copy);
      const result = w.autoPlace(copy, chosen);
      w.applyPlacement(copy, result.moves);
      const after = w.congestionReport(copy);
      return { result, before, after };
    },
    apply: (result) => {
      history().begin(d, 'Auto Place');
      const n = w.applyPlacement(d, result.moves);
      history().commit();
      documentChanged();
      state.selected.clear();
      updateInfo();
      updateStatus();
      draw();
      setMessage(`Auto Place: ${n} part${n === 1 ? '' : 's'} moved`);
    },
  });
}

/**
 * Nets ▸ Route Connection (N J) — lay copper for the ratline nearest the cursor.
 *
 * One connection at a time, on purpose. The whole-board router is not built
 * yet, and a router is only worth having if you can watch it do one thing and
 * agree with what it did.
 *
 * The result is checked before it lands: if the copper it laid would break a
 * clearance rule or a fab rule, it is thrown away and reported, because a
 * route that shorts two nets is worse than no route — you cannot see it.
 */
async function routeOneCommand(): Promise<void> {
  const w = await needBoardwork();
  const d = doc();
  if (d.kind !== 'pcb') {
    setMessage('Route Connection is a board command — switch to the board');
    return;
  }

  const rats = cachedRatsnest(d);
  if (rats.length === 0) { setMessage('Route Connection: nothing left to route'); return; }

  // The ratline nearest the cursor, by distance to its midpoint.
  // Nearest to where you are looking. With no cursor yet, the first ratline.
  const cx = cursorWorld?.x ?? rats[0]!.x1;
  const cy = cursorWorld?.y ?? rats[0]!.y1;
  let pick = rats[0]!;
  let best = Infinity;
  for (const r of rats) {
    const mx = (r.x1 + r.x2) / 2;
    const my = (r.y1 + r.y2) / 2;
    const dist = (mx - cx) ** 2 + (my - cy) ** 2;
    if (dist < best) { best = dist; pick = r; }
  }

  const baseline = clearanceViolations(d).length;
  const baseDfm = dfmCheck(d, d.dfm ?? DEFAULT_DFM).findings.length;

  const result = w.routeConnection(d, { x: pick.x1, y: pick.y1 }, { x: pick.x2, y: pick.y2 });
  if (!result.ok) {
    setMessage(`Route Connection: ${pick.net} — ${result.reason}`);
    return;
  }

  history().begin(d, 'Route Connection');
  d.entities.push(...result.entities);

  // The gate. Nothing lands that the rest of the program would call wrong.
  const nowFaults = clearanceViolations(d).length - baseline;
  const nowDfm = dfmCheck(d, d.dfm ?? DEFAULT_DFM).findings.length - baseDfm;
  if (nowFaults > 0 || nowDfm > 0) {
    history().rollback(d);
    documentChanged();
    draw();
    setMessage(`Route Connection: refused — the route would add `
      + `${nowFaults} clearance and ${nowDfm} manufacturing faults. This is a router bug.`);
    return;
  }

  history().commit();
  documentChanged();
  updateInfo();
  updateStatus();
  draw();
  setMessage(`Routed ${pick.net} — ${formatDisplay(result.length, state.units)}`
    + (result.vias > 0 ? `, ${result.vias} via${result.vias === 1 ? '' : 's'}` : '')
    + `, ${cachedRatsnest(doc()).length} connection${cachedRatsnest(doc()).length === 1 ? '' : 's'} left`);
}

/**
 * Nets ▸ Auto Route (N A) — the whole board.
 *
 * The last command on the feature-gap list, and the one the original never
 * finished either: its L, Z, C and S passes are all `tr1 = 0;`.
 *
 * Runs in a worker, because a large board takes a minute and a half and a
 * frozen window is not a progress report. Nothing lands until the result has
 * been measured against the same clearance and manufacturing checks the rest of
 * the program uses — a route that shorts two nets is worse than no route, since
 * only one of the two is visible.
 */
async function autoRouteCommand(): Promise<void> {
  const w = await needBoardwork();
  const d = doc();
  if (d.kind !== 'pcb') {
    setMessage('Auto Route is a board command — switch to the board');
    return;
  }

  const open = cachedRatsnest(d).length;
  if (open === 0) { setMessage('Auto Route: the board is already routed'); return; }
  const previous = d.entities.filter((e) => e.auto === true).length;

  // Every copper layer is offered; the ones the router would have chosen for
  // itself start ticked. A plane is poured, not routed, so it is not here.
  const wanted = new Set(w.routingLayers(d));
  const layers = d.layers
    .map((l, num) => ({ l, num }))
    .filter(({ l }) => l.type === LayerType.Copper && l.name !== 'Pad Master')
    .map(({ l, num }) => ({ num, name: l.name, on: wanted.has(num) }));

  // What could be poured instead of routed, and where. Offered, not taken:
  // anything a layer already names is ticked, and anything merely called GND
  // is a proposal. A supply left unticked is routed as copper like any other
  // net, which is what stops one being left out altogether.
  const pourOffers = w.planeCandidates(d)
    .filter((c) => c.layer !== undefined)
    .map((c) => ({
      net: c.net,
      layer: c.layer!,
      layerName: d.layers[c.layer!]?.name ?? `Layer ${c.layer!}`,
      pins: c.pins,
      assigned: c.why === 'assigned',
    }));

  showAutoRouteDialog({
    open,
    previous,
    layers,
    pourOffers,
    formatLength: (inches) => formatDisplay(inches, state.units),

    start: (chosen, onProgress) => {
      const worker = new Worker(new URL('./route/worker.ts', import.meta.url), {
        type: 'module',
      });
      const done = new Promise<AutoRouteResult>((resolve, reject) => {
        worker.addEventListener('message', (ev: MessageEvent<RouteMessage>) => {
          const m = ev.data;
          if (m.kind === 'progress') onProgress(m.stage, m.fraction, m.routed, m.total);
          else if (m.kind === 'done') { worker.terminate(); resolve(m.result); }
          else { worker.terminate(); reject(new Error(m.message)); }
        });
        worker.addEventListener('error', (ev) => {
          worker.terminate();
          reject(new Error(ev.message || 'the router stopped'));
        });
      });
      worker.postMessage({
        doc: JSON.parse(JSON.stringify(d)) as CadDocument,
        opts: {
          ripUp: chosen.ripUp,
          maxIterations: chosen.maxIterations,
          layers: chosen.layers,
        },
        pour: chosen.pour,
      });
      return { done, cancel: () => worker.terminate() };
    },

    apply: (result) => {
      const baseline = clearanceViolations(d).length;
      const baseDfm = dfmCheck(d, d.dfm ?? DEFAULT_DFM).findings.length;

      history().begin(d, 'Auto Route');
      if (result.remove.length > 0) {
        const gone = new Set(result.remove);
        d.entities = d.entities.filter((e) => !gone.has(e.id));
      }
      // The worker counts ids from its own zero, so they are renumbered here
      // rather than trusted to be unique in this document.
      for (const e of result.add) e.id = newId();
      d.entities.push(...result.add);

      const faults = clearanceViolations(d).length - baseline;
      const dfmFaults = dfmCheck(d, d.dfm ?? DEFAULT_DFM).findings.length - baseDfm;
      if (faults > 0 || dfmFaults > 0) {
        history().rollback(d);
        documentChanged();
        draw();
        return `Refused: the result would add ${faults} clearance and `
          + `${dfmFaults} manufacturing faults. This is a router bug — nothing was changed.`;
      }

      history().commit();
      documentChanged();
      state.selected.clear();
      updateInfo();
      updateStatus();
      draw();
      setMessage(`Auto Route: ${result.routed} of ${result.total} connections, `
        + `${result.vias} via${result.vias === 1 ? '' : 's'}`
        + (result.unrouted.length > 0 ? `, ${result.unrouted.length} nets left for you` : ''));
      return null;
    },

    onSave: (result) => {
      downloadText(`${d.name || 'board'}-autoroute.txt`, w.formatRouteLog(result));
    },
  });
}

/**
 * Nets ▸ Pour Planes (N E) — the supplies, poured rather than routed.
 *
 * The candidates are proposed and nothing is poured until it is ticked. What
 * comes back leads with **copper thrown away** — poured copper the clearances
 * cut off from its net — and names every pad the pour never reached, because a
 * pin the netlist calls connected and the copper does not is a bench fault on a
 * board that looks right.
 */
async function pourPlanesCommand(): Promise<void> {
  const w = await needBoardwork();
  const d = doc();
  if (d.kind !== 'pcb') {
    setMessage('Pour Planes is a board command — switch to the board');
    return;
  }

  showPlanesDialog({
    candidates: w.planeCandidates(d),
    planeLayers: w.planeLayersOf(d),
    layerName: (i) => d.layers[i]?.name ?? `Layer ${i}`,
    formatArea: (sq) => `${sq.toFixed(4)} sq in`,
    run: (assign) => w.pourPlanes(d, { assign }),

    apply: (report) => {
      const baseline = clearanceViolations(d).length;
      const key = (f: { rule: string; detail: string }): string => `${f.rule}|${f.detail}`;
      const baseDfm = new Set(dfmCheck(d, d.dfm ?? DEFAULT_DFM).findings.map(key));

      history().begin(d, 'Pour Planes');
      if (report.remove.length > 0) {
        const gone = new Set(report.remove);
        d.entities = d.entities.filter((e) => !gone.has(e.id));
      }
      d.entities.push(...report.add);

      const faults = clearanceViolations(d).length - baseline;
      const dfmFaults = dfmCheck(d, d.dfm ?? DEFAULT_DFM).findings
        .filter((f) => !baseDfm.has(key(f))).length;
      if (faults > 0 || dfmFaults > 0) {
        history().rollback(d);
        documentChanged();
        draw();
        return `Refused: the pour would add ${faults} clearance and `
          + `${dfmFaults} manufacturing faults. This is a bug — nothing was changed.`;
      }

      history().commit();
      documentChanged();
      updateInfo();
      updateStatus();
      draw();
      const orphans = report.nets.reduce((a, n) => a + n.unreached.length, 0);
      setMessage(`Poured ${report.nets.length} plane${report.nets.length === 1 ? '' : 's'}, `
        + `${report.vias} via${report.vias === 1 ? '' : 's'}, `
        + `${report.lost.toFixed(4)} sq in thrown away`
        + (orphans > 0 ? ` — ${orphans} pad${orphans === 1 ? '' : 's'} the pour never reaches` : ''));
      return null;
    },

    onSave: (report) => {
      downloadText(`${d.name || 'board'}-planes.txt`, w.formatPlaneReport(report));
    },
  });
}

/**
 * Nets ▸ Tidy Copper (N W) — cut the right angles, pull the pointless vias.
 *
 * The most dangerous change the program makes, because the board has already
 * passed every check once and nobody looks at it again. So it is gated twice:
 * against clearance and manufacturing rules like everything else, and against
 * connectivity — a corner cut too deep breaks a net while making the board look
 * better, and that is the failure this has to be incapable of.
 *
 * Only the router's own copper, so a track somebody drew by hand keeps the
 * shape they drew.
 */
async function tidyCopperCommand(): Promise<void> {
  const w = await needBoardwork();
  const d = doc();
  if (d.kind !== 'pcb') {
    setMessage('Tidy Copper is a board command — switch to the board');
    return;
  }

  const baseline = clearanceViolations(d).length;
  const key = (f: { rule: string; detail: string }): string => `${f.rule}|${f.detail}`;
  const baseDfm = new Set(dfmCheck(d, d.dfm ?? DEFAULT_DFM).findings.map(key));
  const beforeIslands = extractNetlist(d).nets.reduce((a, n) => a + n.groups.length, 0);

  const r = w.cleanup(d);
  if (r.add.length === 0 && r.remove.length === 0) {
    setMessage('Tidy Copper: nothing to tidy');
    return;
  }

  history().begin(d, 'Tidy Copper');
  if (r.remove.length > 0) {
    const gone = new Set(r.remove);
    d.entities = d.entities.filter((e) => !gone.has(e.id));
  }
  d.entities.push(...r.add);

  const faults = clearanceViolations(d).length - baseline;
  const dfmFaults = dfmCheck(d, d.dfm ?? DEFAULT_DFM).findings
    .filter((f) => !baseDfm.has(key(f))).length;
  const afterIslands = extractNetlist(d).nets.reduce((a, n) => a + n.groups.length, 0);

  if (faults > 0 || dfmFaults > 0 || afterIslands > beforeIslands) {
    history().rollback(d);
    documentChanged();
    draw();
    setMessage(afterIslands > beforeIslands
      ? `Tidy Copper: refused — it would break ${afterIslands - beforeIslands} `
        + 'connection(s). This is a bug; nothing was changed.'
      : `Tidy Copper: refused — it would add ${faults} clearance and `
        + `${dfmFaults} manufacturing faults. This is a bug; nothing was changed.`);
    return;
  }

  history().commit();
  documentChanged();
  state.selected.clear();
  invalidateSnap();
  updateInfo();
  updateStatus();
  draw();
  setMessage(`Tidy Copper: ${r.corners} corner${r.corners === 1 ? '' : 's'} cut`
    + (r.viasRemoved > 0 ? `, ${r.viasRemoved} vias pulled` : '')
    + (r.stitches > 0 ? `, ${r.stitches} stitches` : ''));
}

/** Net Scan — report shorts and unrouted nets, each locatable on the board. */
function netScanCommand(): void {
  const d = doc();
  if (d.kind !== 'pcb') { setMessage('Net Scan reports board connectivity — switch to the board'); return; }
  const report = connectivityReport(d);
  showNetReport({
    report,
    // The clearances come from the board's own NetScan:: record — the rules
    // the design was drawn to, not a fixed set.
    violations: clearanceViolations(d),
    // formatDisplay already carries the unit — " for inches, " mil", " mm".
    formatLength: (inches) => formatDisplay(inches, state.units),
    onLocate: (x, y, entityIds) => {
      viewport.rememberView();
      viewport.fit(x - 0.3, y - 0.3, x + 0.3, y + 0.3);
      state.selected.clear();
      if (entityIds) for (const id of entityIds) state.selected.add(id);
      updateInfo();
      updateStatus();
      draw();
    },
  });
}

/**
 * Nets ▸ DFM Check (N D) — can this board be made?
 *
 * Net Scan asks whether the circuit is right. This asks whether the thing can
 * be built: is a drill smaller than the shop's smallest bit, is there enough
 * copper left round a hole after it is drilled, is there an edge to cut the
 * board out of at all. The two sit together in this menu because they are the
 * two ways a finished board fails, and apart in the code because they fail for
 * unrelated reasons.
 *
 * The rules come from the document when it carries a set, and from the standard
 * profile when it does not — so a design that has been told its shop's numbers
 * is checked against them without being asked twice.
 */
function dfmCommand(rules?: DfmRules): void {
  const d = doc();
  if (d.kind !== 'pcb') {
    setMessage('The DFM check is about a board — switch to the board');
    return;
  }
  const use = rules ?? d.dfm ?? DEFAULT_DFM;
  const saved = prefs.dfmProfiles ?? [];
  const named = allProfiles(saved).find((p) => sameRules(p.rules, use));

  const report = dfmCheck(d, use);
  showDfmReport({
    report,
    rules: use,
    saved,
    profileName: named?.name ?? 'this board’s own rules',
    formatLength: (inches) => formatDisplay(inches, state.units),
    onLocate: (x, y, entityIds) => {
      viewport.rememberView();
      viewport.fit(x - 0.3, y - 0.3, x + 0.3, y + 0.3);
      state.selected.clear();
      if (entityIds) for (const id of entityIds) state.selected.add(id);
      updateInfo();
      updateStatus();
      draw();
    },
    onProfile: (id) => {
      const p = allProfiles(saved).find((x) => x.id === id);
      if (p) dfmCommand(p.rules);
    },
    onEditRules: () => {
      showDfmRules({
        rules: use,
        units: state.units,
        saved,
        // A shop's numbers belong to whoever is using the program rather than
        // to one board, so a saved set goes with the preferences and is offered
        // on every design opened from now on.
        onSaveProfile: (made) => {
          const rest = (prefs.dfmProfiles ?? []).filter((x) => x.id !== made.id);
          prefs = { ...prefs, dfmProfiles: [...rest, made] };
          savePrefs(prefs);
          setMessage(`Saved the rule set "${made.name}"`);
        },
        onDeleteProfile: (id) => {
          prefs = {
            ...prefs,
            dfmProfiles: (prefs.dfmProfiles ?? []).filter((x) => x.id !== id),
          };
          savePrefs(prefs);
          setMessage('Rule set deleted');
        },
        onApply: (next) => {
          // Onto the document, so the design carries the rules it was checked
          // against. An optional field, so a file written before this existed
          // reads exactly as it always did.
          history().begin(doc(), 'Manufacturing rules');
          doc().dfm = next;
          history().commit();
          documentChanged();
          dfmCommand(next);
        },
      });
    },
  });

  setMessage(report.errors > 0
    ? `DFM — ${report.errors} problem${report.errors === 1 ? '' : 's'}`
      + `, ${report.warnings} warning${report.warnings === 1 ? '' : 's'}`
    : report.warnings > 0
      ? `DFM — nothing fatal, ${report.warnings} warning${report.warnings === 1 ? '' : 's'}`
      : 'DFM — clean against every rule checked');
}

interface ScanIssue {
  at: { x: number; y: number };
  ids: number[];
  desc: string;
}

/** Flatten a connectivity report into an ordered issue list — shorts first
 *  (they break the board), then the unrouted gaps. */
function scanIssues(): ScanIssue[] {
  const d = doc();
  const rep = connectivityReport(d);
  const issues: ScanIssue[] = [];
  for (const s of rep.shorts) {
    issues.push({ at: s.at, ids: s.entityIds, desc: `Short: ${s.nets.join(' ✕ ')}` });
  }
  // Clearance breaches rank with the shorts: both are copper that will not
  // fabricate, and both want fixing before the unrouted gaps are worth looking
  // at. The tightest come first, which is how the checker orders them.
  for (const v of clearanceViolations(d)) {
    // formatDisplay already carries the unit, so it is not repeated here.
    const gap = v.actual <= 0 ? 'touching' : formatDisplay(v.actual, state.units);
    issues.push({
      at: v.at,
      ids: v.entityIds,
      desc: `Too close: ${v.nets[0]} ↔ ${v.nets[1]} (${gap}, rule `
        + `${formatDisplay(v.required, state.units)})`,
    });
  }
  for (const u of rep.unrouted) {
    const from = u.from ? `${u.from.ref}-${u.from.pin}` : '?';
    const to = u.to ? `${u.to.ref}-${u.to.pin}` : '?';
    issues.push({
      at: { x: (u.x1 + u.x2) / 2, y: (u.y1 + u.y2) / 2 },
      ids: [],
      desc: `Unrouted: ${u.net} (${from} → ${to})`,
    });
  }
  return issues;
}

/**
 * Nets ▸ Net Scan + / − — step through the design-rule errors one at a time,
 * the way the original pauses after each. Each stop centres the view on the
 * fault, selects the offending copper, and names it on the status line.
 */
function stepScan(dir: 1 | -1): void {
  if (doc().kind !== 'pcb') { setMessage('Net Scan is a board command'); return; }
  const issues = scanIssues();
  if (issues.length === 0) {
    scanIndex = -1;
    setMessage('Net Scan: no violations — board is clean');
    updateRatsnestStatus();
    draw();
    return;
  }
  scanIndex = scanIndex < 0 && dir < 0
    ? issues.length - 1
    : Math.min(issues.length - 1, Math.max(0, scanIndex + dir));
  const issue = issues[scanIndex]!;

  state.showRatsnest = true; // so an unrouted gap's ratline is visible
  resumeRatsnest();          // the view jump below must not hide what it found
  viewport.rememberView();
  viewport.fit(issue.at.x - 0.4, issue.at.y - 0.4, issue.at.x + 0.4, issue.at.y + 0.4);
  state.selected.clear();
  for (const id of issue.ids) state.selected.add(id);
  updateInfo();
  updateRatsnestStatus();
  updateStatus();
  draw();
  setMessage(`Net Scan ${scanIndex + 1}/${issues.length}: ${issue.desc}`);
}

/** Nets ▸ Net Scan Log — write the design-rule findings to a text file. */
function netScanLog(): void {
  if (doc().kind !== 'pcb') { setMessage('Net Scan is a board command'); return; }
  const rep = connectivityReport(doc());
  const lines: string[] = [
    `Net Scan log — ${projectName ?? doc().name}`,
    `Nets: ${rep.total}   Routed: ${rep.routed}   `
      + `Shorts: ${rep.shorts.length}   Unrouted gaps: ${rep.unrouted.length}`,
    '',
  ];
  if (rep.shorts.length) {
    lines.push('SHORTS');
    for (const s of rep.shorts) {
      lines.push(`  ${s.nets.join(' x ')} @ ${s.at.x.toFixed(3)},${s.at.y.toFixed(3)}`
        + `  [${s.nodes.map((n) => `${n.ref}-${n.pin}`).join(' ')}]`);
    }
    lines.push('');
  }
  if (rep.unrouted.length) {
    // Gathered under the net they belong to, rather than one line each.
    //
    // A board whose ground plane has been taken out reports a gap between
    // every pair of ground pins: on the one this was written for, 224 lines
    // for GND and 83 for VCC out of 308 — the same finding said three hundred
    // times, burying the one net that had a real fault. The net is named once,
    // with how much of it is adrift, and then a few of its gaps as examples.
    const PER_NET = 8;
    const byNet = new Map<string, typeof rep.unrouted>();
    for (const u of rep.unrouted) {
      const list = byNet.get(u.net) ?? [];
      list.push(u);
      byNet.set(u.net, list);
    }
    lines.push('UNROUTED');
    for (const [net, gaps] of [...byNet].sort((a, b) => b[1].length - a[1].length)) {
      lines.push(gaps.length === 1
        ? `  ${net}`
        : `  ${net} — ${gaps.length} gaps`);
      for (const u of gaps.slice(0, PER_NET)) {
        const from = u.from ? `${u.from.ref}-${u.from.pin}` : '?';
        const to = u.to ? `${u.to.ref}-${u.to.pin}` : '?';
        lines.push(`      ${from} -> ${to}`);
      }
      if (gaps.length > PER_NET) {
        lines.push(`      … and ${gaps.length - PER_NET} more`);
      }
    }
    lines.push('');
  }
  if (!rep.shorts.length && !rep.unrouted.length) lines.push('No violations — board is clean.');
  lines.push('END OF FILE', '');

  const name = `${(projectName ?? 'netscan').replace(/[^\w.-]+/g, '_')}.LOG`;
  const blob = new Blob([lines.join('\r\n')], { type: 'text/plain' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.append(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
  setMessage(`Wrote ${name} — ${rep.shorts.length} shorts, ${rep.unrouted.length} unrouted`);
}

/**
 * A pattern name, from the stock libraries or from a board we have read.
 *
 * The same pair Net List In consults, in the same order, so the check and the
 * transfer never disagree about what can be found.
 */
/** The curated footprints, as the suggester wants them. Built once. */
const CATALOGUE: FootprintCandidate[] = CURATED_ENTRIES
  .filter((e) => e.pins !== undefined)
  .map((e) => ({
    name: e.name, description: e.description, library: e.library,
    pins: e.pins ?? null, aliases: e.aliases ?? [],
    family: e.family ?? null, smt: e.smt ?? null,
  }));

/**
 * What a package could be, best first.
 *
 * The catalogue is static data compiled in, so the suggestions are the same in
 * development and in production and do not wait on a round trip to the server.
 */
function suggestPatterns(need: { part: string; pins: number }): {
  name: string; description: string; library: string; why: string;
}[] {
  return suggestFootprints(need, CATALOGUE).map((s) => ({
    name: s.name, description: s.description, library: s.library, why: s.why,
  }));
}

/** The PCB libraries by keyword, plus whatever this board already uses. */
async function searchPatterns(query: string): Promise<{
  id: string; name: string; description: string; library: string;
}[]> {
  const hits = await parts.search(query);
  const out = hits
    .filter((p) => p.kind === 'pcb')
    .map((p) => ({
      id: p.id, name: p.name, description: p.description, library: p.library,
    }));
  // Footprints harvested off an imported board are not in the catalogue the
  // search reads, and are very often exactly the ones wanted — the sheet and
  // the board came from the same design.
  const q = query.trim().toUpperCase();
  for (const name of footprints.names()) {
    if (!name.toUpperCase().includes(q)) continue;
    if (out.some((o) => o.name.toUpperCase() === name.toUpperCase())) continue;
    out.push({ id: `local:${name}`, name, description: '', library: 'From this board' });
  }
  return out;
}

/**
 * The libraries, for one part, from the part itself.
 *
 * The edit dialog has always had a Footprint box and the box was no help: it
 * wanted the library's own spelling of a package name, and the only place that
 * offered the spellings was a report over the whole design. This is the same
 * suggestions and the same search, reached from the part you are pointing at.
 */
async function pickFootprintFor(about: {
  part: string; ref: string; pins: number; current: string;
}): Promise<string | null> {
  footprints.harvest(state.docs.pcb);

  /**
   * How many pins the *package* brings — the strongest clue there is.
   *
   * On a board the part's own pads are the package's, so what the dialog
   * counted is already right. On a sheet it is not: a 7404's U1a carries two
   * of the package's fourteen, and offering two-pin packages for a hex
   * inverter is worse than offering none. So the sheets are asked, and where
   * they cannot say — a gate still carrying a `?` has no package yet — the
   * count is dropped rather than guessed, and the suggestions fall back to
   * what the part is called.
   */
  const pins = doc().kind === 'pcb'
    ? about.pins
    : footprintNeeds(netlistSheets()).find((n) => n.ref === about.ref)?.pins ?? 0;

  return showFootprintPicker({
    ref: about.ref,
    part: about.part,
    pins,
    current: about.current,
    suggest: suggestPatterns,
    searchFootprints: searchPatterns,
    hint: doc().kind === 'pcb'
      ? 'Choosing one fits it to the part on the board, where the part was put.'
      : 'Choosing one names the pattern on the symbol and changes nothing else.',
  });
}

async function resolveFootprint(name: string): Promise<ComponentEntity | null> {
  // One implementation, in core, so the Footprint Check and the netlist
  // transfer cannot disagree about what a pattern name means. They did: this
  // knew the catalogue's aliases and the transfer's own copy did not.
  return resolvePattern(name,
    async (n) => (await parts.findFootprint(n)) ?? footprints.find(n) ?? null);
}

/**
 * Nets ▸ Footprint Check — every part on the sheet, and the board part it
 * becomes.
 *
 * Opened by hand, and offered on its own the moment a sheet is loaded that has
 * parts with nowhere to go. Matching one writes the pattern's name onto the
 * symbol and nothing else, so it cannot disturb a net on either drawing.
 */
function footprintCheckCommand(): void {
  const design = netlistSheets();
  if (design.length === 0 || design[0]!.kind !== 'sch') {
    setMessage('Footprint Check reads a schematic — open or switch to a sheet');
    return;
  }
  footprints.harvest(state.docs.pcb);
  showFootprintDialog({
    sheets: design,
    findFootprint: resolveFootprint,
    suggest: suggestPatterns,
    searchFootprints: searchPatterns,
    onChange(label) {
      // A package's gates can be on any sheet, so a match may have written to
      // one that is not in front. Clearing both stacks is what the netlist
      // panel does for the same reason: an undo entry recorded against the
      // sheet on screen cannot describe a change made to another one.
      histories.sch.clear();
      documentChanged();
      setMessage(label);
      draw();
    },
  });
}

/**
 * Check a freshly loaded sheet for parts with no board part to become.
 *
 * Quiet when there is nothing to say. When there is, the dialog opens itself:
 * this is the one moment the answer is cheap — before anything has been placed,
 * routed or netlisted — and a line in the status bar is a line nobody reads
 * until the transfer has already placed nothing.
 */
async function checkFootprintsAfterLoad(): Promise<void> {
  const design = netlistSheets();
  if (design.length === 0 || design[0]!.kind !== 'sch') return;
  if (design.every((d) => d.entities.length === 0)) return;
  try {
    const checks = await checkFootprints(design, resolveFootprint);
    const bad = checks.filter((c) => c.status === 'unresolved');
    if (bad.length === 0) return;
    setMessage(`${bad.length} of ${checks.length} packages have no pattern to become — `
      + `${bad.slice(0, 6).map((c) => c.ref).join(', ')}${bad.length > 6 ? '…' : ''}`);
    footprintCheckCommand();
  } catch {
    // A library that cannot be reached is not this command's problem to report:
    // whatever needed it will say so when it is asked for directly.
  }
}

async function openNetlistCommand(): Promise<void> {
  // The board on screen may itself carry footprints worth reusing.
  footprints.harvest(state.docs.pcb);
  const { showNetlistDialog } = await import('./ui/netlist.ts');
  showNetlistDialog({
    sheets: netlistSheets(),
    board: state.docs.pcb,
    // With no schematic the nets are read off the board itself, so the commands
    // that push a schematic onto a board have nothing to do.
    sourceIsBoard: netlistFromBoard(),
    parts,
    findFootprint: resolveFootprint,
    onChange(label) {
      histories.sch.clear();
      histories.pcb.clear();
      documentChanged();
      // Loading the netlist onto the board fills a document that may never have
      // been opened, so it would have had no button to reach it by. Anything on
      // the board means the board is open.
      if (!boardOpen && state.docs.pcb.entities.length > 0) {
        boardOpen = true;
        renderDocTabs();
      }
      setMessage(label);
      buildLayerPanel();
      updateInfo();
      updateStatus();
      draw();
    },
    onShowBoard: () => {
      boardOpen = true;
      switchMode('pcb');
      renderDocTabs();
    },
    onFootprintCheck: () => footprintCheckCommand(),
  });
}

function openProjectsCommand(): void {
  if (!user) {
    showAccountDialog((u) => {
      user = u;
      renderAccount();
      updateSaveButton();
      showProjectDialog(openProject, () => void runImport());
    });
    return;
  }
  showProjectDialog(openProject, () => void runImport());
}

/** Send a generated file to the browser's downloads. */
function downloadText(name: string, text: string, type = 'text/plain'): void {
  const blob = new Blob([text], { type });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.append(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

/** A filename stem for generated output, from the project or document name. */
function outputStem(): string {
  return (projectName ?? doc().name ?? 'sircad').replace(/\.[^.]+$/, '')
    .replace(/[^\w.-]+/g, '_') || 'sircad';
}

/**
 * Render the drawing to an offscreen canvas for paper or a picture file.
 *
 * Plots use each layer's *print* colour on white and drop the editor's state —
 * no selection, no ratsnest, no grid — because none of that is part of the
 * drawing. The board is framed rather than the sheet, so a plot is of the thing
 * being made.
 */
function plotToCanvas(scale = 2): HTMLCanvasElement {
  const d = doc();
  const out = document.createElement('canvas');
  const w = Math.max(320, Math.round(canvasRect.width || 1000));
  const h = Math.max(240, Math.round(canvasRect.height || 700));
  const vp = new Viewport();
  vp.width = w;
  vp.height = h;
  vp.dpr = scale;
  out.width = Math.round(w * scale);
  out.height = Math.round(h * scale);

  const r = new Renderer(out, vp);
  const { rect } = boardExtent(d);
  if (rect) vp.fit(rect.x1, rect.y1, rect.x2, rect.y2);
  else { vp.cx = viewport.cx; vp.cy = viewport.cy; vp.scale = viewport.scale; }

  r.render(d, { printColors: true, showGrid: false, activeLayer: state.activeLayer });
  return out;
}

/**
 * File ▸ Image Save — the drawing as a PNG.
 *
 * `toDataURL` rather than `toBlob`: the callback form is deferred while the
 * document is hidden, so a save started in a background tab would quietly never
 * happen. The synchronous form always produces the image.
 */
function imageSaveCommand(): void {
  const name = `${outputStem()}.png`;
  const url = plotToCanvas(2).toDataURL('image/png');
  if (url.length < 100) { setMessage('Could not render the image'); return; }
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.append(a);
  a.click();
  a.remove();
  setMessage(`Wrote ${name}`);
}

/**
 * File ▸ Print — hand the plot to the browser's print dialog.
 *
 * The plot rather than the page: the app's chrome, panels and dark desktop have
 * no business on paper.
 *
 * A frame, not a window. Opening one had two faults and either was enough to
 * make the command do nothing while reporting success. A pop-up is blocked by
 * default in most browsers, so the usual outcome was a line about allowing
 * pop-ups for a command that has nothing to do with them. And where it *was*
 * allowed, the print was hung on the new window's `load` event — which
 * `document.close()` has usually already fired by the time there is a listener
 * to hear it, so the window opened, showed the plot, and never printed.
 *
 * Hanging it on the image instead settles both: a frame needs no permission,
 * and an image that is already decoded says so with `complete` rather than
 * leaving a listener waiting for an event that has been and gone.
 */
function printCommand(): void {
  const img = plotToCanvas(3).toDataURL('image/png');
  // A canvas that could not be drawn hands back a stub. Printing it would put
  // a blank sheet through the dialog and look like a fault in the printer.
  if (img.length < 100) { setMessage('Could not render the drawing to print'); return; }

  const title = `${outputStem()} — ${doc().name}`;
  const frame = document.createElement('iframe');
  // Off the page rather than hidden: `display:none` in some browsers means the
  // frame never lays out, and a frame with no layout prints an empty page.
  frame.setAttribute('aria-hidden', 'true');
  frame.style.position = 'fixed';
  frame.style.left = '-10000px';
  frame.style.top = '0';
  frame.style.width = '1px';
  frame.style.height = '1px';
  frame.style.border = '0';
  document.body.append(frame);

  const done = (): void => { frame.remove(); };

  const inner = frame.contentWindow;
  const idoc = frame.contentDocument ?? inner?.document;
  if (!inner || !idoc) { done(); setMessage('Could not open a page to print'); return; }

  idoc.open();
  idoc.write(
    `<!doctype html><title>${escapeHtml(title)}</title>`
    + '<style>@page{margin:10mm}html,body{margin:0;padding:0}'
    + 'img{width:100%;height:auto;display:block}</style>'
    + `<img src="${img}" alt="${escapeHtml(title)}">`,
  );
  idoc.close();

  let sent = false;
  const send = (): void => {
    if (sent) return;
    sent = true;
    // Before printing, not after: `print` blocks until the dialog is dismissed
    // in most browsers, so a listener added afterwards has already missed the
    // event it is waiting for. That is the same mistake this whole command was
    // rewritten to stop making.
    //
    // Removing the frame while the browser is still reading it prints nothing,
    // so it goes on `afterprint` — with a timer as well, because that event
    // does not fire everywhere.
    inner.addEventListener?.('afterprint', done);
    window.setTimeout(done, 60_000);
    inner.focus();
    inner.print();
  };

  // Wait for the picture, but never *only* for the picture. An image is
  // supposed to say whether it has finished with `complete`, and one written
  // into a frame may answer neither true nor false; hanging the print on that
  // alone is how a command ends up doing nothing at all. The guard above makes
  // three ways of arriving safe, so all three are used.
  const picture = idoc.querySelector('img');
  picture?.addEventListener('load', send);
  if (picture?.complete) send();
  window.setTimeout(send, 400);

  setMessage('Sent to print');
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"]/g, (c) => (
    { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c] ?? c));
}

/**
 * Whether a block is a real part rather than drawing furniture.
 *
 * `$$` blocks are the original's own symbols — ground, power, signal ports, the
 * title block — and there are usually far more of them than parts: on a typical
 * sheet two thirds of the components are `$$SIG`, `$$GND` or `$$PWR`. Anything
 * that wants "the parts" rather than "everything placed" filters on this.
 */
function isPhysicalPart(e: Entity): e is ComponentEntity {
  if (e.kind !== 'component') return false;
  if (e.name.startsWith('$$')) return false;
  const ref = refDesOf(e);
  return ref !== '' && !ref.startsWith('$$');
}

/**
 * File ▸ BOM Out — the bill of materials.
 *
 * Identical parts are one line: same part, value and footprint means same thing
 * to buy, however many of them the board carries. Written as CSV because that
 * is what a purchasing spreadsheet wants.
 */
function bomCommand(): void {
  const rows = new Map<string, { part: string; value: string; footprint: string; refs: string[] }>();

  // Every sheet, because a bill of materials is of the design and not of the
  // page you happen to be looking at. Counting by package designator is what
  // makes that safe: a chip whose gates are drawn on three sheets is still one
  // `U1` to buy.
  for (const src of netlistSheets()) {
    for (const e of src.entities) {
      if (!isPhysicalPart(e)) continue;
      const gate = refDesOf(e);
      // A quad comparator placed as four gates is still one chip to buy, so the
      // designator is reduced to its package: U1a…U1d all count as U1, once.
      const ref = packageOf(gate);
      const value = (e.params[2] ?? '').trim();
      const footprint = (e.footprint ?? '').trim();
      const key = `${e.name}${KEY_SEP}${value}${KEY_SEP}${footprint}`;
      const row = rows.get(key) ?? { part: e.name, value, footprint, refs: [] };
      if (!row.refs.includes(ref)) row.refs.push(ref);
      rows.set(key, row);
    }
  }

  if (rows.size === 0) {
    setMessage('No parts to list — place some components first');
    return;
  }

  const csv = (v: string): string => (/[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v);
  const byRef = (a: string, b: string): number =>
    a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });

  const lines = ['Item,Qty,References,Part,Value,Footprint'];
  const sorted = [...rows.values()].sort(
    (a, b) => byRef(a.refs[0] ?? '', b.refs[0] ?? ''));
  sorted.forEach((r, i) => {
    const refs = [...r.refs].sort(byRef);
    lines.push([
      String(i + 1), String(refs.length), csv(refs.join(' ')),
      csv(r.part), csv(r.value), csv(r.footprint),
    ].join(','));
  });

  const total = sorted.reduce((n, r) => n + r.refs.length, 0);
  // Opened in the editor rather than dropped straight into downloads: it is a
  // file you want to read — and correct — and Save is a button away.
  openReportTab('bom', 'BOM', `${outputStem()}.BOM.csv`, 'text/csv',
    `${lines.join('\r\n')}\r\n`,
    `${sorted.length} line${sorted.length === 1 ? '' : 's'}, ${total} parts`);
  setMessage(`BOM — ${sorted.length} line${sorted.length === 1 ? '' : 's'}, ${total} parts`);
}

/** Net List Out — the original's pipe-delimited node list. */
function exportNetlistCommand(): void {
  const fromBoard = netlistFromBoard();
  const design = netlistSheets();
  const nl = extractDesignNetlist(design);
  const real = nl.nets.filter((n) => n.pins.length > 1);
  if (real.length === 0) {
    setMessage(fromBoard
      ? 'No nets to write — the board has no connected pads'
      : 'No nets to write — wire the schematic first');
    return;
  }
  const lines = [
    `Contents: ${fromBoard ? 'Board' : 'Schematic'} Net List`,
    `Source: ${projectName ?? design[0]?.name ?? 'sircad'}`,
    ...(nl.sheets > 1 ? [`Sheets: ${nl.sheets}`] : []),
    `Date: ${new Date().toISOString().slice(0, 10)}`,
    '',
    '*NET*',
    '|Signal|Nodes',
  ];
  for (const net of real) {
    // Nodes are RefID-Pin, the original's CIRCAD-format separator. The RefID is
    // the *package*: the board has one chip whichever gate of it a wire reached,
    // and U1a-3 and U1-3 are the same physical pin. A netlist naming gates would
    // ask the board for parts that do not exist.
    const nodes = [...new Set(net.pins.map((p) => `${packageOf(p.ref)}-${p.pin}`))];
    lines.push(`|${net.name}|${nodes.join(' ')}`);
  }
  lines.push('', 'END OF FILE', '');

  const name = `${(projectName ?? 'netlist').replace(/[^\w.-]+/g, '_')}.NET`;
  openReportTab('netlist', 'Net List', name, 'text/plain', `${lines.join('\r\n')}\r\n`,
    `${real.length} net${real.length === 1 ? '' : 's'} from the ${fromBoard ? 'board' : 'schematic'}`);
  setMessage(`Net List — ${real.length} nets`);
}

/**
 * The net under a click: the primitive there and every other primitive joined
 * to it. Clicking a bare trace or wire resolves directly; clicking a component
 * picks the net of its nearest pin, so hitting a footprint pad works too.
 */
function netAtPoint(w: { x: number; y: number }): { name: string; net: Net } | null {
  const hit = hitTest(doc(), w.x, w.y, doc().grid.current || 0.01);
  if (!hit) return null;
  const byId = new Map<number, Net>();
  for (const net of extractNetlist(doc()).nets) {
    for (const id of net.entityIds) byId.set(id, net);
  }
  let net = byId.get(hit.id);
  if (!net && hit.kind === 'component') {
    let best = Infinity;
    for (const c of hit.children) {
      if (c.kind !== 'pad') continue;
      const p = placeInComponent({ x: c.x, y: c.y }, hit);
      const d = Math.hypot(p.x - w.x, p.y - w.y);
      const n = byId.get(c.id);
      if (n && d < best) { best = d; net = n; }
    }
  }
  return net ? { name: net.name, net } : null;
}

/** Nets ▸ Highlight Net — click a net to light every trace on it in cyan. */
/**
 * Every net on the board, to pick one from.
 *
 * Reached by running Highlight Net and clicking where there is no copper —
 * the same fallback the Edit-by-kind commands use, and for the same reason:
 * a net you cannot find is a net you cannot click. Typing filters the list,
 * so a board with three hundred nets is still a couple of keystrokes.
 *
 * Choosing one puts the view on it as well as lighting it up. Highlighting
 * something off screen tells you nothing.
 */
function chooseNetToHighlight(): void {
  const d = doc();
  const nets = extractNetlist(d).nets
    .filter((n) => n.entityIds.length > 0)
    .sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }));
  if (nets.length === 0) { setMessage('Highlight Net — this board has no nets'); return; }

  const box = (n: (typeof nets)[number]): { x1: number; y1: number; x2: number; y2: number } | null => {
    let x1 = Infinity; let y1 = Infinity; let x2 = -Infinity; let y2 = -Infinity;
    const ids = new Set(n.entityIds);
    const walk = (list: Entity[], owner: ComponentEntity | null): void => {
      for (const e of list) {
        if (e.kind === 'component') { walk(e.children, e); continue; }
        if (!ids.has(e.id)) continue;
        const b = entityExtent(e);
        if (!b) continue;
        const off = owner ? { x: owner.x, y: owner.y } : { x: 0, y: 0 };
        x1 = Math.min(x1, b.x1 + off.x); x2 = Math.max(x2, b.x2 + off.x);
        y1 = Math.min(y1, b.y1 + off.y); y2 = Math.max(y2, b.y2 + off.y);
      }
    };
    walk(d.entities, null);
    return Number.isFinite(x1) ? { x1, y1, x2, y2 } : null;
  };

  openDialog({
    title: 'Highlight Net',
    build(body, handle) {
      const find = document.createElement('input');
      find.type = 'search';
      find.placeholder = `Search ${nets.length} nets…`;
      find.autocomplete = 'off';
      body.append(find);

      const list = document.createElement('div');
      list.className = 'pick-list';
      body.append(list);

      const pick = (n: (typeof nets)[number]): void => {
        handle.close();
        highlightNet = { name: n.name, ids: new Set(n.entityIds) };
        const b = box(n);
        if (b) {
          viewport.rememberView();
          const pad = 0.3;
          viewport.fit(b.x1 - pad, b.y1 - pad, b.x2 + pad, b.y2 + pad);
        }
        updateStatus();
        draw();
        const gaps = n.groups.length - 1;
        setMessage(`Highlighting ${n.name} — ${n.entityIds.length} objects`
          + (gaps > 0 ? ` · ${gaps} unrouted gap${gaps === 1 ? '' : 's'}` : ' · routed'));
      };

      const paint = (): void => {
        const q = find.value.trim().toLowerCase();
        const shown = q === '' ? nets : nets.filter((n) => n.name.toLowerCase().includes(q));
        list.replaceChildren();
        for (const n of shown) {
          const row = document.createElement('button');
          row.type = 'button';
          row.className = 'pick-row';
          const what = document.createElement('span');
          what.textContent = n.name + (n.named ? '' : '  (unnamed)');
          const where = document.createElement('span');
          where.className = 'pick-where';
          // How many pins, and whether copper actually joins them all: one
          // group means routed, more means that many gaps still open.
          const gaps = n.groups.length - 1;
          where.textContent = `${n.pins.length} pin${n.pins.length === 1 ? '' : 's'}`
            + (gaps > 0 ? ` · ${gaps} gap${gaps === 1 ? '' : 's'}` : '');
          if (gaps > 0) row.classList.add('unrouted');
          row.append(what, where);
          row.addEventListener('click', () => pick(n));
          list.append(row);
        }
        if (shown.length === 0) {
          const none = document.createElement('p');
          none.className = 'modal-hint';
          none.textContent = 'No net of that name';
          list.append(none);
        }
      };

      find.addEventListener('input', paint);
      // Enter takes the first match, so a name can simply be typed.
      find.addEventListener('keydown', (ev) => {
        if (ev.key !== 'Enter') return;
        ev.preventDefault();
        const q = find.value.trim().toLowerCase();
        const hit = nets.find((n) => n.name.toLowerCase() === q)
          ?? nets.find((n) => n.name.toLowerCase().includes(q));
        if (hit) pick(hit);
      });
      paint();
      queueMicrotask(() => find.focus());
    },
  });
}

function highlightNetCommand(): void {
  cancelPlace();
  toolbox.cancel();
  clickMode = {
    label: 'Highlight Net',
    run: (w) => {
      const found = netAtPoint(w);
      if (!found) { chooseNetToHighlight(); return; }
      highlightNet = { name: found.name, ids: new Set(found.net.entityIds) };
      updateStatus();
      draw();
      setMessage(`Highlighting ${found.name} — ${found.net.entityIds.length} objects`);
    },
    // +/- walk the nets rather than the layer stack, so you can page through
    // the design's connectivity without hunting for something to click.
    step: (dir) => {
      const nets = extractNetlist(doc()).nets.filter((n) => n.entityIds.length > 0);
      if (nets.length === 0) { setMessage('No nets to highlight'); return; }
      const at = highlightNet ? nets.findIndex((n) => n.name === highlightNet!.name) : -1;
      const next = ((at + dir) % nets.length + nets.length) % nets.length;
      const n = nets[next]!;
      highlightNet = { name: n.name, ids: new Set(n.entityIds) };
      updateStatus();
      draw();
      setMessage(`Highlighting ${n.name} (${next + 1}/${nets.length}) `
        + `— ${n.entityIds.length} objects`);
    },
  };
  setMessage('Highlight Net — click a net, or +/- to step through them, Esc to stop');
  updateStatus();
}

/** The drawing's ratnest layer, found the way the renderer finds it. */
function ratnestLayer(d: CadDocument): number | null {
  const at = d.layers.findIndex((l) => /ratnest|ratsnest/i.test(l.name));
  return at >= 0 ? at : null;
}

/**
 * The ratnest layer, made if the drawing has not got one.
 *
 * The original does exactly this before it draws a single ratline — "ensure the
 * existence of rat layer", `if (!layernumber(prf,65535)) layername(prf,7,-1,0,
 * "Ratnest")` (OGnet1.cpp:746) — and its `layername` knows the name: a Drawing
 * layer, yellow on screen and cyan in print, whatever colours the caller asked
 * for (OGxyz11.cpp:1688). So the values here are its values.
 *
 * Refusing instead, which is what this did, put the operator in an odd place:
 * the command that needs the layer told them to go and make one by hand, and
 * gave no clue what type or colour it wanted.
 */
function ensureRatnestLayer(d: CadDocument): number {
  const found = ratnestLayer(d);
  if (found !== null) return found;

  const num = d.layers.length;
  d.layers.push({
    num,
    name: 'Ratnest',
    type: LayerType.Drawing,
    bottom: false,
    screen: 0xffff00,
    print: 0x00ffff,
    screenNegative: false,
    printNegative: false,
    visible: true,
    locked: false,
  });
  // A layer is part of the file, so the file has changed. Not undoable: the
  // history snapshots entities, and an empty layer left behind by an undone
  // ratline is harmless where a dangling layer index would not be.
  documentChanged();
  buildLayerPanel();
  updateStatus();
  return num;
}

/**
 * Nets ▸ Rat Line (N L) — one net's connections, drawn.
 *
 * The original: "place the cursor over one of the pads in the desired net. If a
 * signal net already has ratlines, they will be removed. If the net has no
 * ratlines, a full set will be created." So it is a toggle, per net, and the
 * lines are real objects on the Ratnest layer — not the whole-board ratsnest
 * overlay, which is computed every frame and cannot be turned on for one net
 * alone. That is the point of it during manual routing: you want *this* net's
 * guides on screen and nothing else's.
 *
 * A persistent click mode, so a run of nets can be lit up one after another,
 * and the view keys keep working throughout — c centres, i and o zoom, the
 * arrows walk the crosshair. Laying out a long net is exactly when you need to
 * zoom out, find the far pad, and come back.
 */
function ratLineCommand(): void {
  // Not board-only: the original rat-nests whatever file is open, and a sheet
  // has component pads carrying signals just as a board does.
  cancelPlace();
  toolbox.cancel();
  clickMode = {
    label: 'Rat Line',
    run: (w) => toggleRatLines(w),
  };
  setMessage('Rat Line — click a net to draw its connections, click again to remove them, '
    + 'Esc to stop');
  updateStatus();
}

/**
 * The net of a ratline under the cursor, if the cursor is on one.
 *
 * Its own scan rather than the general pick, because picking is scoped to
 * visible and active layers and a guide the user can see must be clickable
 * whatever that scope says.
 */
function ratLineAt(
  d: CadDocument, layer: number, w: { x: number; y: number },
): string | undefined {
  const tol = Math.max(d.grid.current || 0.01, 0.02);
  for (const e of d.entities) {
    if (e.kind !== 'line' || e.layer !== layer || !e.signal) continue;
    const dx = e.x2 - e.x1;
    const dy = e.y2 - e.y1;
    const len = dx * dx + dy * dy;
    let t = len === 0 ? 0 : ((w.x - e.x1) * dx + (w.y - e.y1) * dy) / len;
    t = Math.max(0, Math.min(1, t));
    if (Math.hypot(w.x - (e.x1 + t * dx), w.y - (e.y1 + t * dy)) <= tol) return e.signal;
  }
  return undefined;
}

/**
 * Nets ▸ Un-ratnest (N U) — take every drawn ratline off the board.
 *
 * "Clear and remove all lines and arcs from the Ratnest layer", which is the
 * whole layer and not one net's worth. Returns how many went.
 */
function clearRatnestLayer(): number {
  const d = doc();
  const layer = ratnestLayer(d);
  if (layer === null) return 0;
  const doomed = d.entities.filter(
    (e) => (e.kind === 'line' || e.kind === 'arc') && e.layer === layer);
  if (doomed.length === 0) return 0;
  history().begin(d, 'Un-ratnest');
  const ids = new Set(doomed.map((e) => e.id));
  for (let i = d.entities.length - 1; i >= 0; i--) {
    if (ids.has(d.entities[i]!.id)) d.entities.splice(i, 1);
  }
  history().commit();
  documentChanged();
  invalidateSnap();
  return doomed.length;
}

/** Where P - is up to: the first pad, and the net the pair is to carry. */
let ratPair: { x: number; y: number; signal: string } | null = null;

/**
 * Place ▸ Rat-line (P -) — one connection, by hand, between two pads.
 *
 * The original: "construct a rat-line between any two pads. When you click on
 * the first point… a dialog box will appear which will allow you to enter the
 * target signal name."
 *
 * Naming it *is* the netlist edit. CIRCAD keeps no separate connectivity
 * table — a net is the set of primitives carrying that `Signal:` — so the name
 * put on this line is what joins the two pads as far as everything downstream
 * is concerned. That is why the name is asked for on the first click rather
 * than treated as a label afterwards.
 *
 * The name offered is the one the first pad already carries, since that is
 * nearly always the answer and typing it again is a chance to mistype it.
 */
function placeRatLineCommand(): void {
  const layer = ensureRatnestLayer(doc());
  cancelPlace();
  toolbox.cancel();
  ratPair = null;
  clickMode = {
    label: 'Place Rat-line',
    run: (w) => runRatPair(w, layer),
  };
  setMessage('Place Rat-line — click the first pad, Esc to stop');
  updateStatus();
}

/** One click of P -: the first pad names the net, the second finishes the line. */
function runRatPair(w: { x: number; y: number }, layer: number): void {
  const d = doc();
  // The pad's exact centre, so the ratline lands on it rather than near it —
  // the original offers this as a Query Items setting and it is the only
  // sensible behaviour for a connection.
  const hit = snapPoint(d, w.x, w.y, {
    tolerance: SNAP_PIXELS / viewport.scale,
    grid: activeGrid(),
    padsOnly: true,
    candidates: snapCandidateCache(),
  });
  const at = { x: hit.x, y: hit.y };

  if (!ratPair) {
    const suggested = hit.signal ?? '';
    const asked = window.prompt('Rat-line signal name:', suggested);
    if (asked === null) { setMessage('Place Rat-line cancelled'); return; }
    const signal = asked.trim();
    if (!signal) { setMessage('A rat-line needs a signal name'); return; }
    ratPair = { x: at.x, y: at.y, signal };
    setMessage(`Place Rat-line on ${signal} — click the second pad`);
    return;
  }

  if (Math.hypot(at.x - ratPair.x, at.y - ratPair.y) < 1e-9) {
    setMessage('Pick a different pad for the other end');
    return;
  }

  history().begin(d, 'Place Rat-line');
  d.entities.push({
    kind: 'line', id: newId(), layer,
    width: d.netScan?.ratLine || 0.008,
    x1: ratPair.x, y1: ratPair.y, x2: at.x, y2: at.y,
    signal: ratPair.signal,
  });
  history().commit();
  const signal = ratPair.signal;
  ratPair = null;
  documentChanged();
  invalidateSnap();
  draw();
  // Armed for the next one, as every persistent mode is.
  setMessage(`Rat-line on ${signal} — click the first pad of the next, Esc to stop`);
}

/** Draw one net's ratlines, or take them away if they are already there. */
function toggleRatLines(w: { x: number; y: number }): void {
  const d = doc();
  const layer = ensureRatnestLayer(d);

  // A ratline already drawn is what the cursor lands on next time, and it sits
  // on a Drawing layer, so it belongs to no net and `netAtPoint` cannot see it.
  // Without this the toggle only ever worked one way: the guides went down and
  // there was no way to click them off again.
  const name = ratLineAt(d, layer, w) ?? netAtPoint(w)?.name;
  if (name === undefined) {
    setMessage('No net there — click a pad or a track on the net you want');
    return;
  }

  const mine = d.entities.filter(
    (e) => e.kind === 'line' && e.layer === layer && e.signal === name);

  if (mine.length > 0) {
    history().begin(d, 'Rat Line');
    const ids = new Set(mine.map((e) => e.id));
    for (let i = d.entities.length - 1; i >= 0; i--) {
      if (ids.has(d.entities[i]!.id)) d.entities.splice(i, 1);
    }
    history().commit();
    documentChanged();
    invalidateSnap();
    draw();
    setMessage(`Removed ${mine.length} ratline${mine.length === 1 ? '' : 's'} from ${name}`);
    return;
  }

  // The same spanning tree the ratsnest overlay draws, for this net alone —
  // and already minus whatever copper has joined, so a routed pair gets no
  // line and the guides thin out as the work is done.
  const wanted = buildRatsnest(d).filter((r) => r.net === name);
  if (wanted.length === 0) {
    setMessage(`${name} is fully routed — nothing to draw`);
    return;
  }

  // The file's own ratnest line size, as Setup ▸ File Parameters sets it.
  const width = d.netScan?.ratLine || 0.008;
  history().begin(d, 'Rat Line');
  for (const r of wanted) {
    d.entities.push({
      kind: 'line', id: newId(), layer, width,
      x1: r.x1, y1: r.y1, x2: r.x2, y2: r.y2,
      signal: name,
    });
  }
  history().commit();
  documentChanged();
  invalidateSnap();
  draw();
  setMessage(`${wanted.length} ratline${wanted.length === 1 ? '' : 's'} on ${name}`);
}

/** Nets ▸ Clear Highlights — drop any highlighted net. */
function clearHighlightsCommand(): void {
  highlightNet = null;
  clickMode = null;
  updateStatus();
  draw();
  setMessage('Highlights cleared');
}

/** Nets ▸ Name Net — click a net and give it (and everything on it) a name. */
function nameNetCommand(): void {
  cancelPlace();
  toolbox.cancel();
  clickMode = {
    label: 'Name Net',
    run: (w) => {
      const found = netAtPoint(w);
      if (!found) { setMessage('Name Net — click a track, wire or pad'); return; }
      const suggested = found.net.named ? found.name : '';
      const name = window.prompt(
        `Name for this net (${found.net.entityIds.length} objects):`, suggested);
      if (name === null) return;
      const trimmed = name.trim();
      if (!trimmed) return;
      const ids = new Set(found.net.entityIds);
      history().begin(doc(), 'Name net');
      applyNetName(ids, trimmed);
      history().commit();
      documentChanged();
      if (highlightNet && highlightNet.name === found.name) {
        highlightNet = { name: trimmed, ids };
      }
      updateInfo();
      updateStatus();
      draw();
      setMessage(`Named net ${trimmed}`);
    },
  };
  setMessage('Name Net — click a net to name it, Esc to stop');
  updateStatus();
}

/** Set the signal on every conductive primitive whose id is in `ids`. */
function applyNetName(ids: ReadonlySet<number>, name: string): void {
  const walk = (list: Entity[]): void => {
    for (const e of list) {
      if (ids.has(e.id)
        && (e.kind === 'line' || e.kind === 'arc' || e.kind === 'pad' || e.kind === 'fill')) {
        e.signal = name;
      }
      if (e.kind === 'component') walk(e.children);
    }
  };
  walk(doc().entities);
}

// --- Canvas interaction -----------------------------------------------------

let panning = false;
let panX = 0;
let panY = 0;

function worldAt(ev: MouseEvent): { x: number; y: number } {
  return viewport.toWorld(ev.clientX - canvasRect.left, ev.clientY - canvasRect.top);
}

/**
 * The point a mouse event stands for — grid-stepped while the crosshair is up.
 *
 * The original does this in its mouse handler and not in the tools: with the
 * graphics cursor showing, it takes the mouse's movement, quantises it to the
 * grid, and keeps the leftover as an alignment error for next time, so the
 * crosshair only ever stands on a grid intersection however finely the mouse is
 * pushed. Absolute rounding here rather than that running remainder — the same
 * result, without the crosshair drifting away from the pointer it stands in for.
 *
 * It matters beyond neatness. The readout said .1234 while Grid Snap said .1,
 * and everything taken from the raw cursor — the status line, a block corner,
 * where a carried item is put down — was off the grid the rest of the drawing
 * is on. The arrow keys always stepped by grid; the mouse now does too, which
 * is the whole of the original's crosshair mode.
 *
 * With the plain pointer up it does nothing: there the mouse is a pointer, and
 * the tools snap whatever they place.
 */
function cursorAt(ev: MouseEvent): { x: number; y: number } {
  const w = worldAt(ev);
  const g = activeGrid();
  if (g <= 0 || !(gridMode || isDrawing())) return w;
  return { x: snap(w.x, g), y: snap(w.y, g) };
}

// Anything that can move the canvas without resizing it refreshes the cache.
window.addEventListener('scroll', refreshCanvasRect, { passive: true });

/**
 * Whether a click means "zoom" rather than anything else.
 *
 * The manual allows mouse-button zoom only "when there is no function in
 * effect", which is the whole reason it can coexist with drawing: a command
 * waiting for a point always gets the click first. Selection counts too, so a
 * click that lands on an object picks it up as usual and only empty space
 * zooms.
 *
 * Carrying something is a function in effect. Without that clause the click
 * that puts a grabbed item — or a whole block — down on bare board zoomed in
 * instead, and the thing stayed stuck to the cursor.
 *
 * So is a Grab still looking for its target. A Grab-by-kind does not switch
 * tool — {@link grabOnly} arms the plain cursor and hangs a filter on it — so
 * the tool's own id cannot tell one from the neutral pointer, and every click
 * that missed the thing being reached for zoomed the view instead of doing
 * nothing. That is exactly the moment it does most harm: clicking about to find
 * a track among others is how the command is used, and each miss moved the
 * board out from under the next attempt.
 */
function clickZooms(w: { x: number; y: number }): boolean {
  if (!prefs.mouseButtonZoom) return false;
  if (state.placing || clickMode || clickCapture) return false;
  if (toolbox.holding) return false;
  if (toolbox.active.id !== 'select') return false;
  if (grabFilter) return false;
  return hitTest(doc(), w.x, w.y, doc().grid.current || 0.01, state.activeLayer) === null;
}

/** Where a press started, so a click can be told from a drag. */
let pressAt: { x: number; y: number; button: number; time: number } | null = null;

canvas.addEventListener('mousedown', (ev) => {
  pressAt = { x: ev.clientX, y: ev.clientY, button: ev.button, time: Date.now() };

  // Middle or right button pans, as in the original — a click and hold. A quick
  // right click without moving is handled on release, so both can coexist.
  if (ev.button === 1 || ev.button === 2) {
    panning = true;
    panX = ev.clientX;
    panY = ev.clientY;
    canvas.classList.add('panning');
    ev.preventDefault();
    return;
  }
  if (ev.button !== 0) return;

  const w = cursorAt(ev);
  // The point the drawing will act on, not the raw pointer: a macro recorded
  // off the grid and replayed on it would land somewhere else every time.
  if (recorder.recording) recorder.click(w.x, w.y, ev.button);

  if (clickZooms(w)) {
    viewport.rememberView();
    viewport.zoomAt(ev.clientX - canvasRect.left, ev.clientY - canvasRect.top, 1.414);
    pauseRatsnest();
    updateStatus();
    draw(true);
    return;
  }
  dispatchPoint(w, ev);
});

/**
 * A right click means Escape, when there is something to escape from.
 *
 * Drawing a run of wire with the mouse otherwise has no way to say "that is the
 * end of it" without reaching for the keyboard — and a right click was zooming
 * out instead, which is a poor answer to finishing a line. Returns true when it
 * did something, so the caller leaves the zoom alone.
 *
 * Only a click: a right *drag* still pans, which is the other thing the button
 * has always done.
 */
function rightClickEscape(): boolean {
  if (clickCapture || blockAnchor) {
    clickCapture = null;
    clearBlockPreview();
    setMessage('Cancelled');
    draw();
    return true;
  }
  if (clickMode) {
    clickMode = null;
    setMessage('');
    updateStatus();
    return true;
  }
  // Abandons whatever is in hand, exactly as Escape does.
  if (state.placing) {
    cancelPlace();
    draw();
    return true;
  }
  if (toolbox.holding || isPlacementTool(toolbox.active.id)) {
    toolbox.cancel();
    updateInfo();
    updateStatus();
    draw();
    return true;
  }
  return false;
}

/** True when the release belongs to a press that neither moved nor dwelt. */
function wasQuickClick(ev: MouseEvent): boolean {
  if (!pressAt || pressAt.button !== ev.button) return false;
  return Math.abs(ev.clientX - pressAt.x) < 4
    && Math.abs(ev.clientY - pressAt.y) < 4
    && Date.now() - pressAt.time < 400;
}

/**
 * Act on a point — from a mouse click or from pressing Enter at the keyboard
 * crosshair. Routes to a pending capture, a click mode, component placement, or
 * the active tool, in that order.
 */
function dispatchPoint(w: { x: number; y: number }, ev?: MouseEvent): void {
  // A command awaiting a point (Zoom Window/Center) consumes it.
  if (clickCapture) {
    const cb = clickCapture;
    clickCapture = null;
    cb(w);
    return;
  }

  // A persistent click mode (Delete Line, etc.) handles it and stays on.
  if (clickMode) {
    clickMode.run(w);
    return;
  }

  // Placement pre-empts the active tool: a component in hand is the immediate
  // intent, and it ends as soon as it is placed.
  if (state.placing) {
    const g = doc().grid.current;
    const comp = state.placing;
    const port = state.placingPort;
    // A port aims at a connection, so it lands on the pin or wire end under the
    // cursor; a part lands on the grid.
    const hit = port
      ? snapPoint(doc(), w.x, w.y, {
        tolerance: SNAP_PIXELS / viewport.scale,
        grid: activeGrid(),
        candidates: snapCandidateCache(),
      })
      : { x: snap(w.x, g), y: snap(w.y, g), kind: 'grid' as const };
    comp.x = hit.x;
    comp.y = hit.y;
    comp.id = newId();
    const rotation = comp.rotation;
    history().begin(doc(), `Place ${comp.name}`);
    doc().entities.push(comp);
    history().commit();
    documentChanged();
    const placed = state.placingLabel;
    state.placing = null;
    if (port) {
      // Number up and stay in hand: a bus is clicked out, not typed out. Esc
      // ends it, which is the only way out of the original's placement too.
      if (port === 'signal' || port === 'bus') {
        portNames[port] = nextPortName(portNames[port]);
      }
      armPort(port);
      // Keep whatever angle was set — a row of ports all face the same way.
      if (rotation) state.placing!.rotation = rotation;
      setMessage(`Placed ${placed}`
        + (hit.kind === 'grid' ? ' — on the grid, not on a pin' : ' — connected'));
    } else {
      state.placingPort = null;
      el('placing-hint').hidden = true;
      setMessage(`Placed ${placed}`);
    }
    updateStatus();
    draw();
    return;
  }

  toolbox.pointerDown(w, ev);
}

canvas.addEventListener('dblclick', (ev) => {
  const w = cursorAt(ev);
  // While drawing, double-click finishes the operation (the track tool relies
  // on it). Otherwise it opens the edit dialog for whatever is under the
  // cursor — the original edits any item you click.
  // Anything that is not mid-draw opens the item under the pointer, which is
  // how the original tells you about a thing: its layer, its width, its net,
  // all of it editable in the same breath. Mid-draw the double click belongs
  // to the tool, which uses it to finish a track.
  if (!isDrawing()) {
    if (editItemAt(w.x, w.y, { x: ev.clientX, y: ev.clientY })) return;
  }
  toolbox.doubleClick(w, ev);
});

window.addEventListener('mousemove', (ev) => {
  if (panning) {
    viewport.panByPixels(ev.clientX - panX, ev.clientY - panY);
    panX = ev.clientX;
    panY = ev.clientY;
    pauseRatsnest();
    draw(true); // pure viewport move — reuse the cached flatten
    return;
  }

  const w = cursorAt(ev);
  updateStatus(w.x, w.y);

  // Track the cursor so the crosshair follows it. Only over the canvas — the
  // full-canvas lines would be nonsense anchored to a point off it.
  const overCanvas = ev.target === canvas || canvas.contains(ev.target as Node);
  cursorWorld = overCanvas ? w : null;

  if (state.placing) {
    // `activeGrid`, not the raw grid: with Grid Snap off a part goes where you
    // put it, the same as every other placed point.
    const g = activeGrid();
    state.placing.x = snap(w.x, g);
    state.placing.y = snap(w.y, g);
    draw();
    return;
  }

  // A block with one corner down follows the cursor with the other.
  if (blockAnchor) {
    showBlockPreview(blockAnchor, w);
    draw();
    return;
  }

  toolbox.pointerMove(w, ev);
  // A placement tool that is between clicks still needs a redraw to move the
  // crosshair; tools that drew a preview already requested one.
  if (crosshairAt()) draw();
});

canvas.addEventListener('mouseleave', () => {
  if (cursorWorld) { cursorWorld = null; draw(); }
});

window.addEventListener('mouseup', (ev) => {
  if (panning) {
    panning = false;
    canvas.classList.remove('panning');
    // A right button pressed and released without moving was a click, not a
    // pan: with mouse-button zoom on that means zoom out. Only the canvas
    // records a press, so having one is proof the gesture started there — a
    // target check on the release is not, since it may land anywhere.
    if (ev.button === 2 && wasQuickClick(ev) && !rightClickEscape()
        && clickZooms(worldAt(ev))) {
      viewport.rememberView();
      viewport.zoomAt(ev.clientX - canvasRect.left, ev.clientY - canvasRect.top, 0.707);
      pauseRatsnest();
      updateStatus();
      draw(true);
    }
    pressAt = null;
    return;
  }
  if (ev.button === 0) toolbox.pointerUp(cursorAt(ev), ev);
  pressAt = null;
});

canvas.addEventListener('contextmenu', (ev) => ev.preventDefault());

canvas.addEventListener('wheel', (ev) => {
  ev.preventDefault();
  viewport.zoomAt(ev.clientX - canvasRect.left, ev.clientY - canvasRect.top, ev.deltaY < 0 ? 1.15 : 1 / 1.15);
  updateStatus();
  pauseRatsnest();
  draw(true); // pure viewport move — reuse the cached flatten
}, { passive: false });

window.addEventListener('keydown', (ev) => {
  // A modal dialog owns the keyboard for as long as it is open.
  //
  // Every key here is a command — the menu is single letters, not a modifier
  // in sight — so anything not explicitly excluded reaches the drawing. That
  // was survivable while dialogs held only short fields, but typing a
  // paragraph into the support report fired a menu command on most
  // keystrokes. Excluding the field types one at a time is how it got missed;
  // this excludes the whole dialog, buttons and all.
  if (document.querySelector('dialog[open]') !== null) return;

  const target = ev.target as HTMLElement | null;
  if (target && (target.tagName === 'INPUT' || target.tagName === 'SELECT'
    || target.tagName === 'TEXTAREA' || target.isContentEditable)) {
    if (ev.key === 'Escape') target.blur();
    return;
  }

  // A photograph being lined up owns the arrow keys, the turn keys and Enter
  // until it is done — before anything else looks at them, because every one of
  // those means something to the drawing as well.
  if (photoAdjustKey(ev)) { ev.preventDefault(); return; }

  // The settings you change most while drawing, each on one key you can reach
  // without stopping: Alt+G steps the grid, Alt+W the track width, Alt+O turns
  // ortho snap on and off, Alt+P shows or hides the pin numbers, and Alt+L
  // swaps between picking on every visible layer and picking only on the one
  // being worked on. Each is named in the status bar, and clicking the field
  // there does the same thing; the pickers remain for choosing exact values.
  if (ev.altKey && !ev.ctrlKey && !ev.metaKey) {
    if (ev.key === 'g' || ev.key === 'G') {
      ev.preventDefault();
      cycleGrid();
      return;
    }
    if (ev.key === 'w' || ev.key === 'W') {
      ev.preventDefault();
      cycleWidth();
      return;
    }
    if (ev.key === 'o' || ev.key === 'O') {
      ev.preventDefault();
      cycleOrtho();
      return;
    }
    if (ev.key === 'p' || ev.key === 'P') {
      ev.preventDefault();
      setShowPins(!state.showPins);
      return;
    }
    if (ev.key === 'l' || ev.key === 'L') {
      ev.preventDefault();
      togglePickScope();
      return;
    }
    if (ev.key === 'u' || ev.key === 'U') {
      ev.preventDefault();
      cycleUnits();
      return;
    }
    if (ev.key === 's' || ev.key === 'S') {
      ev.preventDefault();
      setSnap(!state.snap);
      return;
    }
  }

  if ((ev.ctrlKey || ev.metaKey) && (ev.key === 's' || ev.key === 'S')) {
    ev.preventDefault();
    void saveProject();
    return;
  }

  // Undo/redo before the tool sees the event.
  // The one route, so the keys and the menu commands reach the saved history
  // in the same way when this session's own stack is spent.
  if ((ev.ctrlKey || ev.metaKey) && (ev.key === 'z' || ev.key === 'Z')) {
    ev.preventDefault();
    undoRedo(ev.shiftKey);
    return;
  }
  if ((ev.ctrlKey || ev.metaKey) && (ev.key === 'y' || ev.key === 'Y')) {
    ev.preventDefault();
    undoRedo(true);
    return;
  }

  // Cut, copy and paste on the drawing.
  //
  // The original has no such keys — its clipboard is Block Copy and Block Paste,
  // two commands that ask for a region — but Ctrl+X, C and V are what anybody
  // arriving at a drawing reaches for first, and the geometry to move is
  // already in hand as the selection. One clipboard between these and the Block
  // commands, so copying with one and pasting with the other works and there is
  // no way to lose a copy in the wrong one.
  //
  // Not while a text file is showing: there the browser's own clipboard is
  // handling real text, and taking these keys would break copying a line.
  if ((ev.ctrlKey || ev.metaKey) && !ev.altKey && activeText === null
      && 'xcv'.includes(ev.key.toLowerCase())) {
    const k = ev.key.toLowerCase();
    if (k === 'v') {
      if (!blockClipboard) return; // nothing held; let the browser have it
      ev.preventDefault();
      pasteClipboardAtCursor();
      return;
    }
    if (state.selected.size === 0) return; // nothing grabbed; not ours to take
    ev.preventDefault();
    copySelectionToClipboard(k === 'x');
    return;
  }


  if (ev.ctrlKey || ev.metaKey || ev.altKey) return;

  // F11 records, F12 plays back. Ahead of the written keystroke macros, which
  // otherwise claim every function key — the recorder owns these two, and
  // assigning a written macro to F11 would silently take the recorder away.
  if (ev.key === 'F11') { ev.preventDefault(); recordMacroCommand(); return; }
  if (ev.key === 'F12') { ev.preventDefault(); void playMacroCommand(); return; }

  // Everything from here is a command, so this is where a recording listens.
  // Above it are the guards that keep a dialog's typing and a photograph's
  // arrow keys out, which is exactly what a macro must not capture; below it
  // the key is about to reach the drawing, which is what it must. F11 and F12
  // returned above, so a recording can never contain the keys that work it.
  if (recorder.recording) {
    recorder.key(ev.key, { shift: ev.shiftKey, ctrl: ev.ctrlKey, alt: ev.altKey });
  }

  // The rest run their keystroke macro (Setup ▸ Keystroke Macros).
  if (/^F([1-9]|10)$/.test(ev.key)) {
    if (runMacro(ev.key)) { ev.preventDefault(); return; }
  }

  if (ev.key === 'Escape') {
    if (menus.handleKey(ev)) return;
    closePicker();
    if (clickMode) { clickMode = null; setMessage(''); updateStatus(); return; }
    if (clickCapture || blockAnchor) {
      clickCapture = null;
      clearBlockPreview();
      setMessage('Cancelled');
      draw();
      return;
    }
    clickCapture = null;
    if (partPickerOpen() && !state.placing) { closePartPicker(); return; }
    // Escape abandons. It used to finish a run of track or line segments and
    // keep them, which made it the one key that could commit something — so
    // hitting it to get out of a half-drawn track left the half-drawn track
    // behind. It now means the same thing everywhere: stop, and put things back
    // as they were before this was started. A run is finished by a double click
    // or by placing a corner on top of the last one.
    //
    // Then, if there was nothing to abandon, it puts the *command* down too.
    //
    // A command staying armed is right while you are using it — laying six
    // wires should not mean typing P W six times, and Delete works the same way
    // — but it left no way back. Place Wire stayed armed indefinitely and every
    // click on the drawing started another wire, so the only way to a plain
    // cursor was to pick some other command instead. Escape is what "no
    // command" has always meant; it just had nothing left to do on the second
    // press. Now the first press abandons the run, the second hands back the
    // cursor.
    const wasBusy = toolbox.busy || state.placing !== null;
    cancelPlace();
    closePartPicker();
    // Ends what is being drawn, keeping it — the original's own behaviour, and
    // its help says so of Place Filled Solid outright. A tool that is only
    // holding something lets go and puts it back instead; Block Move's help
    // says that just as plainly.
    toolbox.end();
    if (!wasBusy && !toolbox.isNeutral) {
      toolbox.select('select');
      setMessage('No command — click to select, or press a menu letter');
    }
    updateInfo();
    updateStatus();
    draw();
    return;
  }

  // Keyboard crosshair, the original's way of laying track without a mouse:
  // arrow keys move it by grid snaps (a leading number repeats — 4↑ = four),
  // Space or Enter places a point. The manual leans on the space bar for this —
  // press it to drop a line's start, again to drop its end — so it is the
  // keyboard twin of a left click. Skipped while a menu or modal dialog is up.
  if (!menus.isOpen && !document.querySelector('dialog.modal[open]')) {
    const dir = ARROWS[ev.key];
    if (dir) {
      ev.preventDefault();
      // An arrow key always moves something. With the crosshair up it walks
      // the crosshair a snap at a time; with the plain pointer there is
      // nothing on the board to move, so it scrolls the board instead.
      if (gridMode || isDrawing()) moveCursor(dir[0], dir[1]);
      else scrollView(dir[0], dir[1]);
      return;
    }
    if (/^[0-9]$/.test(ev.key)) { cursorRepeat += ev.key; ev.preventDefault(); return; }
    if (ev.key === 'Enter') {
      ev.preventDefault();
      dispatchPoint(ensureCursor());
      return;
    }
    if (ev.key === ' ') {
      ev.preventDefault();
      // Mid-draw the space bar is the keyboard's left click, as the manual
      // uses it: press to drop a line's start, again to drop its end. With
      // nothing being drawn it does the other job the original gives it —
      // swap between the pointer and the grid crosshair, cancelling whatever
      // tool was in hand on the way, which is how Grab is let go of.
      if (isDrawing()) { dispatchPoint(ensureCursor()); return; }
      setGridMode(!gridMode);
      return;
    }
  }

  // Rotating the component in hand takes priority over the tool's own R.
  if ((ev.key === 'r' || ev.key === 'R') && state.placing) {
    state.placing.rotation = ((state.placing.rotation ?? 0) + 90) % 360;
    draw();
    return;
  }

  // The active tool gets Enter, Backspace and Delete — the keys that finish or
  // amend what it is drawing. It must come before the menu, or Delete would be
  // swallowed while a wire is in progress.
  if (toolbox.key(ev)) {
    ev.preventDefault();
    updateInfo();
    draw();
    return;
  }

  // Numpad + / - step the active layer, as the manual describes — a shortcut
  // for Zoom Layer that also drops a via when stepping copper mid-track. Not
  // while a menu is open, where + / - are the Net Scan ± command letters, and
  // not while a click mode is running: there they belong to that mode (Highlight
  // Net steps through the nets), and changing layer under it is never what was
  // meant.
  if (!menus.isOpen && (ev.key === '+' || ev.key === '=' || ev.key === '-' || ev.key === '_')) {
    ev.preventDefault();
    const dir = ev.key === '+' || ev.key === '=' ? 1 : -1;
    if (clickMode) clickMode.step?.(dir);
    else stepLayer(dir);
    return;
  }

  // Single-key transforms on a grabbed item (manual Ch.13 Shortcut Keys):
  // R rotate 90°, A rotate n°, X/Y mirror, M mirror top/bottom, K scale. Not
  // during a click mode — its own keys come first, and a stale selection must
  // not be silently transformed by a keystroke aimed at the mode.
  //
  // And not with a menu pending, where they are command letters. B M is Block
  // Move; with anything selected the M was taken here and mirrored the
  // selection instead, which is both the wrong command and a silent edit. The
  // same went for B R, B K, G M and every other second letter in this list.
  if (!menus.isOpen && !clickMode && !state.placing
      && state.selected.size > 0 && grabShortcut(ev.key)) {
    ev.preventDefault();
    return;
  }

  // View keys: centre on the cursor, zoom in, zoom out.
  //
  // `!menus.isOpen` is the whole of the guard. With a menu pending these are
  // *command* letters — G C is Grab Component, G I is Grab Item — and taking
  // them here made those commands zoom instead. With no menu open they belong
  // to the view, and they work while drawing too: laying a long track by
  // keyboard is exactly when you need to zoom out, look, and zoom back in, and
  // no tool claims c, i or o for anything of its own.
  if (!menus.isOpen && !ev.ctrlKey && !ev.metaKey && !ev.altKey) {
    // T steps the ortho mode, as it does in the original (OGxyz02.cpp:1195).
    // It belongs with these rather than on a menu: ortho is decided *while* you
    // are drawing — the command sets the mode it wants and T is how you argue
    // with it mid-run — so it has to work with a wire half laid. No menu letter
    // is T, so nothing is taken from the menu by claiming it here.
    if (ev.key === 't' || ev.key === 'T') {
      ev.preventDefault();
      cycleOrtho();
      draw();
      return;
    }

    // The mouse if it has been over the canvas, else the keyboard crosshair —
    // these are view keys, and they should answer to whichever pointer is
    // actually in use rather than only to the mouse.
    const at = cursorWorld ?? (gridMode ? ensureCursor() : null);
    if ((ev.key === 'c' || ev.key === 'C') && at) {
      ev.preventDefault();
      viewport.rememberView();
      viewport.cx = at.x;
      viewport.cy = at.y;
      updateStatus();
      draw(true);
      setMessage('Centred on the cursor');
      return;
    }
    if (ev.key === 'i' || ev.key === 'I' || ev.key === 'o' || ev.key === 'O') {
      ev.preventDefault();
      const inward = ev.key === 'i' || ev.key === 'I';
      viewport.rememberView();
      // About the cursor when there is one, so the point of interest stays put
      // — the same behaviour as the wheel. Otherwise about the middle.
      const sx = at ? viewport.toScreenX(at.x) : viewport.width / 2;
      const sy = at ? viewport.toScreenY(at.y) : viewport.height / 2;
      viewport.zoomAt(sx, sy, inward ? 1.414 : 0.707);
      pauseRatsnest();
      updateStatus();
      draw(true);
      return;
    }
  }

  // Then the menu. Letters belong to it: `P` opens Place, it does not select a
  // "pad tool". That is how the original is driven, and why the tool palette
  // is gone.
  if (menus.handleKey(ev)) {
    ev.preventDefault();
    return;
  }

  // Display units are a view setting, not a command in the original's tree.
  if (ev.key === '"' || ev.key === "'") {
    cycleUnits();
  }
});

/**
 * A one-shot capture of the next canvas click(s), used by commands like Zoom
 * Window and Zoom Center that ask you to pick a point. It pre-empts the active
 * tool for exactly as long as the command needs, then hands control back.
 */
let clickCapture: ((w: { x: number; y: number }) => void) | null = null;

function beginZoomCenter(): void {
  setMessage('Select central reference point');
  clickCapture = (w) => {
    viewport.rememberView();
    viewport.cx = w.x;
    viewport.cy = w.y;
    updateStatus();
    draw();
    setMessage('Centred');
  };
}

// --- Block operations -------------------------------------------------------
//
// The original's whole second editing modality: mark a rectangle, then act on
// everything inside it. It is not the same thing as the selection — you do not
// pick the items, you pick an area — which is why it has its own menu and why a
// board is edited far faster with it than by grabbing item after item.

/** The corner already placed, while a block is being marked. Drives the preview. */
let blockAnchor: { x: number; y: number } | null = null;

/** The rectangle drawn while marking, kept apart from the tools' own preview. */
const blockPreview: Entity[] = [];

/** Geometry cut or copied by Block ▸ Copy Clipboard, with the point it was taken about. */
let blockClipboard: { kind: DocumentKind; items: Entity[]; origin: { x: number; y: number } } | null = null;

function clearBlockPreview(): void {
  blockAnchor = null;
  blockPreview.length = 0;
}

/**
 * The containment region every Block command acts on.
 *
 * This is the original's model, and it took reading its own help to see it: the
 * Block menu has three commands whose whole job is to define a region — `BG`
 * Rectangle, `BI` Polyline and `BE` Release — and the rest say things like "A
 * block must be defined before this function will be operational". You mark the
 * area once and then work on it: copy it, move it, delete outside it, isolate
 * the tracks in it. Asking for two corners afresh on every command, which is
 * what this used to do, made a five-command sequence into fifteen clicks.
 *
 * Held on the app rather than the document: it is where you are working, not
 * part of the drawing, and it should not turn a file dirty or be saved.
 */
let blockRegion: { points: Vertex[]; rect: Rect; ref: Vertex } | null = null;

/**
 * True while a block is riding the cursor and its outline should follow.
 *
 * Block Move carries the geometry; the outline that marks it has to go too, or
 * you drop the block somewhere new and the rectangle saying what the next
 * command will act on is left behind on bare board. Off for Block Copy, where
 * the block stays round the originals — they are what was copied, and may well
 * be copied again.
 */
let carryingBlock = false;

/**
 * Move the carried block's outline the same way its contents just moved.
 *
 * Every transform that reaches a grabbed selection comes through here as well,
 * so a block turned with R arrives with its outline turned about the same
 * centre. Nothing to do when what is in hand is not a block.
 */
function moveCarriedRegion(fn: (p: Vertex) => Vertex): void {
  if (!carryingBlock || !blockRegion) return;
  setBlockRegion(blockRegion.points.map(fn), fn(blockRegion.ref));
}

/** The rectangle enclosing the region, which is all most callers need. */
function regionBounds(pts: readonly Vertex[]): Rect {
  return {
    x1: Math.min(...pts.map((p) => p.x)),
    y1: Math.min(...pts.map((p) => p.y)),
    x2: Math.max(...pts.map((p) => p.x)),
    y2: Math.max(...pts.map((p) => p.y)),
  };
}

/**
 * Remember a region and show it.
 *
 * `ref` is the block's own reference point — the original's third click when
 * you mark a rectangle, and what it calls the source point. Every later command
 * measures from it: Block Move carries the block by that point, so it is the
 * one you put on the spot you want the block to land. Defaults to the first
 * corner when a caller has none to give.
 */
function setBlockRegion(points: Vertex[], ref?: Vertex): void {
  blockRegion = points.length >= 3
    ? {
      points,
      rect: regionBounds(points),
      ref: ref ? { x: ref.x, y: ref.y } : { x: points[0]!.x, y: points[0]!.y },
    }
    : null;
  showRegion();
  updateStatus();
  draw();
}

/** Draw the defined region, so it is obvious what the next command will act on. */
function showRegion(): void {
  regionPreview.length = 0;
  const r = blockRegion;
  if (!r) return;
  for (let i = 0; i < r.points.length; i++) {
    const a = r.points[i]!;
    const b = r.points[(i + 1) % r.points.length]!;
    regionPreview.push({
      kind: 'line', id: -100 - i, layer: state.activeLayer,
      width: 1 / viewport.scale, x1: a.x, y1: a.y, x2: b.x, y2: b.y,
    });
  }
}

/** The marked region, drawn above the drawing and apart from any tool's preview. */
const regionPreview: Entity[] = [];

/**
 * Is this entity inside the region?
 *
 * A rectangle is answered by its bounds, as the rubber band always did. An
 * irregular region has to be answered properly — every corner of the entity's
 * box inside the polygon — or a Delete Outside would take what the operator
 * carefully drew a boundary around.
 */
function insideRegion(e: Entity): boolean {
  const r = blockRegion;
  if (!r) return false;
  const b = entityBounds(e);
  if (!b) return false;
  if (b.x1 < r.rect.x1 || b.x2 > r.rect.x2 || b.y1 < r.rect.y1 || b.y2 > r.rect.y2) {
    return false;
  }
  // Four corners is enough for the shapes a person draws round a board; a
  // region convoluted enough to defeat it would be a region nobody could see
  // the edges of either.
  if (r.points.length === 4) return true;   // a rectangle, already answered
  return [
    { x: b.x1, y: b.y1 }, { x: b.x2, y: b.y1 },
    { x: b.x2, y: b.y2 }, { x: b.x1, y: b.y2 },
  ].every((p) => pointInPolygon(p, r.points));
}

/** Ray casting: odd crossings means inside. */
function pointInPolygon(p: Vertex, poly: readonly Vertex[]): boolean {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const a = poly[i]!;
    const b = poly[j]!;
    if ((a.y > p.y) === (b.y > p.y)) continue;
    if (p.x < ((b.x - a.x) * (p.y - a.y)) / (b.y - a.y) + a.x) inside = !inside;
  }
  return inside;
}

/** Everything the region holds, by the same visibility rule a band uses. */
function entitiesInRegion(): Entity[] {
  const d = doc();
  return d.entities.filter((e) => selectable(d, e) && insideRegion(e));
}


/**
 * How strongly a traced-from photograph shows, 0 to 1.
 *
 * A setting rather than a stored value: it is about looking, not about the
 * board, and the right amount changes as you work — full strength to find a
 * track, faint to check what you have drawn over it.
 */
let photoOpacity = 0.6;

/**
 * Views ▸ Photo Underlay — everything about the picture you are tracing from.
 *
 * One way in rather than four commands, because loading, placing, scaling and
 * removing are all the same job at different moments, and a menu that spreads
 * them out makes you hunt.
 */
function photoCommand(): void {
  const d = doc();
  const refs = d.images ?? [];

  openDialog({
    title: 'Photo Underlay',
    build(body, handle) {
      const form = document.createElement('div');
      form.className = 'modal-form';
      body.append(form);

      if (refs.length === 0) {
        const p = document.createElement('p');
        p.className = 'modal-hint';
        p.textContent =
          'Lay a photograph of a board under the drawing and trace over it. '
          + 'The picture is not saved into the file — only where it goes — so it is '
          + 'loaded again each session.';
        form.append(p);
      }

      for (const ref of refs) {
        const row = document.createElement('div');
        row.className = 'photo-row';

        const name = document.createElement('span');
        name.textContent = shortName(ref.name);
        name.title = ref.name;

        const state = document.createElement('span');
        state.className = 'pick-where';
        const px = photoPixels(ref);
        state.textContent = px
          ? `${ref.width}×${ref.height} at ${(1 / ref.resolution).toFixed(0)} dpi`
          : 'not loaded';

        row.append(name, state);
        form.append(row);

        const actions = document.createElement('div');
        actions.className = 'photo-actions';
        form.append(actions);

        if (!px) {
          button(actions, 'Load…', () => {
            handle.close();
            void reloadPhoto(ref);
          });
        } else {
          button(actions, 'Set scale…', () => { handle.close(); beginPhotoScale(ref); });
          button(actions, 'Move', () => { handle.close(); beginPhotoMove(ref); });
          button(actions, 'Nudge…', () => { handle.close(); beginPhotoAdjust(ref); });
          button(actions, 'Corner…', () => { handle.close(); beginPhotoCorner(ref); });
          button(actions, 'Turn 90°', () => {
            history().begin(d, 'Turn photo');
            ref.rotation = (((ref.rotation ?? 0) + 90) % 360 + 360) % 360;
            history().commit();
            documentChanged();
            draw();
            setMessage(`Photo turned to ${ref.rotation}°`);
          });
        }
        button(actions, 'Remove', () => {
          handle.close();
          removePhoto(ref);
        });
      }

      // Fade, which is the control you reach for most while tracing.
      const fade = document.createElement('label');
      fade.className = 'modal-row';
      const fspan = document.createElement('span');
      fspan.textContent = 'Show at';
      const slider = document.createElement('input');
      slider.type = 'range';
      slider.min = '10';
      slider.max = '100';
      slider.value = String(Math.round(photoOpacity * 100));
      const pct = document.createElement('span');
      pct.className = 'pick-where';
      pct.textContent = `${slider.value}%`;
      slider.addEventListener('input', () => {
        photoOpacity = parseInt(slider.value, 10) / 100;
        pct.textContent = `${slider.value}%`;
        draw();
      });
      fade.append(fspan, slider, pct);
      form.append(fade);

      const row = buttons(body);
      button(row, 'Add a photo…', () => { handle.close(); void addPhoto(); }, refs.length === 0);
      button(row, 'Close', () => handle.close(), refs.length > 0);
    },
  });
}


/**
 * Which photo a command acts on. One: use it. None: say so. Several: open the
 * list — rescaling the wrong picture is tedious to undo.
 */
function thePhoto(what: string): ImageRef | null {
  const refs = doc().images ?? [];
  if (refs.length === 1) return refs[0]!;
  if (refs.length === 0) {
    setMessage(`${what} — no photograph here. Views ▸ Load Image puts one under the drawing.`);
    return null;
  }
  setMessage(`${what} — which photograph? Pick one from the list.`);
  photoCommand();
  return null;
}

/** Views ▸ Load Image. */
function photoLoadCommand(): void {
  void addPhoto();
}

/** Views ▸ Move Image. */
function photoMoveCommand(): void {
  const ref = thePhoto('Move Image');
  if (ref) beginPhotoMove(ref);
}

/** Views ▸ Scale Image — exact, from two known points. */
function photoScaleCommand(): void {
  const ref = thePhoto('Scale Image');
  if (ref) beginPhotoScale(ref);
}

/** Views ▸ Corner Image — rough, by eye. */
function photoCornerCommand(): void {
  const ref = thePhoto('Corner Image');
  if (ref) beginPhotoCorner(ref);
}

/** Views ▸ Nudge Image — keyboard, for the last thousandth. */
function photoNudgeCommand(): void {
  const ref = thePhoto('Nudge Image');
  if (ref) beginPhotoAdjust(ref);
}

/** Views ▸ Turn Image — quarter turns. Nudge does fine angles. */
function photoTurnCommand(): void {
  const ref = thePhoto('Turn Image');
  if (!ref) return;
  const d = doc();
  history().begin(d, 'Turn photo');
  ref.rotation = (((ref.rotation ?? 0) + 90) % 360 + 360) % 360;
  history().commit();
  documentChanged();
  draw();
  setMessage(`Photo turned to ${ref.rotation}°`);
}

/** Views ▸ Fade Image. Steps per press and wraps — most-used control while tracing. */
function photoFadeCommand(): void {
  if ((doc().images ?? []).length === 0) {
    setMessage('Fade Image — no photograph here');
    return;
  }
  const steps = [0.2, 0.4, 0.6, 0.8, 1];
  const at = steps.findIndex((v) => Math.abs(v - photoOpacity) < 0.01);
  photoOpacity = steps[(at + 1) % steps.length]!;
  draw();
  setMessage(`Photo showing at ${Math.round(photoOpacity * 100)}%`);
}

/** Views ▸ Drop Image. */
function photoDropCommand(): void {
  const ref = thePhoto('Drop Image');
  if (ref) removePhoto(ref);
}
/** Just the file's own name, for a dialog that has no room for a path. */
function shortName(path: string): string {
  return path.split(/[\\/]/).pop() || path;
}

/** Pick a picture and lay it under the drawing at the middle of the view. */
async function addPhoto(): Promise<void> {
  const file = await photoPick();
  if (!file) { setMessage('No photo chosen'); return; }

  let img;
  try {
    img = await photoLoad(file);
  } catch (err) {
    setMessage(err instanceof Error ? err.message : 'That file is not a picture');
    return;
  }

  const d = doc();
  const b = viewport.bounds();
  const ref = photoPlace(img, file.name, {
    x: b.x1 + (b.x2 - b.x1) * 0.15,
    y: b.y1 + (b.y2 - b.y1) * 0.15,
  });

  history().begin(d, 'Place photo');
  (d.images ??= []).push(ref);
  history().commit();
  documentChanged();
  draw();
  setMessage(
    `${shortName(ref.name)} placed — set its scale from two known points `
    + 'so what you trace comes out the right size');
}

/**
 * Offer to load the picture a board says it was traced from.
 *
 * A browser cannot open the path the file records — it is an absolute path from
 * whatever machine placed it, and no page may read the disk uninvited — so the
 * best that can be done is to say which picture is wanted and let the person
 * who has it point at it.
 */
async function reloadPhoto(ref: ImageRef): Promise<void> {
  const file = await photoPick();
  if (!file) { setMessage('No photo chosen'); return; }
  try {
    const img = await photoLoad(file, ref.name);
    // A replacement of different pixel dimensions would sit at the wrong size
    // if the recorded ones were kept.
    ref.width = img.naturalWidth;
    ref.height = img.naturalHeight;
    documentChanged();
    draw();
    setMessage(`${shortName(ref.name)} loaded`);
  } catch (err) {
    setMessage(err instanceof Error ? err.message : 'That file is not a picture');
  }
}

/** Drop a photograph and the reference to it. */
function removePhoto(ref: ImageRef): void {
  const d = doc();
  history().begin(d, 'Remove photo');
  d.images = (d.images ?? []).filter((r) => r !== ref);
  if (d.images.length === 0) delete d.images;
  history().commit();
  photoForget(ref.name);
  documentChanged();
  draw();
  setMessage(`${shortName(ref.name)} removed`);
}

/**
 * Set a photograph's scale from two points a known distance apart.
 *
 * Click two features you can measure — two pads of a tenth-inch header, the
 * span of a chip — then say how far apart they really are. Everything traced
 * afterwards inherits this number, so it is worth spending the two clicks on
 * rather than sizing the picture by eye.
 */
function beginPhotoScale(ref: ImageRef): void {
  cancelPlace();
  toolbox.cancel();
  setMessage('Photo scale — click the first of two points you know the distance between');
  clickCapture = (a) => {
    setMessage('Photo scale — now the second point');
    clickCapture = (b) => {
      const apart = Math.hypot(b.x - a.x, b.y - a.y);
      if (!(apart > 0)) { setMessage('Photo scale — those are the same point'); return; }

      const said = window.prompt(
        `Those two points are ${formatDisplay(apart, state.units)} apart on screen.\n`
        + `How far apart are they really, in ${unitName(state.units)}?`,
        editValue(apart, state.units));
      if (said === null) { setMessage('Photo scale — left alone'); return; }

      const want = readEdit(said, state.units, apart);
      if (want === null || want <= 0) {
        setMessage('Photo scale — that is not a distance');
        return;
      }

      const next = photoResolutionFrom(a, b, want, ref);
      if (next === null) { setMessage('Photo scale — could not work that out'); return; }

      const d = doc();
      history().begin(d, 'Scale photo');
      // The picture grows about its insertion point, so the point you clicked
      // first is held still — otherwise the feature you just measured walks
      // off and you have to find it again.
      const k = next / ref.resolution;
      ref.x = a.x - (a.x - ref.x) * k;
      ref.y = a.y - (a.y - ref.y) * k;
      ref.resolution = next;
      history().commit();
      documentChanged();
      draw();
      setMessage(
        `Photo scaled to ${(1 / next).toFixed(0)} dpi — `
        + `${formatDisplay(want, state.units)} across those two points`);
    };
  };
}

/** Move a photograph: click where it is, then where it should be. */
function beginPhotoMove(ref: ImageRef): void {
  cancelPlace();
  toolbox.cancel();
  setMessage('Photo move — click a feature on the photo');
  clickCapture = (from) => {
    setMessage('Photo move — now click where that feature belongs');
    clickCapture = (to) => {
      const d = doc();
      history().begin(d, 'Move photo');
      ref.x += to.x - from.x;
      ref.y += to.y - from.y;
      history().commit();
      documentChanged();
      draw();
      setMessage('Photo moved');
    };
  };
}

/**
 * Size a photograph by its far corner — the rough-and-ready way.
 *
 * Quicker than measuring, and wrong by however much the eye misjudges. It is
 * here for getting a picture roughly in place before setting the scale
 * properly, which is the order most people work in.
 */
function beginPhotoCorner(ref: ImageRef): void {
  cancelPlace();
  toolbox.cancel();
  setMessage('Photo corner — click where the far corner of the picture should sit');
  clickCapture = (at) => {
    const wantW = at.x - ref.x;
    const wantH = at.y - ref.y;
    if (!(wantW > 0) || !(wantH > 0)) {
      setMessage('Photo corner — that corner is not up and to the right of the picture');
      return;
    }
    // Whichever way is the tighter fit, so the picture keeps its proportions
    // rather than being stretched into something that no longer matches.
    const next = Math.min(wantW / ref.width, wantH / ref.height);
    const d = doc();
    history().begin(d, 'Size photo');
    ref.resolution = next;
    history().commit();
    documentChanged();
    draw();
    setMessage(
      `Photo sized to ${(1 / next).toFixed(0)} dpi — `
      + 'set the scale from two known points to make it exact');
  };
}


/** Photo being nudged, or null. Small repeated corrections want the keyboard. */
let adjusting: ImageRef | null = null;

/** Step per press: a fraction of the view, so it scales with zoom. Shift = ×0.1. */
function adjustStep(fine: boolean): number {
  const across = viewport.width / viewport.scale;   // inches across the view
  return (across / 200) * (fine ? 0.1 : 1);
}

/** Begin nudging a photograph with the keyboard. */
function beginPhotoAdjust(ref: ImageRef): void {
  cancelPlace();
  toolbox.cancel();
  adjusting = ref;
  history().begin(doc(), 'Adjust photo');
  reportAdjust();
  draw();
}

/** Finish nudging, keeping what was done. */
function endPhotoAdjust(): void {
  if (!adjusting) return;
  adjusting = null;
  history().commit();
  documentChanged();
  draw();
  setMessage('Photo set');
}

/** Show the numbers while they change. */
function reportAdjust(): void {
  const r = adjusting;
  if (!r) return;
  const dpiX = r.resolution > 0 ? (1 / r.resolution).toFixed(0) : '?';
  const dpiY = (r.resolutionY ?? r.resolution) > 0
    ? (1 / (r.resolutionY ?? r.resolution)).toFixed(0) : '?';
  const turn = (r.rotation ?? 0).toFixed(2);
  setMessage(
    `Adjusting ${shortName(r.name)} — arrows move · shift+arrows stretch · `
    + `, and . turn · [ and ] scale · Enter or Esc done   `
    + `(${dpiX}×${dpiY} dpi, ${turn}°)`);
}

/** Nudge keys, taken before the drawing sees them. True if used. */
function photoAdjustKey(ev: KeyboardEvent): boolean {
  const r = adjusting;
  if (!r) return false;

  if (ev.key === 'Enter' || ev.key === 'Escape') {
    // Both keep it — a nudge has nothing half-finished. Undo reverses it.
    endPhotoAdjust();
    return true;
  }

  const step = adjustStep(ev.shiftKey);
  const dir = ARROWS[ev.key];

  if (dir) {
    if (ev.shiftKey) {
      // Stretch along the arrow's axis, about the insertion point.
      const [dx, dy] = dir;
      if (dx) {
        const span = r.width * r.resolution;
        if (span > 0) r.resolution *= (span + dx * step) / span;
      }
      if (dy) {
        const now = r.resolutionY ?? r.resolution;
        const span = r.height * now;
        if (span > 0) r.resolutionY = now * ((span + dy * step) / span);
      }
    } else {
      r.x += dir[0] * step;
      r.y += dir[1] * step;
    }
    reportAdjust();
    draw();
    return true;
  }

  // Turn by 0.1°, or 0.01° with shift — a whole degree overshoots the error.
  if (ev.key === ',' || ev.key === '<') {
    r.rotation = round2(((r.rotation ?? 0) - (ev.shiftKey ? 0.01 : 0.1)));
    reportAdjust();
    draw();
    return true;
  }
  if (ev.key === '.' || ev.key === '>') {
    r.rotation = round2(((r.rotation ?? 0) + (ev.shiftKey ? 0.01 : 0.1)));
    reportAdjust();
    draw();
    return true;
  }

  // Scale both axes together.
  if (ev.key === '[' || ev.key === ']') {
    const k = ev.key === ']'
      ? (ev.shiftKey ? 1.001 : 1.01)
      : (ev.shiftKey ? 1 / 1.001 : 1 / 1.01);
    r.resolution *= k;
    if (r.resolutionY !== undefined) r.resolutionY *= k;
    reportAdjust();
    draw();
    return true;
  }

  return false;
}

/** Two decimals, so a turn reads as a number. */
function round2(v: number): number {
  return Math.round(v * 100) / 100;
}
/**
 * Ask about photographs a newly opened board expects but does not have.
 *
 * The file says it was traced from a picture; the pixels are not in it and the
 * path is from somebody else's machine. So the board is asked after rather than
 * silently ignored — and if the answer is no, the reference itself is offered
 * up for removal, because a board that keeps asking after a photograph nobody
 * has any more is just nagging.
 */
async function offerPhotos(): Promise<void> {
  const d = doc();
  for (const ref of photoMissing(d)) {
    const short = shortName(ref.name);
    if (window.confirm(`This board was traced from ${short}.\n\nLoad it?`)) {
      await reloadPhoto(ref);
      continue;
    }
    if (window.confirm(`Remove the reference to ${short} from this board?`)) {
      removePhoto(ref);
    }
  }
}

/**
 * Drop any marked block, without a word about it.
 *
 * Marking a new block releases the old one first. The original does this in its
 * own first-corner handler — it deletes the block element before it starts the
 * new one — and its menu goes further, greying Block Rectangle out entirely
 * while a block stands so that Release is the only way on. Either way the
 * operator ends up pressing Release before every second block, which is a
 * keystroke spent telling the program something it can see for itself: you are
 * defining a block, so the one you had is finished with.
 */
function releaseBlockQuietly(): void {
  if (!blockRegion) return;
  blockRegion = null;
  regionPreview.length = 0;
  updateStatus();
}

/**
 * Ask for the block's reference point, then remember the block.
 *
 * The original's last step for both marking commands: "Select source reference
 * point." It is the grab handle — Block Move picks the block up by it — so it
 * wants to be a corner of the thing you are moving, or a pin, and not wherever
 * you happened to start the rectangle.
 */
function askBlockReference(label: string, points: Vertex[]): void {
  // Shown as the marking preview, not as a marked region — you cannot choose a
  // handle for a shape you can no longer see, but nothing is a block until the
  // handle is given. That is what makes Escape here mean what the original
  // means by it: `BlockDefineEscape` is still the right-button function through
  // this step, and it deletes the block outright. Escape clears the marking
  // preview, so the drawing is left with no block at all — which is right,
  // since marking one is what was abandoned.
  showPolyPreview(points);
  draw();
  setMessage(`${label} — reference point (the handle the block is carried by)`);
  clickCapture = (r) => {
    clearBlockPreview();
    setBlockRegion(points, r);
    const n = entitiesInRegion().length;
    setMessage(`Block marked — ${n} item${n === 1 ? '' : 's'} inside`);
  };
}

/** Draw a closed outline as the marking preview, for any number of corners. */
function showPolyPreview(points: readonly Vertex[]): void {
  blockPreview.length = 0;
  for (let i = 0; i < points.length; i++) {
    const a = points[i]!;
    const b = points[(i + 1) % points.length]!;
    blockPreview.push({
      kind: 'line', id: -1 - i, layer: state.activeLayer,
      width: 1 / viewport.scale, x1: a.x, y1: a.y, x2: b.x, y2: b.y,
    });
  }
}

/** Block ▸ Rectangle — mark a rectangular region for the block commands. */
function blockRectangleCommand(): void {
  cancelPlace();
  toolbox.cancel();
  clearBlockPreview();
  // Every Block command clears ortho on the way in — the original does it once,
  // above the switch (OGmenu09.cpp:38). A block is dragged out and carried, and
  // a mode left over from the last wire has no business holding either.
  setOrtho(0, true);
  setMessage('Block Rectangle — first corner');
  clickCapture = (a) => {
    // Here and not at the command, so Escape before the first corner leaves the
    // block you had alone — nothing has been asked for yet.
    releaseBlockQuietly();
    blockAnchor = a;
    showBlockPreview(a, a);
    draw();
    setMessage('Block Rectangle — opposite corner');
    clickCapture = (b) => {
      clearBlockPreview();
      askBlockReference('Block Rectangle', [
        { x: a.x, y: a.y }, { x: b.x, y: a.y },
        { x: b.x, y: b.y }, { x: a.x, y: b.y },
      ]);
    };
  };
}

/**
 * Block ▸ Polyline — mark an irregular region.
 *
 * Corners until a repeated one closes it, the same way a filled solid is drawn,
 * because it is the same gesture and should not need a second thing learning.
 */
function blockPolylineCommand(): void {
  cancelPlace();
  toolbox.cancel();
  clearBlockPreview();
  // Every Block command clears ortho on the way in — the original does it once,
  // above the switch (OGmenu09.cpp:38). A block is dragged out and carried, and
  // a mode left over from the last wire has no business holding either.
  setOrtho(0, true);
  const pts: Vertex[] = [];

  const takePoint = (w: { x: number; y: number }): void => {
    const last = pts[pts.length - 1];
    const repeated = last
      && Math.abs(last.x - w.x) < 1e-9 && Math.abs(last.y - w.y) < 1e-9;

    if (repeated || (pts.length >= 3 && near(pts[0]!, w))) {
      if (pts.length < 3) {
        setMessage('Block Polyline — a region needs at least three corners');
        clickCapture = takePoint;
        return;
      }
      clearBlockPreview();
      askBlockReference('Block Polyline', pts.slice());
      return;
    }

    if (pts.length === 0) releaseBlockQuietly();
    pts.push({ x: w.x, y: w.y });
    blockPreview.length = 0;
    for (let i = 0; i + 1 < pts.length; i++) {
      blockPreview.push({
        kind: 'line', id: -1 - i, layer: state.activeLayer,
        width: 1 / viewport.scale,
        x1: pts[i]!.x, y1: pts[i]!.y, x2: pts[i + 1]!.x, y2: pts[i + 1]!.y,
      });
    }
    draw();
    setMessage(pts.length < 3
      ? `Block Polyline — ${pts.length} corner${pts.length === 1 ? '' : 's'}, keep going`
      : `Block Polyline — ${pts.length} corners, repeat a corner or click the first to close`);
    clickCapture = takePoint;
  };

  setMessage('Block Polyline — first corner');
  clickCapture = takePoint;
}

/** Within half a grid step, for closing a polyline on its own first corner. */
function near(a: Vertex, b: { x: number; y: number }): boolean {
  const g = (doc().grid.current || 0.01) * 0.5;
  return Math.abs(a.x - b.x) <= g && Math.abs(a.y - b.y) <= g;
}

/** Block ▸ Release — forget the region. */
function blockReleaseCommand(): void {
  if (!blockRegion) { setMessage('No block is marked'); return; }
  blockRegion = null;
  regionPreview.length = 0;
  updateStatus();
  draw();
  setMessage('Block released');
}
/** Draw the marking rectangle as four preview lines on the working layer. */
function showBlockPreview(a: { x: number; y: number }, b: { x: number; y: number }): void {
  blockPreview.length = 0;
  const pts = [
    { x: a.x, y: a.y }, { x: b.x, y: a.y },
    { x: b.x, y: b.y }, { x: a.x, y: b.y },
  ];
  for (let i = 0; i < 4; i++) {
    const p = pts[i]!;
    const q = pts[(i + 1) % 4]!;
    blockPreview.push({
      kind: 'line', id: -1 - i, layer: state.activeLayer,
      width: 1 / viewport.scale, x1: p.x, y1: p.y, x2: q.x, y2: q.y,
    });
  }
}

/**
 * Mark a block, then hand what it holds to `then`.
 *
 * Two corners, with the rectangle drawn as it is dragged out, because a block
 * you cannot see the edges of is a block you will mark twice. What it collects
 * is `entitiesInBand` — the same rule the rubber band uses, so hidden and
 * locked layers are left alone and a block can never take what you cannot see.
 */
function beginBlock(
  label: string,
  then: (
    items: Entity[],
    band: { x1: number; y1: number; x2: number; y2: number },
    ref: Vertex,
  ) => void,
): void {
  cancelPlace();
  toolbox.cancel();
  clearBlockPreview();
  // Every Block command clears ortho on the way in — the original does it once,
  // above the switch (OGmenu09.cpp:38). A block is dragged out and carried, and
  // a mode left over from the last wire has no business holding either.
  setOrtho(0, true);

  // A region already marked *is* the block, as the original has it: mark once
  // with B G or B I, then run as many commands on it as you like. Its own help
  // says as much — "a block must be defined before this function will be
  // operational" — and asking for two fresh corners per command, which this
  // used to do, turned a five-command sequence into fifteen clicks.
  if (blockRegion) {
    const items = entitiesInRegion();
    if (items.length === 0) {
      setMessage(`${label} — nothing inside the marked block`);
      return;
    }
    then(items, blockRegion.rect, blockRegion.ref);
    return;
  }

  // With none marked, one is marked inline rather than refusing outright — a
  // command that does nothing but scold is a poor first answer. Two corners and
  // no third click for a reference point: the command was asked for, not the
  // block, so the first corner serves as the handle and the operator is not
  // made to pick one to get at Block Delete.
  setMessage(`${label} — first corner of the block`);
  clickCapture = (a) => {
    blockAnchor = a;
    showBlockPreview(a, a);
    setMessage(`${label} — opposite corner`);
    draw();
    clickCapture = (b) => {
      clearBlockPreview();
      const band = {
        x1: Math.min(a.x, b.x), y1: Math.min(a.y, b.y),
        x2: Math.max(a.x, b.x), y2: Math.max(a.y, b.y),
      };
      const items = entitiesInBand(doc(), band);
      if (items.length === 0) {
        setMessage(`${label} — nothing inside that block`);
        draw();
        return;
      }
      // Kept, so a run of block commands needs marking only once.
      setBlockRegion([
        { x: band.x1, y: band.y1 }, { x: band.x2, y: band.y1 },
        { x: band.x2, y: band.y2 }, { x: band.x1, y: band.y2 },
      ], a);
      then(items, band, { x: a.x, y: a.y });
    };
  };
}

/**
 * Put a block on the cursor and let it be carried to where it goes.
 *
 * This is what the original does and what makes Block Move legible: `blmove`
 * attaches every item in the block to the graphics cursor the instant the
 * command is given, and the one point it then asks for is the target. You watch
 * the block travel, you can turn it with R or flip it with X and Y on the way,
 * the arrow keys nudge it a grid step at a time, and Escape puts it back where
 * it was — none of which a from-point-then-to-point pair of clicks could offer.
 *
 * For a move the marked region travels with it: a block left behind on bare
 * board is a block the next command finds empty, and the operator has done
 * nothing to deserve having to mark it again. For a copy it stays, still round
 * the originals, because that is what was copied and may well be copied again.
 */
function carryBlock(
  label: string, items: Entity[], ref: Vertex,
  opts: { moveRegion?: boolean; abandon?: () => void } = {},
): void {
  state.selected.clear();
  for (const e of items) state.selected.add(e.id);

  // Where the block stood before the carry, so abandoning it puts the outline
  // back along with the geometry.
  const was = blockRegion
    ? { points: blockRegion.points.map((p) => ({ x: p.x, y: p.y })), ref: { ...blockRegion.ref } }
    : null;
  carryingBlock = opts.moveRegion !== false && blockRegion !== null;

  const settle = (): void => {
    carryingBlock = false;
    documentChanged();
    updateInfo();
    updateStatus();
    draw();
  };

  toolbox.carry(ref, {
    // The outline keeps up with the geometry step by step rather than being
    // shifted once at the end. It has to: a block can be turned on its way, and
    // a turn happens about where the block is *now* — so a total displacement
    // applied afterwards would land the outline somewhere the block never was.
    move: (dx, dy) => moveCarriedRegion((p) => ({ x: p.x + dx, y: p.y + dy })),
    drop() {
      settle();
      setMessage(`${label} — ${items.length} item${items.length === 1 ? '' : 's'} placed`);
    },
    abandon() {
      opts.abandon?.();
      if (was) setBlockRegion(was.points, was.ref);
      settle();
      setMessage(`${label} — put back`);
    },
  });

  updateStatus();
  draw();
  setMessage(`${label} — carrying ${items.length} item${items.length === 1 ? '' : 's'}`
    + ' · move to the target and click · R turns it, X and Y flip it, Esc puts it back');
}

/** Block ▸ Move — carry everything in the block to where it goes. */
function blockMoveCommand(): void {
  beginBlock('Block Move', (items, _band, ref) => carryBlock('Block Move', items, ref));
}

/**
 * Block ▸ Copy — duplicate everything in the block and carry the copy.
 *
 * The copy is what goes on the cursor, not the original: `blcopy` in the
 * original attaches duplicates and leaves the block where it was. So the
 * duplicates are made at once, on the spot, and then travel — and the block
 * stays round the originals, which is what was copied and may well be copied
 * again.
 *
 * Escape takes the duplicates away rather than leaving them stacked invisibly
 * on what they were copied from. The original's escape drops its cursor
 * attachments; they were never in the file, so nothing is left behind.
 */
function blockCopyCommand(): void {
  beginBlock('Block Copy', (items, _band, ref) => {
    const d = doc();
    history().begin(d, 'Block Copy');
    const made: Entity[] = [];
    for (const e of items) {
      // `instantiate` deep-copies; fresh ids keep the copy a separate item.
      const copy = instantiate(e);
      ensureUniqueIds([copy]);
      made.push(copy);
    }
    d.entities.push(...made);
    history().commit();
    documentChanged();
    invalidateSnap();

    const ids = new Set(made.map((e) => e.id));
    carryBlock('Block Copy', made, ref, {
      moveRegion: false,
      abandon() {
        const live = doc();
        live.entities = live.entities.filter((e) => !ids.has(e.id));
        invalidateSnap();
      },
    });
  });
}

/** Block ▸ Rotate — turn the block a quarter turn about its own centre. */
function blockRotateCommand(): void {
  beginBlock('Block Rotate', (items, band) => {
    const cx = (band.x1 + band.x2) / 2;
    const cy = (band.y1 + band.y2) / 2;
    history().begin(doc(), 'Block Rotate');
    for (const e of items) rotateEntity(e, 90, cx, cy);
    history().commit();
    documentChanged();
    updateInfo();
    draw();
    setMessage(`Block Rotate — ${items.length} item${items.length === 1 ? '' : 's'} turned 90°`);
  });
}

/** Block ▸ Delete — remove everything inside the block. */
function blockDeleteCommand(): void {
  beginBlock('Block Delete', (items) => {
    const d = doc();
    const doomed = new Set(items.map((e) => e.id));
    history().begin(d, 'Block Delete');
    d.entities = d.entities.filter((e) => !doomed.has(e.id));
    history().commit();
    pruneSelection();
    documentChanged();
    updateInfo();
    draw();
    setMessage(`Block Delete — ${doomed.size} item${doomed.size === 1 ? '' : 's'} deleted`);
  });
}

/**
 * Block ▸ Delete Outside — keep the block, remove the rest.
 *
 * This is the one block command that can empty a board in a click, and unlike
 * the others what it destroys is the part you are *not* looking at. It says how
 * much before it does it.
 */
function blockDeleteOutsideCommand(): void {
  beginBlock('Block Delete Outside', (items) => {
    const d = doc();
    const keep = new Set(items.map((e) => e.id));
    const doomed = d.entities.filter((e) => !keep.has(e.id) && selectable(d, e));
    if (doomed.length === 0) { setMessage('Block Delete Outside — nothing outside the block'); return; }
    if (!window.confirm(
      `Delete ${doomed.length} item${doomed.length === 1 ? '' : 's'} outside the block,`
      + ` keeping ${keep.size}?`)) {
      setMessage('Block Delete Outside — cancelled');
      return;
    }
    const gone = new Set(doomed.map((e) => e.id));
    history().begin(d, 'Block Delete Outside');
    d.entities = d.entities.filter((e) => !gone.has(e.id));
    history().commit();
    pruneSelection();
    documentChanged();
    updateInfo();
    draw();
    setMessage(`Block Delete Outside — ${gone.size} deleted, ${keep.size} kept`);
  });
}

/** Block ▸ Copy Clipboard — hold a block for pasting, here or in another file. */
function blockCopyClipboardCommand(): void {
  beginBlock('Block Copy Clipboard', (items, band) => {
    blockClipboard = {
      kind: state.mode,
      items: items.map((e) => instantiate(e)),
      // About the block's own corner, so a paste lands where you point rather
      // than offset by wherever the block happened to sit on the sheet.
      origin: { x: band.x1, y: band.y1 },
    };
    for (const e of blockClipboard.items) {
      translateEntity(e, -band.x1, -band.y1);
    }
    setMessage(`Clipboard — ${items.length} item${items.length === 1 ? '' : 's'} held`);
    draw();
  });
}

/** Block ▸ Paste Clipboard — drop what the clipboard holds at a point. */
function blockPasteClipboardCommand(): void {
  const clip = blockClipboard;
  if (!clip) { setMessage('Clipboard is empty — Block ▸ Copy Clipboard first'); return; }
  if (clip.kind !== state.mode) {
    setMessage(`Clipboard holds ${clip.kind === 'pcb' ? 'board' : 'schematic'} geometry`);
    return;
  }
  cancelPlace();
  toolbox.cancel();
  setMessage(`Paste — where the block's lower-left corner goes (${clip.items.length} items)`);
  clickCapture = (w) => {
    const d = doc();
    const g = d.grid.current;
    const x = snap(w.x, g);
    const y = snap(w.y, g);
    history().begin(d, 'Paste Block');
    const made = clip.items.map((e) => instantiate(e));
    ensureUniqueIds(made);
    for (const e of made) translateEntity(e, x, y);
    d.entities.push(...made);
    history().commit();
    documentChanged();
    state.selected.clear();
    for (const e of made) state.selected.add(e.id);
    updateInfo();
    draw();
    setMessage(`Pasted ${made.length} item${made.length === 1 ? '' : 's'}`);
  };
}

/** Block ▸ Layers — move everything in the block onto one layer. */
function blockLayersCommand(): void {
  beginBlock('Block Layers', (items) => {
    const d = doc();
    openDialog({
      title: `Block Layers — ${items.length} item${items.length === 1 ? '' : 's'}`,
      build(body, handle) {
        const list = document.createElement('div');
        list.className = 'pick-list';
        body.append(list);
        d.layers.forEach((l, i) => {
          const row = document.createElement('button');
          row.type = 'button';
          row.className = 'pick-row';
          const sw = document.createElement('span');
          sw.className = 'st-swatch';
          sw.style.background = `#${(l.screen >>> 0).toString(16).padStart(6, '0').slice(-6)}`;
          const name = document.createElement('span');
          name.textContent = l.name;
          const note = document.createElement('span');
          note.className = 'pick-where';
          note.textContent = layerTypeLabel(l);
          row.append(sw, name, note);
          row.addEventListener('click', () => {
            handle.close();
            history().begin(d, 'Block Layers');
            for (const e of items) e.layer = i;
            history().commit();
            documentChanged();
            updateInfo();
            draw();
            setMessage(`Block Layers — ${items.length} moved to ${l.name}`);
          });
          list.append(row);
        });
        const row = buttons(body);
        button(row, 'Cancel', () => handle.close(), true);
      },
    });
  });
}


/** The last isolation settings used, so a second run does not start over. */
let isolationDefaults: IsolationOptions | null = null;
let isolationTarget: number | null = null;
let isolationOutput: OutputChoice | null = null;

/**
 * Block ▸ Track Isolation — cut a moat round every track and pad.
 *
 * The paths are drawn into the document rather than sent straight to a machine,
 * which is the order the manual sets out: "Once isolated and checked for
 * correctness, the PCB data may then be converted to mill, drill, and route
 * files by using the Isolation Output function." You get to look at what the
 * mill is going to do before it does it.
 */
async function trackIsolationCommand(): Promise<void> {
  if (doc().kind !== 'pcb') { setMessage('Track Isolation is a board command'); return; }
  const c = await camwork();

  beginBlock('Track Isolation', (_items, band) => {
    const d = doc();
    const copper = d.layers.findIndex((l) => l.type === LayerType.Copper);
    const drawing = d.layers.findIndex((l) => l.type === LayerType.Drawing);

    c.showIsolationDialog({
      doc: d,
      units: state.units,
      defaults: isolationDefaults ?? {
        layer: copper >= 0 ? copper : state.activeLayer,
        passes: 2,
        toolSize: 0.01,
        overlap: 0.003,
        clearance: 0,
        floodToolSize: 0,
        floodOverlap: 0.01,
        floodClearance: 0.02,
        floodAngle: 0,
      },
      targetLayer: isolationTarget ?? (drawing >= 0 ? drawing : state.activeLayer),
      onConfirm(opts, target) {
        isolationDefaults = opts;
        isolationTarget = target;

        const began = performance.now();
        const result = c.trackIsolation(d, band, opts);
        const took = Math.round(performance.now() - began);

        if (result.passes.length === 0 && result.flood.length === 0) {
          setMessage(result.warnings[0] ?? 'Track Isolation — nothing to cut');
          return;
        }

        history().begin(d, 'Track Isolation');
        // Each contour becomes a run of segments on the chosen layer, drawn at
        // the tool's own width, so what is on screen is the width of the cut.
        for (const path of result.passes) {
          for (let i = 0; i + 1 < path.length; i++) {
            d.entities.push({
              kind: 'line', id: newId(), layer: target, width: opts.toolSize,
              x1: path[i]!.x, y1: path[i]!.y, x2: path[i + 1]!.x, y2: path[i + 1]!.y,
            });
          }
        }
        for (const s of result.flood) {
          d.entities.push({
            kind: 'line', id: newId(), layer: target, width: opts.floodToolSize,
            x1: s.a.x, y1: s.a.y, x2: s.b.x, y2: s.b.y,
          });
        }
        history().commit();
        documentChanged();
        updateInfo();
        draw();

        const note = result.warnings.length ? ` — ${result.warnings[0]}` : '';
        setMessage(
          `Track Isolation: ${result.passes.length} path${result.passes.length === 1 ? '' : 's'}, `
          + `${result.flood.length} flood stroke${result.flood.length === 1 ? '' : 's'}, `
          + `${(result.cell * 1000).toFixed(1)} thou grid, ${took} ms${note}`);
      },
    });
  });
}

/**
 * Block ▸ Isolation Output — send the cut paths to the machine.
 *
 * It plots what is on the layer rather than working it out again: the manual
 * says an isolation file is generated "from all lines and arcs within the
 * containment block on the layer specified", and that separation is what lets
 * you correct a path by hand and have the correction honoured.
 */
/**
 * The order isolation paths are cut in, kept for the next output.
 *
 * Beside `isolationOutput` rather than on the document: it is a fact about the
 * machine and the tool, not about the board, and the same board sent to two
 * mills wants two answers.
 */
let isolationOrder: SequenceOptions = { nearest: true, reverse: true };

/**
 * Block ▸ Isolation Sequencing (B Q) — what order to cut in.
 *
 * A block first, as the original requires: sequencing is about the paths inside
 * the region being milled, and "all of them" is not a region.
 */
async function isolationSequenceCommand(): Promise<void> {
  const c = await camwork();
  if (doc().kind !== 'pcb') { setMessage('Isolation Sequencing is a board command'); return; }

  beginBlock('Isolation Sequencing', (items, band) => {
    const d = doc();
    void items;
    // Every layer the output would write, together: the order is one journey
    // per file, and the layer that will be cut is the one worth measuring.
    const layer = isolationOutput?.topLayer
      ?? d.layers.findIndex((l) => l.type === LayerType.Drawing);
    const paths = millPaths(d, band, layer >= 0 ? layer : state.activeLayer);

    c.showSequenceDialog({
      paths: paths.length,
      current: isolationOrder,
      measure: (opts) => c.sequencePaths(paths, { ...opts, start: { x: band.x1, y: band.y1 } }),
      formatLength: (inches) => formatDisplay(inches, state.units),
      onConfirm: (opts) => {
        isolationOrder = opts;
        const r = c.sequencePaths(paths, { ...opts, start: { x: band.x1, y: band.y1 } });
        setMessage(paths.length === 0
          ? 'No isolation paths in the block'
          : `Cutting order set — travel ${formatDisplay(r.after, state.units)}`
            + ` of ${formatDisplay(r.before, state.units)}`);
      },
    });
  });
}

async function isolationOutputCommand(): Promise<void> {
  const c = await camwork();
  if (doc().kind !== 'pcb') { setMessage('Isolation Output is a board command'); return; }

  beginBlock('Isolation Output', (items, band) => {
    const d = doc();
    const drawing = d.layers.findIndex((l) => l.type === LayerType.Drawing);
    const bottom = d.layers.findIndex((l) => l.type === LayerType.Copper && l.bottom);

    c.showIsolationOutputDialog({
      doc: d,
      defaults: isolationOutput ?? {
        drill: true,
        drillMirror: false,
        top: true,
        topLayer: drawing >= 0 ? drawing : state.activeLayer,
        topMirror: false,
        bottom: false,
        bottomLayer: bottom >= 0 ? bottom : state.activeLayer,
        bottomMirror: true,
        route: false,
        routeLayer: drawing >= 0 ? drawing : state.activeLayer,
        format: 'gcode',
      },
      onConfirm(choice) {
        isolationOutput = choice;
        // The board is turned over about its own middle, so a mirrored side
        // lands back on the same fixture rather than somewhere off the table.
        const about = (band.x1 + band.x2) / 2;
        const written: string[] = [];

        const send = (suffix: string, job: MillJob, mirror: boolean): void => {
          if (job.paths.length === 0 && job.holes.length === 0) return;
          // In the order Isolation Sequencing settled on. Done here rather than
          // where the paths are collected so that every file gets it, and so
          // that the order is decided once for the whole job.
          job = {
            ...job,
            paths: c.sequencePaths(job.paths, {
              ...isolationOrder,
              start: { x: band.x1, y: band.y1 },
            }).paths,
          };
          const text = c.millFile(job, choice.format, { mirror, mirrorAbout: about });
          const stem = (projectName ?? d.name ?? 'board').replace(/[^\w.-]+/g, '_');
          const name = `${stem}-${suffix}${c.MILL_SUFFIX[choice.format]}`;
          downloadText(name, text);
          written.push(name);
        };

        if (choice.top) {
          send('t', { paths: millPaths(d, band, choice.topLayer), holes: [] }, choice.topMirror);
        }
        if (choice.bottom) {
          send('b', { paths: millPaths(d, band, choice.bottomLayer), holes: [] }, choice.bottomMirror);
        }
        if (choice.route) {
          send('r', { paths: millPaths(d, band, choice.routeLayer), holes: [] }, false);
        }
        if (choice.drill) {
          send('d', { paths: [], holes: millHoles(d, band) }, choice.drillMirror);
        }

        setMessage(written.length
          ? `Isolation Output — wrote ${written.join(', ')}`
          : `Isolation Output — nothing on those layers inside the block (${items.length} items)`);
      },
    });
  });
}

/**
 * Lines and arcs on one layer, inside the block, as paths for the machine.
 *
 * Segments that share an end are joined into one path, so the tool cuts a
 * contour in a single travel rather than lifting between every pair of points —
 * which is how the isolation paths were laid down in the first place.
 */
function millPaths(
  d: CadDocument, band: { x1: number; y1: number; x2: number; y2: number }, layer: number,
): Vertex[][] {
  const inside = (x: number, y: number): boolean =>
    x >= band.x1 && x <= band.x2 && y >= band.y1 && y <= band.y2;

  const segs: [Vertex, Vertex][] = [];
  for (const e of d.entities) {
    if (e.layer !== layer) continue;
    if (e.kind === 'line') {
      if (!inside(e.x1, e.y1) || !inside(e.x2, e.y2)) continue;
      segs.push([{ x: e.x1, y: e.y1 }, { x: e.x2, y: e.y2 }]);
    } else if (e.kind === 'arc') {
      // Five degrees a chord: finer than any mill will notice, coarse enough
      // that a full circle is seventy-odd moves rather than hundreds.
      const sweep = e.sweep === 0 ? 360 : e.sweep;
      const steps = Math.max(8, Math.ceil(Math.abs(sweep) / 5));
      let prev: Vertex | null = null;
      for (let k = 0; k <= steps; k++) {
        const a = ((e.angle + (sweep * k) / steps) * Math.PI) / 180;
        const p = { x: e.cx + e.radius * Math.cos(a), y: e.cy + e.radius * Math.sin(a) };
        if (prev && inside(prev.x, prev.y) && inside(p.x, p.y)) segs.push([prev, p]);
        prev = p;
      }
    }
  }
  return joinSegments(segs);
}

/** Chain segments end to end so the tool travels as little as possible. */
function joinSegments(segs: [Vertex, Vertex][]): Vertex[][] {
  const EPS = 1e-6;
  const key = (p: Vertex): string =>
    Math.round(p.x / EPS) + ',' + Math.round(p.y / EPS);

  const ends = new Map<string, number[]>();
  segs.forEach((s, i) => {
    for (const p of s) {
      const k = key(p);
      const list = ends.get(k);
      if (list) list.push(i);
      else ends.set(k, [i]);
    }
  });

  const used = new Uint8Array(segs.length);
  const out: Vertex[][] = [];
  for (let i = 0; i < segs.length; i++) {
    if (used[i]) continue;
    used[i] = 1;
    const path: Vertex[] = [segs[i]![0], segs[i]![1]];
    for (const forward of [true, false]) {
      for (;;) {
        const tip = forward ? path[path.length - 1]! : path[0]!;
        const next = (ends.get(key(tip)) ?? []).find((n) => !used[n]);
        if (next === undefined) break;
        used[next] = 1;
        const s = segs[next]!;
        const far = key(s[0]) === key(tip) ? s[1]! : s[0]!;
        if (forward) path.push(far);
        else path.unshift(far);
      }
    }
    out.push(path);
  }
  return out;
}

/** Through-holes inside the block, for the drill file. */
function millHoles(
  d: CadDocument, band: { x1: number; y1: number; x2: number; y2: number },
): { x: number; y: number; diameter: number }[] {
  const out: { x: number; y: number; diameter: number }[] = [];
  const walk = (list: readonly Entity[], owner: ComponentEntity | null): void => {
    for (const e of list) {
      if (e.kind === 'component') { walk(e.children, e); continue; }
      if (e.kind !== 'pad' || !e.hole || !padThrough(e)) continue;
      const p = owner ? placeInComponent({ x: e.x, y: e.y }, owner) : { x: e.x, y: e.y };
      if (p.x < band.x1 || p.x > band.x2 || p.y < band.y1 || p.y > band.y2) continue;
      out.push({ x: p.x, y: p.y, diameter: e.hole });
    }
  };
  walk(d.entities, null);
  return out;
}

/** Block ▸ Nest Component — make a library part out of a block of geometry. */
function blockNestCommand(): void {
  beginBlock('Nest Component', (items) => {
    // Build Component already does this from the selection, dialog and all, so
    // marking a block is simply another way of choosing what goes in.
    state.selected.clear();
    for (const e of items) state.selected.add(e.id);
    updateInfo();
    draw();
    buildComponentCommand();
  });
}

function beginZoomWindow(): void {
  setMessage('Select first corner point');
  clickCapture = (a) => {
    setMessage('Select second corner point');
    clickCapture = (b) => {
      viewport.rememberView();
      viewport.fit(Math.min(a.x, b.x), Math.min(a.y, b.y), Math.max(a.x, b.x), Math.max(a.y, b.y));
      updateStatus();
      draw();
      setMessage('Zoomed to window');
    };
  };
}

/**
 * Zoom ▸ Board — frame the physical board rather than the whole sheet.
 *
 * Extents fits everything drawn, which on a CIRCAD file includes the drawing
 * sheet, its title block and the notes beside it, so the board itself ends up a
 * small patch in the middle. This fits the outline, which is what "the board"
 * means for size, plotting and pour. With no outline drawn there is nothing to
 * distinguish it from Extents, so it says so and behaves as Extents.
 */
/**
 * Zoom ▸ Scale (Z S) — set the magnification by typing it.
 *
 * The original asks for `wp->XYzoom` outright, which is pixels per inch, and
 * seeds the box with what it is now (OGmenu11.cpp:75, OGdlg02.cpp:573). The
 * status bar has been showing that number all along, so this is the way to put
 * one back: to draw a footprint at a known size on screen, or to come back to a
 * magnification you were working at an hour ago.
 *
 * About the cursor, as the original is: it holds the point under the pointer
 * still, so typing a number does not also lose your place. With the pointer off
 * the canvas it holds the middle instead.
 */
function zoomScaleCommand(): void {
  const now = viewport.scale;
  const raw = window.prompt(
    'Pixels per inch? (the number in the status bar)', String(Math.round(now)));
  if (raw === null) return;
  const want = parseFloat(raw);
  if (!Number.isFinite(want) || want <= 0) {
    setMessage('A scale is a positive number of pixels per inch');
    return;
  }

  viewport.rememberView();
  const at = cursorWorld;
  const sx = at ? viewport.toScreenX(at.x) : viewport.width / 2;
  const sy = at ? viewport.toScreenY(at.y) : viewport.height / 2;
  viewport.zoomAt(sx, sy, want / now);
  pauseRatsnest();
  updateStatus();
  draw(true);
  // What it actually landed on: the viewport clamps, so asking for a millionth
  // or a million gets you the nearest it will go and should say so.
  setMessage(`Scale ${Math.round(viewport.scale)} pixels per inch`);
}

/**
 * Zoom ▸ Absolute (Z A) — the photograph at its own resolution.
 *
 * Not a second way of typing a scale, which is what the name suggests and what
 * this page used to claim. The original calls it Zoom PIX 1:1 and it is about
 * the raster underlay: it finds the picture under the cursor, or the topmost
 * one, and sets the magnification so that one pixel of the image is one pixel
 * of the screen — `dz = 1.0 / fx.dpp`, then centres on it (OGmenu11.cpp:110).
 *
 * Which is exactly the magnification you want while tracing. Any more and you
 * are reading the scanner's noise; any less and you are guessing at where the
 * track really runs. The photo carries `resolution` in inches per pixel, so the
 * scale that shows it true is one over that.
 */
function zoomImageActualSize(): void {
  const d = doc();
  const refs = d.images ?? [];
  if (refs.length === 0) {
    setMessage('No photograph to zoom to — Views ▸ Photo Underlay lays one down');
    return;
  }

  // The one under the cursor, as the original scans for; otherwise the last
  // laid down, which is the one on top.
  const at = cursorWorld;
  const under = at ? refs.find((r) => {
    const w = r.width * r.resolution;
    const h = r.height * (r.resolutionY ?? r.resolution);
    return at.x >= r.x && at.x <= r.x + w && at.y >= r.y && at.y <= r.y + h;
  }) : undefined;
  const ref = under ?? refs[refs.length - 1]!;

  const perPixel = ref.resolution;
  if (!Number.isFinite(perPixel) || perPixel <= 0) {
    setMessage('That photograph has no resolution set — scale it first');
    return;
  }

  viewport.rememberView();
  // Held to the same limits the wheel and the zoom commands work within: a
  // photograph recorded at some absurd resolution should not be able to put the
  // view somewhere nothing else can reach.
  viewport.scale = Math.max(4, Math.min(40000, 1 / perPixel));
  viewport.cx = ref.x + (ref.width * perPixel) / 2;
  viewport.cy = ref.y + (ref.height * (ref.resolutionY ?? perPixel)) / 2;
  pauseRatsnest();
  updateStatus();
  draw();
  setMessage(`${ref.name} at its own size — one image pixel to one on screen`);
}

function zoomBoard(): void {
  const { rect, fromOutline } = boardExtent(doc());
  if (!rect) { setMessage('Nothing to zoom to'); return; }
  viewport.rememberView();
  viewport.fit(rect.x1, rect.y1, rect.x2, rect.y2);
  updateStatus();
  draw();
  setMessage(fromOutline
    ? `Board: ${formatDisplay(rect.x2 - rect.x1, state.units)}`
      + ` × ${formatDisplay(rect.y2 - rect.y1, state.units)}`
    : 'No board outline drawn — zoomed to extents');
}

function zoomFit(): void {
  const d = doc();
  let x1 = Infinity, y1 = Infinity, x2 = -Infinity, y2 = -Infinity;

  for (const e of d.entities) {
    const b = e.kind === 'component' ? componentRect(e) : entityExtent(e);
    if (!b) continue;
    x1 = Math.min(x1, b.x1); x2 = Math.max(x2, b.x2);
    y1 = Math.min(y1, b.y1); y2 = Math.max(y2, b.y2);
  }

  if (!Number.isFinite(x1)) {
    viewport.cx = 0;
    viewport.cy = 0;
    viewport.scale = 300;
  } else {
    viewport.fit(x1, y1, x2, y2);
  }
  updateStatus();
  draw();
}

/** Rough extent for zoom-to-fit; picking uses the more careful version. */
function entityExtent(e: Entity): { x1: number; y1: number; x2: number; y2: number } | null {
  switch (e.kind) {
    case 'line':
      return {
        x1: Math.min(e.x1, e.x2), y1: Math.min(e.y1, e.y2),
        x2: Math.max(e.x1, e.x2), y2: Math.max(e.y1, e.y2),
      };
    case 'arc':
      return {
        x1: e.cx - e.radius, y1: e.cy - e.radius,
        x2: e.cx + e.radius, y2: e.cy + e.radius,
      };
    case 'pad':
      return {
        x1: e.x - e.sizeX / 2, y1: e.y - e.sizeY / 2,
        x2: e.x + e.sizeX / 2, y2: e.y + e.sizeY / 2,
      };
    case 'text':
      return { x1: e.x, y1: e.y, x2: e.x, y2: e.y + e.height };
    default:
      return null;
  }
}

// --- Mode switching ---------------------------------------------------------

// The editors are reached from the document bar, which `renderDocTabs` builds.

el<HTMLInputElement>('opt-ortho').addEventListener('change', (ev) => {
  // The checkbox is on/off, so it can only say 'none' or 'all four'; the
  // status field is where the middle two are chosen.
  state.ortho = (ev.target as HTMLInputElement).checked ? 3 : 0;
  // The status bar names the setting now, so it has to hear about this route
  // too — otherwise the field says one thing and the line tools do another.
  updateStatus();
});

el<HTMLInputElement>('opt-rats').addEventListener('change', (ev) => {
  state.showRatsnest = (ev.target as HTMLInputElement).checked;
  resumeRatsnest();
  updateRatsnestStatus();
  draw();
});

// --- Status-bar pickers -----------------------------------------------------
// The original's size, grid-snap and layer popups (OG3.cpp): click the field,
// pick from what the document already uses. The size list is how you lay a
// wider track — pick a bigger width, then draw.

el('st-size').addEventListener('click', (ev) => {
  ev.stopPropagation();
  showSizePicker(el('st-size'), pickerContext());
});

el('st-grid').addEventListener('click', (ev) => {
  ev.stopPropagation();
  showGridPicker(el('st-grid'), pickerContext());
});

el('st-layer').addEventListener('click', (ev) => {
  ev.stopPropagation();
  showLayerPicker(el('st-layer'), pickerContext(), state.activeLayer);
});

el('st-units').addEventListener('click', (ev) => {
  ev.stopPropagation();
  cycleUnits();
});

el('st-snap').addEventListener('click', (ev) => {
  ev.stopPropagation();
  setSnap(!state.snap);
});

el('st-ortho').addEventListener('click', (ev) => {
  ev.stopPropagation();
  cycleOrtho();
});

el('st-pins').addEventListener('click', (ev) => {
  ev.stopPropagation();
  setShowPins(!state.showPins);
});

el('st-scope').addEventListener('click', (ev) => {
  ev.stopPropagation();
  togglePickScope();
});

el('st-sheet').addEventListener('click', (ev) => {
  ev.stopPropagation();
  sheetPicker();
});

// --- Search -----------------------------------------------------------------

let searchTimer = 0;
el<HTMLInputElement>('part-search').addEventListener('input', (ev) => {
  const q = (ev.target as HTMLInputElement).value;
  window.clearTimeout(searchTimer);
  searchTimer = window.setTimeout(() => void refreshPartSearch(q), 160);
});

// Enter imports an LCSC part number, or searches LCSC online by name.
el<HTMLInputElement>('part-search').addEventListener('keydown', (ev) => {
  if (ev.key !== 'Enter') return;
  const q = (ev.target as HTMLInputElement).value.trim();
  if (isLcscId(q)) { ev.preventDefault(); void importLcsc(q); }
  else if (q.length >= 2 && user) { ev.preventDefault(); void searchLcsc(q); }
});

async function refreshPartSearch(q: string): Promise<void> {
  const query = q.trim();
  if (query.length < 2) {
    await showLibrary(el<HTMLSelectElement>('library-select').value);
    return;
  }
  const rows = await parts.search(query);
  // Search spans every library, so filter to the current editor.
  const matches = rows.filter((r) => r.kind === state.mode);
  currentParts = matches;
  renderPartList(matches);
  const box = el('part-list');
  if (isLcscId(query)) {
    // A bare LCSC number imports directly.
    prependLcscImport(query);
  } else if (user) {
    // Otherwise offer an online search by name, at the top so it is visible
    // above however many local matches there are.
    const btn = document.createElement('button');
    btn.className = 'part lcsc-import';
    btn.textContent = `⌕  Search LCSC online for “${query}”`;
    btn.addEventListener('click', () => void searchLcsc(query));
    box.prepend(btn);
  }
  const hidden = rows.length - matches.length;
  setMessage(
    `${matches.length} match${matches.length === 1 ? '' : 'es'}`
    + (hidden > 0
      ? ` · ${hidden} in ${state.mode === 'sch' ? 'PCB' : 'schematic'} libraries hidden`
      : ''),
  );
}

/** Put an "Import from LCSC" action at the top of the part list. */
function prependLcscImport(id: string): void {
  const box = el('part-list');
  const btn = document.createElement('button');
  btn.className = 'part lcsc-import';
  btn.textContent = `⤓  Import ${id.toUpperCase()} from LCSC`;
  btn.addEventListener('click', () => void importLcsc(id));
  box.prepend(btn);
}

/** Run an online LCSC keyword search and list the results, each importable. */
async function searchLcsc(query: string): Promise<void> {
  const box = el('part-list');
  if (!user) { note(box, 'Sign in to search LCSC.'); return; }

  // Immediate feedback in the list, so the click is visibly acknowledged even
  // while the request is in flight.
  box.replaceChildren();
  note(box, `Searching LCSC for “${query}”…`);
  setMessage(`Searching LCSC for “${query}”…`);

  try {
    const res = await parts.lcscSearch(query);
    const hits = res.results ?? [];
    box.replaceChildren();

    const head = document.createElement('div');
    head.className = 'part-group';
    head.textContent = (hits.length
      ? `LCSC — ${hits.length} result${hits.length === 1 ? '' : 's'}`
      : 'LCSC — no results')
      + (res.source ? ` · via ${res.source.replace(/^https?:\/\//, '')}` : '');
    box.append(head);

    if (hits.length === 0) {
      note(box, `No LCSC parts matched “${query}”. Try a manufacturer part number or a value like “0.1uF 0603”.`);
      // Surface the proxy diagnostics so a wrong endpoint is visible.
      if (res.tried?.length) {
        note(box, 'Endpoints tried: ' + res.tried.map((t) => `${t.url} → ${t.status}`).join('  ·  '));
      }
      if (res.shape?.length) note(box, 'Response shape: ' + res.shape.join(', '));
    }
    for (const hit of hits) {
      const row = document.createElement('button');
      row.className = 'part lcsc-hit';
      const nm = document.createElement('span');
      nm.className = 'nm';
      nm.textContent = hit.lcsc;
      const ds = document.createElement('span');
      ds.className = 'ds';
      ds.textContent = [hit.title, hit.package, hit.description].filter(Boolean).join(' · ');
      row.append(nm, ds);
      row.title = `Import ${hit.lcsc}`;
      row.addEventListener('click', () => void importLcsc(hit.lcsc));
      box.append(row);
    }
    setMessage(hits.length
      ? `${hits.length} LCSC result${hits.length === 1 ? '' : 's'} — click one to import`
      : `No LCSC parts for “${query}”`);
  } catch (e) {
    box.replaceChildren();
    note(box, `LCSC search failed: ${(e as Error).message}`);
    setMessage(`LCSC search failed: ${(e as Error).message}`);
  }
}

/** A plain message row inside the part list. */
function note(box: HTMLElement, text: string): void {
  const p = document.createElement('div');
  p.className = 'part-group';
  p.style.whiteSpace = 'normal';
  p.textContent = text;
  box.append(p);
}

el<HTMLSelectElement>('library-select').addEventListener('change', (ev) => {
  void showLibrary((ev.target as HTMLSelectElement).value);
});

// --- Account and persistence ------------------------------------------------

/** Signed-in user, or null when browsing anonymously. */
let user: User | null = null;
/** Open project, and the server-side id of each document kind. */
let projectId: number | null = null;
let projectName: string | null = null;
const documentIds: Partial<Record<DocumentKind, number>> = {};
let dirty = false;

/**
 * Record that the document changed.
 *
 * Every mutation must come through here. The snap cache in particular has to
 * be invalidated on *each* change, not just the first: it was previously
 * refreshed only from the tool commit path, so placing a component left the
 * cache stale and its pads unsnappable. Routing within the first component
 * worked and routing to the next one silently missed its pads by a fraction
 * of the grid, leaving the ratline in place.
 *
 * `markDirty` cannot own this, because it returns early once already dirty.
 */

/**
 * Documents already asked about, so the question is put once.
 *
 * A board with a photograph reference is asked after the first time it becomes
 * the working document — which is right after it is opened, whichever of the
 * several routes opened it. Hanging the check here rather than on each of those
 * routes means a new one cannot forget to do it; a weak set means a closed
 * document is not kept alive by having been asked.
 */
const askedAboutPhotos = new WeakSet<CadDocument>();

/** Put the photograph question to a document that has just come into view. */
function maybeOfferPhotos(): void {
  const d = doc();
  if (askedAboutPhotos.has(d)) return;
  askedAboutPhotos.add(d);
  if (photoMissing(d).length === 0) return;
  // After the frame that brought it on screen: being asked about a picture
  // before the board it belongs to has been drawn is disorienting.
  setTimeout(() => { void offerPhotos(); }, 0);
}
function documentChanged(): void {
  ensureDocumentOpen();
  // The bar is derived from what the documents hold, so an edit can change it —
  // filling a board that was never opened gives it a button. Rebuilding a
  // handful of buttons per edit is cheap, and it keeps the bar honest whatever
  // route the change came in by.
  renderDocTabs();
  invalidateSnap();
  markDirty();
  maybeOfferPhotos();
}

/**
 * Adopt the document being edited if it is not open yet.
 *
 * The app boots with nothing open, so the document bar is empty — but the tools
 * still work, and an edit made before opening anything would otherwise sit in a
 * document that Save does not know about and would be lost. Editing counts as
 * opening.
 */
function ensureDocumentOpen(): void {
  if (state.mode === 'sch') {
    if (sheets.length > 0) return;
    resetSheets();
  } else {
    if (boardOpen) return;
    boardOpen = true;
  }
  renderDocTabs();
  updateSheetStatus();
}

/**
 * Sign out, whatever the server says.
 *
 * Ending the session here is the part that matters, and the round trip that
 * clears the cookie is best-effort: both callers used to be
 * `void Session.logout().then(…)` with nothing catching, so signing out with
 * the server unreachable — or with a session that had already lapsed, which
 * answers 401 — threw an unhandled rejection, left the person signed in, and
 * said nothing at all about why.
 */
async function signOut(): Promise<void> {
  let note = 'Signed out';
  try {
    await Session.logout();
  } catch (err) {
    note = `Signed out here, but the server was not told: ${(err as Error).message}`;
  }
  user = null;
  projectId = null;
  dirty = false;
  renderAccount();
  updateSaveButton();
  setMessage(note);
}

function markDirty(): void {
  // An edit of the operator's own ends any walk back through saved states: the
  // drawing is theirs again from here, and this change is what commits
  // wherever they had got back to.
  revisionWalk = null;
  // The autosave is armed on every change, not only the first: a burst of edits
  // must keep pushing the pause out, or the first one would start a save while
  // the rest were still arriving.
  scheduleAutosave();
  if (dirty) return;
  dirty = true;
  updateSaveButton();
}

// --- Autosave ---------------------------------------------------------------

/**
 * The work goes to the account on its own.
 *
 * Not a numbered backup on a timer, which is what the original does and which
 * leaves you choosing between files named after clock times. Every change goes
 * up a moment after it is made, and the server keeps the state it replaced —
 * `save_document` writes the old body to `document_revisions` before
 * overwriting, fifty deep. So the history is on the server as a side effect of
 * saving, and there is nothing for anyone to name or choose.
 *
 * A pause rather than a save per keystroke. Dragging a track fires a change per
 * mouse move, and each is a whole document over the wire; waiting for the hand
 * to stop turns a hundred of those into one. Long enough to catch a gesture,
 * short enough that stepping away for a moment leaves the work safe.
 */
const AUTOSAVE_PAUSE_MS = 1500;
let autosaveTimer = 0;
/** True while a save is in flight, so two cannot overlap. */
let autosaveBusy = false;
/** A change arrived mid-save, so another is owed as soon as this one lands. */
let autosaveAgain = false;

type CloudState = 'off' | 'dirty' | 'saving' | 'saved' | 'failed';

/**
 * Whether there is anywhere to save to.
 *
 * Autosave never asks a question. With nobody signed in, or before a project
 * exists, saving would have to stop and prompt for a name — so it does nothing
 * at all and the cloud says so. The Save button is the way in: it makes the
 * project, and from then on this takes over.
 */
function canAutosave(): boolean {
  return user !== null && projectId !== null;
}

function scheduleAutosave(): void {
  if (!canAutosave()) { setCloud('off'); return; }
  setCloud('dirty');
  if (autosaveTimer) window.clearTimeout(autosaveTimer);
  autosaveTimer = window.setTimeout(() => {
    autosaveTimer = 0;
    void runAutosave();
  }, AUTOSAVE_PAUSE_MS);
}

async function runAutosave(): Promise<void> {
  if (!canAutosave() || !dirty) return;
  // One at a time. A save takes as long as it takes, and starting a second
  // while the first is still going would race two writes of the same document
  // — the loser's revision landing on top of the winner's.
  if (autosaveBusy) { autosaveAgain = true; return; }

  autosaveBusy = true;
  setCloud('saving');
  try {
    await saveProject({ quiet: true });
    setCloud(dirty ? 'dirty' : 'saved');
  } catch (err) {
    // Said in the cloud's title rather than the message line: an autosave that
    // shouts every time the network hiccups would bury whatever the operator
    // was actually doing. The next change tries again.
    setCloud('failed', (err as Error).message);
  } finally {
    autosaveBusy = false;
    if (autosaveAgain) {
      autosaveAgain = false;
      scheduleAutosave();
    }
  }
}

/** Save now rather than waiting out the pause. */
function autosaveNow(): void {
  if (autosaveTimer) { window.clearTimeout(autosaveTimer); autosaveTimer = 0; }
  void runAutosave();
}

function setCloud(state: CloudState, detail = ''): void {
  const box = el('cloud');
  box.className = `cloud ${state}`;
  const said: Record<CloudState, string> = {
    off: user
      ? 'Not saving yet — press Save to start a project, and it saves itself from then on'
      : 'Not saving — sign in and your work goes to your account as you make it',
    dirty: 'Changes not saved yet — they go up in a moment, or click to send them now',
    saving: 'Saving to your account…',
    saved: 'Saved to your account',
    failed: `Could not save${detail ? `: ${detail}` : ''} — click to try again`,
  };
  box.title = said[state];
  const label = box.querySelector('.sr-only');
  if (label) label.textContent = said[state];
}

/** Bring the cloud in line with the state, without arming a save. */
function refreshCloud(): void {
  if (autosaveBusy) return;
  if (!canAutosave()) { setCloud('off'); return; }
  setCloud(dirty ? 'dirty' : 'saved');
}

function updateSaveButton(): void {
  const btn = el<HTMLButtonElement>('btn-save');
  // Enabled without a project too: Save then becomes Save to Project, which is
  // what saveProject already does. Requiring a project first left the button
  // greyed out with a dot beside it, so there was no way to press the thing
  // that would have made the project — a text file could never reach the web.
  btn.disabled = !user || !dirty;
  btn.textContent = dirty ? 'Save •' : 'Save';
  // The cloud is the same state in another form, so it follows the one function
  // that already runs wherever that state changes.
  refreshCloud();
}

function renderAccount(): void {
  const box = el('account');
  box.replaceChildren();

  if (!user) {
    const btn = document.createElement('button');
    btn.className = 'btn';
    btn.textContent = 'Sign in';
    btn.addEventListener('click', () => {
      showAccountDialog((u) => {
        user = u;
        renderAccount();
        updateSaveButton();
        setMessage(`Signed in as ${u.display_name || u.email}`);
      });
    });
    box.append(btn);
    return;
  }

  const who = document.createElement('span');
  who.className = 'who';
  const name = document.createElement('b');
  name.textContent = user.display_name || user.email;
  who.append(name);

  const out = document.createElement('button');
  out.className = 'btn';
  out.textContent = 'Sign out';
  out.addEventListener('click', () => { void signOut(); });

  box.append(who, out);
}

/** How many the original's list holds: F0 through F9. */
const RECENT_MAX = 10;

/**
 * Put a project at the top of the Re-Open list.
 *
 * Projects rather than filenames, which is what the original remembered. A
 * browser cannot reopen a path — it was handed a file, not a place — so a
 * filename in this list would be a line you could read and not act on. A
 * project has an id the account answers to.
 */
function rememberProject(id: number, name: string): void {
  const rest = (prefs.recentProjects ?? []).filter((r) => r.id !== id);
  prefs = { ...prefs, recentProjects: [{ id, name }, ...rest].slice(0, RECENT_MAX) };
  savePrefs(prefs);
  applyFileMenuLayout();
}

/** File ▸ Re-Open N — the nth project on the list. */
async function reopenRecent(n: number): Promise<void> {
  const entry = (prefs.recentProjects ?? [])[n];
  if (!entry) { setMessage('Nothing at that place on the list'); return; }
  if (dirty && !window.confirm(`Open ${entry.name}? Unsaved changes will be lost.`)) return;
  try {
    openProject(await Projects.open(entry.id));
  } catch (err) {
    // A project that has been deleted is the commonest reason, and leaving its
    // name on a list that cannot open it is worse than dropping it.
    setMessage(`Could not open ${entry.name}: ${(err as Error).message}`);
  }
}

/** File ▸ Purge All Files — forget the list. */
function purgeRecent(): void {
  const n = (prefs.recentProjects ?? []).length;
  prefs = { ...prefs, recentProjects: [] };
  savePrefs(prefs);
  applyFileMenuLayout();
  setMessage(n === 0 ? 'The list is already empty' : `Forgot ${n} project${n === 1 ? '' : 's'}`);
}

/**
 * File ▸ Purge Old Files — drop the ones that are no longer there.
 *
 * The original's words are "purge non-existing data files". Here that means
 * asking the account which projects it still has, since a project deleted from
 * another machine leaves a name on this list that opens nothing.
 */
async function purgeMissingRecent(): Promise<void> {
  const list = prefs.recentProjects ?? [];
  if (list.length === 0) { setMessage('The list is already empty'); return; }
  try {
    const alive = new Set((await Projects.list()).map((p) => p.id));
    const kept = list.filter((r) => alive.has(r.id));
    const gone = list.length - kept.length;
    prefs = { ...prefs, recentProjects: kept };
    savePrefs(prefs);
    applyFileMenuLayout();
    setMessage(gone === 0
      ? 'Every project on the list is still there'
      : `Dropped ${gone} project${gone === 1 ? '' : 's'} that no longer exists`);
  } catch (err) {
    setMessage(`Could not check the list: ${(err as Error).message}`);
  }
}

function openProject(p: LoadedProject): void {
  let repairs = 0;
  /** Documents this build is too old to read, named so the user knows which. */
  const tooNew: string[] = [];
  projectId = p.project.id;
  projectName = p.project.name;
  rememberProject(p.project.id, p.project.name);
  // The last project's text files are not this one's, and they carry row ids
  // belonging to it — kept, the next Save would write them back over it, and a
  // pending removal would delete out of it.
  removedDocs = [];
  textDocs = [];
  activeText = null;
  editorShowing = null;
  showTextView();
  delete documentIds.sch;
  delete documentIds.pcb;

  // Schematic documents are the design's sheets, in the server's sheet order.
  const loadedSheets: { doc: CadDocument; id?: number }[] = [];
  const ordered = [...p.documents].sort((a, b) => (a.sheet ?? 0) - (b.sheet ?? 0));

  for (const d of ordered) {
    // A text file is not a drawing and has none of a drawing's machinery.
    if (d.kind === 'txt') {
      const t = d.data as TextDocumentData;
      openTextDoc({
        id: `doc-${d.id}`,
        docId: d.id,
        title: t.name || d.name,
        file: t.file || d.name,
        mime: 'text/plain',
        text: t.text ?? '',
        ...(t.lang ? { lang: t.lang } : {}),
      });
      continue;
    }
    const drawing = d.data as CadDocument;
    // A newly created project has empty document payloads; keep the blank
    // document we already have rather than replacing it with `{}`.
    let usable = drawing && typeof drawing === 'object' && Array.isArray(drawing.entities);
    if (usable) {
      // Bring it up to the model this build knows, and seed the id counter past
      // what it holds. A document from a *newer* build is not opened at all:
      // reading it here would drop whatever this build does not understand, and
      // the next save would write that loss back over the good copy.
      try {
        repairs += migrateDocument(drawing).changed;
      } catch (err) {
        if (!(err instanceof FutureVersionError)) throw err;
        tooNew.push(d.name);
        usable = false;
      }
    }
    if (d.kind === 'sch') {
      const sheet = usable ? applyUserDisplay(drawing) : blankDocument('sch', d.name);
      sheet.name = sheet.name || d.name;
      loadedSheets.push({ doc: sheet, id: d.id });
    } else {
      documentIds.pcb = d.id;
      boardOpen = true;
      if (usable) state.docs.pcb = applyUserDisplay(drawing);
      else state.docs.pcb.name = d.name;
    }
  }

  if (loadedSheets.length > 0) {
    sheets = loadedSheets;
    useSheet(0);
  } else {
    resetSheets();
  }

  // One branch above keeps the existing document object, so identity alone
  // will not retire the snap cache.
  invalidateSnap();
  histories.sch.clear();
  histories.pcb.clear();
  state.selected.clear();
  dirty = false;
  // Open on the layer the loaded drawing names, not whatever was active before.
  state.activeLayer = workingLayer(doc());
  toolbox.refreshLayer();

  renderDocTabs();
  buildLayerPanel();
  updateInfo();
  updateSheetStatus();
  updateStatus();
  updateSaveButton();
  zoomFit();
  // A document this build cannot read is the one thing here worth saying over
  // the top of everything else: the project opened, and part of it is missing.
  setMessage(tooNew.length > 0
    ? `Opened ${p.project.name} — ${tooNew.join(', ')} needs a newer SirCAD and was left out`
    : repairs > 0
      ? `Opened ${p.project.name} — brought ${repairs} object${repairs === 1 ? '' : 's'} up to date`
      : `Opened ${p.project.name}`);
  // Same as for an import: a sheet whose parts have no pattern is worth saying
  // so before the board is asked for.
  if (sheets.length > 0) void checkFootprintsAfterLoad();
}

/**
 * Everything open, as the thing a file holds.
 *
 * The document in front first, because a Save As that is asked for while
 * looking at the board should suggest the board's name — and because writing to
 * CIRCAD can only carry one document, so the one being looked at is the one
 * that goes.
 */
function openProjectAsFile(): NativeProject {
  const front = doc();
  const rest = [
    ...sheets.map((s) => s.doc),
    ...(boardIsOpen() ? [state.docs.pcb] : []),
  ].filter((d) => d !== front);

  return {
    name: projectName ?? front.name ?? 'Untitled',
    documents: [front, ...rest],
    ...(textDocs.length > 0
      ? {
          texts: textDocs.map((t) => ({
            name: t.title,
            file: t.file,
            text: t.text,
            ...(t.lang ? { lang: t.lang } : {}),
          })),
        }
      : {}),
  };
}

/**
 * File ▸ Save As — write the design out as a file.
 *
 * This saves the *file*; Save to Project saves to the account. Both are needed:
 * a design imported from a `.SCH` has nowhere on the web to go until it is put
 * somewhere, and a design kept on the web still has to come back out as
 * something you can hold.
 *
 * It writes `.sircad` — the whole project, losing nothing — unless the name
 * says otherwise, in which case it exports the document in front to CIRCAD. The
 * default changed because the old one was CIRCAD v6, which has no field for a
 * pad's real shape, for a mirrored string, or for a footprint name: measured
 * across the sample designs, a round trip through it dropped 3757 field values.
 * Saving to disk was quietly losing what saving to the account kept.
 */
async function saveAsCommand(): Promise<void> {
  try {
    const { saveDocumentAs } = await import('./ui/export.ts');
    const name = await saveDocumentAs(openProjectAsFile(), projectName ?? undefined);
    if (name === null) { setMessage('Save As cancelled'); return; }
    setMessage(/\.(sch|pcb)$/i.test(name)
      ? `Exported ${name} — CIRCAD format, one document, and not everything fits`
      : `Saved ${name}`);
  } catch (err) {
    setMessage(`Save As failed: ${(err as Error).message}`);
  }
}

/**
 * File ▸ CIRCAD Out — the document in front, in the original's own format.
 *
 * One document rather than the project, because that is all the format holds,
 * and lossy, because it belongs to another program: it has no field for a
 * pad's real shape, a mirrored string, or a footprint name. The status line
 * says so when it happens, since a silent translation is how the roundrect
 * pads were lost without anybody noticing.
 */
async function circadOutCommand(): Promise<void> {
  const d = doc();
  if (d.entities.length === 0) { setMessage('Nothing to export'); return; }
  try {
    const { saveAsCircad } = await import('./ui/export.ts');
    const name = saveAsCircad(d, projectName ?? undefined);
    setMessage(name === null
      ? 'CIRCAD export cancelled'
      : `Exported ${name} — CIRCAD format, one document, and not everything fits`);
  } catch (err) {
    setMessage(`CIRCAD export failed: ${(err as Error).message}`);
  }
}

/**
 * File ▸ Gerber Out — the photoplot set for the open board.
 *
 * One RS-274X file per layer, zipped. Board-only: there is nothing to
 * photoplot from a schematic.
 */
async function gerberOutCommand(): Promise<void> {
  const d = doc();
  if (d.kind !== 'pcb') { setMessage('Gerber Out plots a board — switch to the board'); return; }
  if (d.entities.length === 0) { setMessage('Nothing to plot'); return; }
  try {
    setMessage('Plotting…');
    const { saveGerberSet } = await import('./ui/export.ts');
    const done = await saveGerberSet(d);
    setMessage(done === null
      ? 'Gerber Out cancelled'
      : `Wrote ${done.files} file${done.files === 1 ? '' : 's'} to ${done.name}`
        + (done.holes > 0 ? ` — ${done.holes} holes drilled` : ' — no holes to drill'));
  } catch (err) {
    setMessage(`Gerber Out failed: ${(err as Error).message}`);
  }
}

/**
 * File ▸ KiCad Out — write the open document as a KiCad file.
 *
 * A board goes out as `.kicad_pcb` and a sheet as `.kicad_sch`. The two are
 * separate files in KiCad as they are here, so this exports whichever document
 * is in front rather than trying to bundle a project.
 */
async function exportKicadCommand(): Promise<void> {
  const d = doc();
  if (d.entities.length === 0) { setMessage('Nothing to export'); return; }
  try {
    const { saveAsKicad } = await import('./ui/export.ts');
    const name = await saveAsKicad(d);
    setMessage(name === null ? 'KiCad export cancelled' : `Exported ${name}`);
  } catch (err) {
    setMessage(`KiCad export failed: ${(err as Error).message}`);
  }
}

/**
 * File ▸ Save to Project — put the whole design into the signed-in account.
 *
 * Save (F S) only ever wrote back to a project already open, and returned in
 * silence when there was none — so importing a design and pressing Save did
 * nothing whatsoever, with nothing said about why. This is the missing half:
 * it creates the project and writes every sheet and the board into it, after
 * which ordinary Save keeps it up to date.
 */
async function saveToProjectCommand(): Promise<void> {
  if (!user) {
    setMessage('Sign in first — a project is saved to your account');
    return;
  }
  // Text files count as something open. A netlist you have corrected, or a
  // note written against a job, is a file of the project like any other — and
  // refusing to make a project for one meant a text file could never reach the
  // web at all, however often Save was pressed.
  if (sheets.length === 0 && !boardIsOpen() && textDocs.length === 0) {
    setMessage('Nothing open to save');
    return;
  }

  const suggested = projectName
    ?? sheets[0]?.doc.name
    ?? (boardIsOpen() ? state.docs.pcb.name : undefined)
    ?? textDocs[0]?.title
    ?? 'Untitled';
  const name = window.prompt('Save to project — name:', suggested);
  if (name === null) { setMessage('Save to Project cancelled'); return; }
  const trimmed = name.trim();
  if (!trimmed) { setMessage('A project needs a name'); return; }

  const btn = el<HTMLButtonElement>('btn-save');
  btn.disabled = true;
  btn.textContent = 'Saving…';
  try {
    const created = await Projects.create(trimmed);
    // Take on the new project, then let the ordinary save path write the
    // documents into it — one description of how a design is stored, not two.
    projectId = created.project.id;
    projectName = created.project.name;
    rememberProject(created.project.id, created.project.name);
    // The documents belong to the new project, not the old one they came from.
    // Text files as much as drawings: left with the old project's row id, a
    // Save As of a design would have written its notes back over the original.
    for (const sheet of sheets) delete sheet.id;
    for (const t of textDocs) delete t.docId;
    delete documentIds.pcb;
    // And nothing is removed from the old project by saving into a new one.
    removedDocs = [];
    dirty = true;
    await saveProject();
    renderDocTabs();
    updateInfo();
    setMessage(`Saved to project ${projectName}`);
  } catch (err) {
    setMessage(`Save to Project failed: ${(err as Error).message}`);
  } finally {
    updateSaveButton();
  }
}

/**
 * Write everything open to the project.
 *
 * `quiet` is the autosave calling: it neither narrates nor swallows. The button
 * reports what happened in the message line and turns a failure into a sentence
 * there, which is right for something the operator just asked for and wrong for
 * something happening on its own every few seconds — so in quiet mode the
 * message line is left alone and a failure is thrown for the cloud to show.
 */
async function saveProject(opts: { quiet?: boolean } = {}): Promise<void> {
  const quiet = opts.quiet === true;
  // No project yet — Save becomes Save to Project, so the command does the
  // obvious thing instead of returning in silence. Never from an autosave,
  // which must not stop and ask for a name.
  if (user && projectId === null) {
    if (quiet) return;
    await saveToProjectCommand();
    return;
  }
  if (!user || projectId === null) {
    if (!quiet) setMessage('Sign in to save to your account — or File ▸ Save As for a file');
    return;
  }
  const btn = el<HTMLButtonElement>('btn-save');
  btn.disabled = true;
  if (!quiet) btn.textContent = 'Saving…';

  try {
    // Every sheet is written, plus the board — edits to any of them may be
    // pending. A sheet added this session has no document yet, so it is created.
    for (const sheet of sheets) {
      if (sheet.id === undefined) {
        const { id } = await Projects.addDocument(
          projectId, 'sch', sheet.doc.name, sheet.doc);
        sheet.id = id;
      } else {
        await Projects.saveDocument(sheet.id, sheet.doc);
      }
    }
    // The board is created on first save just as a new sheet is. It used to be
    // written only when it already had an id, so a board in a project saved for
    // the first time was quietly left behind.
    const boardId = documentIds.pcb;
    if (boardId !== undefined) {
      await Projects.saveDocument(boardId, state.docs.pcb);
    } else if (boardIsOpen()) {
      const { id } = await Projects.addDocument(
        projectId, 'pcb', state.docs.pcb.name, state.docs.pcb);
      documentIds.pcb = id;
    }
    // Text files travel with the design. A netlist you corrected by hand, a
    // fabrication note, a pin map — losing those on reload would make the
    // editor a scratchpad rather than part of the project.
    for (const t of textDocs) {
      const payload: TextDocumentData = {
        kind: 'txt', name: t.title, file: t.file, text: t.text,
        ...(t.lang ? { lang: t.lang } : {}),
      };
      if (t.docId === undefined) {
        const { id } = await Projects.addDocument(projectId, 'txt', t.title, payload);
        t.docId = id;
      } else {
        await Projects.saveDocument(t.docId, payload);
      }
      t.dirty = false;
    }

    // Files closed since the last save leave the project now. Held back until
    // here so a cross is never the thing that deletes: put the tab back before
    // saving and it stays.
    const dropped = removedDocs;
    removedDocs = [];
    const refused: string[] = [];
    for (const r of dropped) {
      try {
        await Projects.removeDocument(r.id);
      } catch (err) {
        // A 404 means it is not in the project, which is what was wanted.
        // Anything else failed, and is said out loud rather than swallowed:
        // silence here reads as "removed" and the file returns on the next
        // load with nothing to explain it. The entry is kept so the next Save
        // tries again.
        const status = err instanceof ApiError ? err.status : 0;
        if (status === 404) continue;
        refused.push(r.label);
        removedDocs.push(r);
        console.warn(`could not remove ${r.label}:`, err);
      }
    }

    dirty = false;
    renderDocTabs();
    const files = textDocs.length;
    const what = [
      sheets.length > 1 ? `${sheets.length} sheets` : null,
      files > 0 ? `${files} text file${files === 1 ? '' : 's'}` : null,
      dropped.length > refused.length
        ? `removed ${dropped.filter((r) => !refused.includes(r.label))
          .map((r) => r.label).join(', ')}`
        : null,
      refused.length > 0 ? `could NOT remove ${refused.join(', ')}` : null,
    ].filter(Boolean).join(' · ');
    if (!quiet) setMessage(what ? `Saved — ${what}` : 'Saved');
  } catch (err) {
    // Thrown on for the cloud when nobody asked; said out loud when they did.
    if (quiet) throw err;
    setMessage(`Save failed: ${(err as Error).message}`);
  } finally {
    updateSaveButton();
  }
}

el('btn-save').addEventListener('click', () => void saveProject());

// Pressing the cloud sends what is waiting rather than sitting out the pause —
// and with no project yet it does what the Save button does, which is make one.
el('cloud').addEventListener('click', () => {
  if (!canAutosave()) { void saveProject(); return; }
  autosaveNow();
});

// New, Import, Export, Netlist, Projects and Setup Layers are reached from the
// keyboard menu now (F E / F D / F A / N I / F O / S L) — see registerCommands.

function switchMode(mode: DocumentKind): void {
  if (state.mode !== mode) enterMode(mode);
}

// The ratsnest/shorts status is a live summary; clicking it opens the details.
el('st-rats').addEventListener('click', () => netScanCommand());
el('st-rats').title = 'Net Scan — click for shorts and unrouted nets';

/** Exposed for the console while the netlist panel is still to come. */
Object.assign(window, {
  sircad: {
    state,
    doc,
    netlist: () => extractNetlist(doc()),
    renderer,
    viewport,
  },
});

// --- Boot -------------------------------------------------------------------

// A ResizeObserver rather than a window resize listener: the canvas is a grid
// cell, so it changes size when panels do, and at boot it has no size at all
// until the stylesheet has applied.
new ResizeObserver(() => resize()).observe(canvas);
closeAllDocuments(); // boot with nothing open, so the document bar is empty
el('pp-close').addEventListener('click', () => closePartPicker());

// The offer to install, before the commands: `?I` asks this for its answer, and
// the Help menu is built from what is registered. Set up in a browser tab and
// in the installed window alike — in the window it finds it has nothing to
// offer and shows nothing, which is the whole of its behaviour there.
install = installUi({
  slot: el('file-actions'),
  banner: el('install-host'),
  seen: prefs.installBannerSeen === true,
  onSeen: () => {
    prefs = { ...prefs, installBannerSeen: true };
    savePrefs(prefs);
  },
  setMessage,
});
// The worker is what makes the offer possible at all: without one registered,
// Chrome never fires the event the button waits for.
registerWorker(__APP_VERSION__);

// Register handlers first: the bar only shows commands that are implemented.
registerCommands();
applyFileMenuLayout();
// Pressing G grabs an item straight away; G R / G X still reach the variants.
menus.setDefaults({ G: 'GI' });
menus.mount(el('menubar'));
buildLayerPanel();
updateInfo();
renderDocTabs();
updateSheetStatus();
resize();
updateStatus(0, 0);
renderAccount();
updateSaveButton();
setMessage(toolbox.active.hint);
void loadLibraries();

// The status-line clock, colon blinking one second on, one off, as the manual
// describes.
function tickClock(): void {
  const now = new Date();
  const p = (n: number) => String(n).padStart(2, '0');
  const sep = now.getSeconds() % 2 === 0 ? ':' : ' ';
  el('st-clock').textContent = `${p(now.getHours())}${sep}${p(now.getMinutes())}`;
}
tickClock();
window.setInterval(tickClock, 1000);

// Losing a schematic to a stray reload would be unforgivable.
window.addEventListener('beforeunload', (ev) => {
  if (!dirty) return;
  ev.preventDefault();
  ev.returnValue = '';
});

// Restore an existing session. In dev there is no API, so this is skipped.
if (!import.meta.env.DEV) {
  void Session.current()
    .then((u) => {
      user = u;
      renderAccount();
      updateSaveButton();
      if (u) setMessage(`Signed in as ${u.display_name || u.email}`);
    })
    .catch(() => {
      // An unreachable API is already reported by the catalogue load; do not
      // stack a second copy of the same message on top of it.
    });
}
